import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { 
  Shield, AlertTriangle, Search, MessageSquare, User, LogOut, 
  Target, MapPin, DollarSign, Eye, Send, Scan, ChevronRight,
  Activity, TrendingUp, Clock, Menu, X, Bot, FileText, Bell,
  Upload, Image, FolderOpen, Map, Users, CheckCircle, XCircle,
  BarChart3, Bookmark, BookmarkCheck, RefreshCw, Filter, SlidersHorizontal,
  Gift, Heart, Trophy, Zap, ScanLine, Camera, Trash2, ArrowLeft, ArrowRight,
  Brain, Sparkles, HelpCircle, MessageCircle, ThumbsUp, Flag, ChevronDown, ChevronUp,
  Building, Radar, Mail, Lock, ChevronLeft, ExternalLink
} from "lucide-react";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from "recharts";
import { AgencyAuthProvider, AgencyLoginPage, AgencyDashboard, AgencyTipsPage, AgencyAnalyticsPage, AgencyProfilePage, AgencyFugitivesPage, AgencyAICommandCenter } from './AgencyPortal';

// Extracted page components
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import GoogleCallback from './pages/GoogleCallback';
import StateFugitivesPage from './pages/StateFugitivesPage';
import MissingPersonsPage from './pages/MissingPersonsPage';

// Intelligence Dashboard
import { IntelligenceDashboard } from './components/intelligence';

// Landing Page Conversion Elements
import { FugitivesNearYou, LiveActivityFeed, FeaturedFugitive, StickyCTABar } from './components/landing/ConversionElements';

// Extracted layout components
import Navbar from './components/layout/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// API instance with credentials
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Add auth header interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("wysspr_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses globally - clear token on explicit unauthorized
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only remove token if it's a genuine auth failure, not during login
      const isAuthEndpoint = error.config?.url?.includes('/auth/');
      if (!isAuthEndpoint) {
        localStorage.removeItem("wysspr_token");
      }
    }
    return Promise.reject(error);
  }
);

// ==================== AUTH CONTEXT ====================
const AuthContext = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    checkAuth();
  }, []);

  // Real-time notifications via polling (WebSocket not supported by infrastructure)
  useEffect(() => {
    if (user) {
      startPolling();
      fetchNotifications();
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [user]);

  // Notification polling
  const startPolling = () => {
    if (pollIntervalRef.current) return;
    pollIntervalRef.current = setInterval(() => {
      fetchNotifications();
    }, 15000);
  };

  const checkAuth = async () => {
    const token = localStorage.getItem("wysspr_token");
    
    // If no token, user is definitely not logged in
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      // Only clear user on explicit 401 (unauthorized)
      // Don't clear on network errors or other issues
      if (error.response?.status === 401) {
        localStorage.removeItem("wysspr_token");
        setUser(null);
      }
      // For other errors, keep existing user state if any
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        api.get("/notifications"),
        api.get("/notifications/unread-count")
      ]);
      setNotifications(notifRes.data);
      setUnreadCount(countRes.data.count);
    } catch {
      // Silent fail
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch {
      toast.error("Failed to mark notification as read");
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put("/notifications/read-all");
      fetchNotifications();
      toast.success("All notifications marked as read");
    } catch {
      toast.error("Failed to mark all as read");
    }
  };

  return (
    <AuthProvider value={{ user, setUser, loading, checkAuth, notifications, unreadCount, markAsRead, markAllAsRead, fetchNotifications }}>
      {children}
    </AuthProvider>
  );
};

// Simple context
import { createContext, useContext } from "react";
const AuthContextValue = createContext(null);
const AuthProvider = ({ value, children }) => (
  <AuthContextValue.Provider value={value}>{children}</AuthContextValue.Provider>
);
export const useAuth = () => useContext(AuthContextValue);

// ==================== REFERRAL INVITE MODAL ====================

const ReferralInviteModal = ({ isOpen, onClose }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [referralData, setReferralData] = useState(null);

  useEffect(() => {
    if (isOpen) {
      // Fetch referral code
      api.get("/referral/my-code")
        .then(res => setReferralData(res.data))
        .catch(err => console.error("Failed to get referral code:", err));
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName || !email) {
      toast.error("Please enter your friend's name and email");
      return;
    }
    
    setLoading(true);
    try {
      await api.post("/referral/invite", {
        friend_first_name: firstName,
        friend_last_name: lastName,
        friend_email: email
      });
      setSent(true);
      toast.success(`Invitation sent to ${email}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setSent(false);
    onClose();
  };

  const copyReferralLink = () => {
    if (referralData?.referral_link) {
      navigator.clipboard.writeText(referralData.referral_link);
      toast.success("Referral link copied to clipboard!");
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 pt-20 overflow-y-auto"
        onClick={resetAndClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: -20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: -20 }}
          className="glass-panel rounded-xl w-full max-w-md overflow-hidden my-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 p-6 border-b border-slate-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center">
                  <Gift className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Refer a Friend</h2>
                  <p className="text-purple-300 text-sm">Earn 100 points per signup!</p>
                </div>
              </div>
              <button onClick={resetAndClose} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {sent ? (
              <div className="text-center py-6">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Invitation Sent!</h3>
                <p className="text-slate-400 mb-6">
                  We've sent an invitation to <strong className="text-cyan-400">{email}</strong>
                </p>
                <p className="text-slate-500 text-sm mb-6">
                  You'll earn 100 points when they sign up!
                </p>
                <button onClick={resetAndClose} className="btn-primary">
                  Send Another Invite
                </button>
              </div>
            ) : (
              <>
                {/* Referral Stats */}
                {referralData && (
                  <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-slate-400 text-sm">Your Referrals</span>
                      <span className="text-cyan-400 font-bold">{referralData.referrals_count || 0}</span>
                    </div>
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-slate-400 text-sm">Points Earned</span>
                      <span className="text-green-400 font-bold">{referralData.rewards_earned || 0}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input 
                        type="text" 
                        value={referralData.referral_link || ""} 
                        readOnly 
                        className="input-field flex-1 text-xs"
                      />
                      <button 
                        onClick={copyReferralLink}
                        className="px-3 py-2 bg-cyan-500/20 text-cyan-400 rounded hover:bg-cyan-500/30 transition-colors"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                )}

                {/* Invite Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-slate-400 text-xs mb-1">First Name *</label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="John"
                        className="input-field w-full"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-slate-400 text-xs mb-1">Last Name</label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Doe"
                        className="input-field w-full"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-slate-400 text-xs mb-1">Email Address *</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="friend@example.com"
                      className="input-field w-full"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <span className="animate-spin w-5 h-5 border-2 border-black border-t-transparent rounded-full" />
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Invitation
                      </>
                    )}
                  </button>
                </form>

                {/* Benefits */}
                <div className="mt-6 pt-6 border-t border-slate-700">
                  <p className="text-slate-500 text-xs text-center">
                    🎁 Your friend gets <strong className="text-cyan-400">50 bonus points</strong> when they sign up!
                  </p>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

// ==================== TUTORIAL COMPONENT ====================

const TutorialModal = ({ isOpen, onClose, tutorialType = 'public' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  
  const publicSteps = [
    {
      title: "Welcome to Wysspr! 🎯",
      icon: Shield,
      content: "You've just joined America's most powerful fugitive tracking network. Together, we can bring dangerous criminals to justice.",
      highlight: "Help capture fugitives and earn rewards!"
    },
    {
      title: "Dashboard Navigation",
      icon: Target,
      content: "Your Dashboard shows all FBI Most Wanted fugitives. Use the tabs to switch between 'All Fugitives', 'For You' (personalized), and 'Threat Analysis' (AI-powered danger assessments).",
      highlight: "Track fugitives and view AI threat scores"
    },
    {
      title: "Submit Tips Anonymously",
      icon: Send,
      content: "Have information about a fugitive? Submit tips anonymously through our secure system. You can even upload photos for AI analysis!",
      highlight: "Your identity is protected"
    },
    {
      title: "AI-Powered Features",
      icon: Brain,
      content: "Use Wysspr AI to analyze sightings, get fugitive intel, and view our Predictive Map showing likely fugitive locations.",
      highlight: "AI helps you track smarter"
    },
    {
      title: "Bounty Pool & Rewards",
      icon: DollarSign,
      content: "Contribute to the Bounty Pool or earn 50% of rewards for tips that lead to capture. Track your earnings in your profile.",
      highlight: "Get paid for successful tips!"
    },
    {
      title: "Community Intel",
      icon: Users,
      content: "Join our anonymous community to share sightings, discuss fugitives, and collaborate with other hunters.",
      highlight: "Together we're stronger"
    }
  ];
  
  const agencySteps = [
    {
      title: "Welcome, Agent! 🛡️",
      icon: Shield,
      content: "Welcome to Wysspr's Law Enforcement Command Center. This secure portal gives you access to advanced tools for tracking and capturing fugitives.",
      highlight: "Agency-only access granted"
    },
    {
      title: "Command Center",
      icon: BarChart3,
      content: "Your dashboard shows real-time statistics, tip queues, and fugitive tracking status. Monitor all activity from one place.",
      highlight: "Central intelligence hub"
    },
    {
      title: "Tip Management",
      icon: FileText,
      content: "Review incoming tips, verify information, and update tip statuses. Prioritize based on urgency and credibility.",
      highlight: "Process tips efficiently"
    },
    {
      title: "Database Sync",
      icon: RefreshCw,
      content: "Sync fugitive data from FBI, US Marshals, DEA, ATF, and ICE. Keep your database current with the latest intel.",
      highlight: "Multi-agency integration"
    },
    {
      title: "Predictive Analysis",
      icon: Brain,
      content: "Access AI-powered analytics, heat maps, and network graphs to identify patterns and predict fugitive movements.",
      highlight: "AI-enhanced intelligence"
    },
    {
      title: "User Management",
      icon: Users,
      content: "Super Admins can approve new agencies, manage users, and oversee platform activity.",
      highlight: "Full administrative control"
    }
  ];
  
  const steps = tutorialType === 'agency' ? agencySteps : publicSteps;
  
  if (!isOpen) return null;
  
  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      localStorage.setItem(`tutorial_${tutorialType}_completed`, 'true');
      onClose();
    }
  };
  
  const handlePrev = () => {
    if (currentStep > 0) setCurrentStep(prev => prev - 1);
  };
  
  const handleSkip = () => {
    localStorage.setItem(`tutorial_${tutorialType}_completed`, 'true');
    onClose();
  };
  
  const step = steps[currentStep];
  const Icon = step.icon;
  
  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/95 backdrop-blur-md z-[9999] flex items-center justify-center"
        style={{ minHeight: '100vh' }}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="relative bg-slate-900 border border-cyan-500/50 rounded-xl max-w-md w-full mx-4 p-6 shadow-2xl shadow-cyan-500/20"
        >
          {/* Close Button */}
          <button 
            onClick={handleSkip}
            className="absolute top-3 right-3 text-slate-400 hover:text-white transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Progress bar */}
          <div className="flex gap-1 mb-4">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-all ${
                  i <= currentStep ? 'bg-cyan-500' : 'bg-slate-700'
                }`}
              />
            ))}
          </div>
          
          {/* Icon */}
          <div className="flex justify-center mb-3">
            <div className="p-3 bg-cyan-500/20 rounded-full">
              <Icon className="w-10 h-10 text-cyan-400" />
            </div>
          </div>
          
          {/* Content */}
          <h2 className="font-heading font-bold text-xl text-white text-center mb-2">
            {step.title}
          </h2>
          <p className="text-slate-300 text-center text-sm mb-3 leading-relaxed">
            {step.content}
          </p>
          <div className="bg-cyan-500/10 border border-cyan-500/30 rounded p-2 mb-4">
            <p className="text-cyan-400 text-xs text-center font-semibold">
              💡 {step.highlight}
            </p>
          </div>
          
          {/* Step counter */}
          <p className="text-slate-500 text-xs text-center mb-3">
            Step {currentStep + 1} of {steps.length}
          </p>
          
          {/* Buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleSkip}
              className="flex-1 py-2 text-slate-400 hover:text-white transition-colors text-xs"
            >
              Skip
            </button>
            <button
              onClick={handlePrev}
              disabled={currentStep === 0}
              className="px-3 py-2 bg-slate-700 text-white rounded text-sm disabled:opacity-50"
            >
              Back
            </button>
            <button
              onClick={handleNext}
              className="flex-1 btn-primary"
            >
              {currentStep === steps.length - 1 ? "Get Started!" : "Next"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const TutorialButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors text-sm"
    title="View Tutorial"
  >
    <HelpCircle className="w-4 h-4" />
    <span className="hidden md:inline">Tutorial</span>
  </button>
);

// ==================== NEWS TICKER COMPONENT ====================

const NewsTicker = () => {
  const [tickerItems, setTickerItems] = useState([]);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const fetchTicker = async () => {
      try {
        const res = await api.get('/news/ticker');
        setTickerItems(res.data.ticker || []);
      } catch (error) {
        console.error('Failed to fetch news ticker');
      }
    };
    
    fetchTicker();
    // Refresh every 5 minutes
    const interval = setInterval(fetchTicker, 300000);
    return () => clearInterval(interval);
  }, []);

  if (tickerItems.length === 0) return null;

  const getSourceColor = (source) => {
    switch(source) {
      case 'FBI': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'FBI WANTED': return 'bg-red-500/30 text-red-300 border-red-500/40';
      case 'USMARSHALS': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'CRIMESTOPPERS': return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  return (
    <div 
      className="bg-slate-900/80 border-b border-slate-800 py-2 overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="flex items-center">
        <div className="flex-shrink-0 px-4 flex items-center gap-2 border-r border-slate-700">
          <Activity className="w-4 h-4 text-red-400 animate-pulse" />
          <span className="text-xs font-bold text-red-400 uppercase tracking-wider">LIVE</span>
        </div>
        <div className="overflow-hidden flex-1">
          <div 
            className={`flex gap-8 ${isPaused ? '' : 'animate-marquee'}`}
            style={{
              animation: isPaused ? 'none' : 'marquee 60s linear infinite',
            }}
          >
            {[...tickerItems, ...tickerItems].map((item, idx) => (
              <a 
                key={`${item.id}_${idx}`}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 whitespace-nowrap hover:text-cyan-400 transition-colors group"
              >
                <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded border ${getSourceColor(item.source)}`}>
                  {item.source}
                </span>
                <span className="text-xs text-slate-300 group-hover:text-cyan-400">{item.text}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          display: flex;
          width: max-content;
        }
      `}</style>
    </div>
  );
};

// ==================== NEWS SIDEBAR COMPONENT ====================

const NewsSidebar = () => {
  const [news, setNews] = useState([]);
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const res = await api.get('/news/feed?limit=10');
      setNews(res.data.news || []);
    } catch (error) {
      console.error('Failed to fetch news');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    setSummaryLoading(true);
    try {
      const res = await api.get('/news/summary');
      setSummary(res.data.summary || '');
    } catch (error) {
      console.error('Failed to fetch summary');
    } finally {
      setSummaryLoading(false);
    }
  };

  const getSourceColor = (source) => {
    switch(source) {
      case 'fbi': return 'bg-red-500/20 text-red-400';
      case 'fbi_wanted': return 'bg-red-500/30 text-red-300';
      case 'usmarshals': return 'bg-amber-500/20 text-amber-400';
      case 'crimestoppers': return 'bg-cyan-500/20 text-cyan-400';
      default: return 'bg-slate-500/20 text-slate-400';
    }
  };

  return (
    <div className="card-tactical p-4 h-fit sticky top-24">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-red-400" />
          <h3 className="font-heading font-bold text-sm text-white uppercase tracking-wider">Live Intel Feed</h3>
        </div>
        <button 
          onClick={fetchSummary}
          disabled={summaryLoading}
          className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
        >
          <Brain className="w-3 h-3" />
          {summaryLoading ? 'Analyzing...' : 'AI Brief'}
        </button>
      </div>

      {/* AI Summary */}
      {summary && (
        <div className="mb-4 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded text-xs text-slate-300">
          <div className="flex items-center gap-1 mb-1 text-cyan-400 font-semibold">
            <Brain className="w-3 h-3" />
            AI Intel Brief
          </div>
          {summary}
        </div>
      )}

      {/* News Items */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => (
            <div key={i} className="animate-pulse">
              <div className="h-3 bg-slate-700 rounded w-1/4 mb-2" />
              <div className="h-4 bg-slate-700 rounded w-full" />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
          {news.map((item, idx) => (
            <a
              key={idx}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="block p-2 rounded hover:bg-slate-800/50 transition-colors group"
            >
              <div className="flex items-center gap-2 mb-1">
                <span className={`px-1.5 py-0.5 text-[10px] font-bold rounded ${getSourceColor(item.source)}`}>
                  {item.source.toUpperCase().replace('_', ' ')}
                </span>
                <span className="text-slate-500 text-[10px]">
                  {item.published ? new Date(item.published).toLocaleDateString() : ''}
                </span>
              </div>
              <p className="text-xs text-slate-300 group-hover:text-cyan-400 line-clamp-2 leading-relaxed">
                {item.title}
              </p>
            </a>
          ))}
        </div>
      )}

      <button 
        onClick={fetchNews}
        className="w-full mt-4 py-2 text-xs text-slate-400 hover:text-cyan-400 flex items-center justify-center gap-1"
      >
        <RefreshCw className="w-3 h-3" />
        Refresh Feed
      </button>
    </div>
  );
};

// ==================== COMPONENTS ====================

const NotificationBell = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button 
        onClick={() => setOpen(!open)}
        className="relative text-slate-400 hover:text-cyan-400 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-y-auto bg-slate-900 border border-slate-800 rounded-sm shadow-xl z-50"
          >
            <div className="p-3 border-b border-slate-800 flex justify-between items-center">
              <span className="text-white font-semibold">Notifications</span>
              {unreadCount > 0 && (
                <button onClick={markAllAsRead} className="text-xs text-cyan-400 hover:underline">
                  Mark all read
                </button>
              )}
            </div>
            
            {notifications.length === 0 ? (
              <div className="p-4 text-center text-slate-500">No notifications</div>
            ) : (
              notifications.slice(0, 10).map((notif) => (
                <div
                  key={notif.notification_id}
                  className={`p-3 border-b border-slate-800 cursor-pointer hover:bg-slate-800/50 ${
                    !notif.is_read ? 'bg-cyan-500/5' : ''
                  } ${notif.from_admin ? 'border-l-2 border-l-purple-500' : ''}`}
                  onClick={() => markAsRead(notif.notification_id)}
                >
                  <div className="flex items-start gap-2">
                    <div className={`mt-1 w-2 h-2 rounded-full ${
                      notif.type === 'ADMIN_MESSAGE' ? 'bg-purple-500' :
                      notif.type === 'ALERT' ? 'bg-red-500' :
                      notif.type === 'SUCCESS' ? 'bg-green-500' :
                      notif.type === 'WARNING' ? 'bg-amber-500' : 'bg-cyan-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      {notif.from_admin && (
                        <span className="text-xs text-purple-400 font-semibold">From Wysspr Team</span>
                      )}
                      <p className="text-sm font-medium text-white truncate">{notif.title}</p>
                      <p className="text-xs text-slate-400 line-clamp-2">{notif.message}</p>
                      <p className="text-xs text-slate-600 mt-1">
                        {new Date(notif.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Navbar component extracted to components/layout/Navbar.js

const FugitiveCard = ({ fugitive, onClick, isTracked, onTrack }) => {
  const [imgError, setImgError] = useState(false);
  const isFbiData = fugitive.fugitive_id?.startsWith('fbi_');
  const isTopTen = (fugitive.charges || []).includes('Ten Most Wanted Fugitives');
  const isHighReward = (fugitive.reward_amount || 0) >= 1000000;
  const isCaptured = fugitive.status?.toLowerCase() === 'captured';
  const capturedViaWysspr = fugitive.captured_via_wysspr;
  const defaultImage = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400";
  
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`card-fugitive cursor-pointer group ${isTopTen && !isCaptured ? 'ring-2 ring-red-500/50' : ''} ${isCaptured ? 'ring-2 ring-green-500/50' : ''}`}
      data-testid={`fugitive-card-${fugitive.fugitive_id}`}
    >
      <div className="relative" onClick={onClick}>
        <img 
          src={imgError ? defaultImage : (fugitive.image_url || defaultImage)} 
          alt={fugitive.name}
          className={`w-full h-48 object-cover transition-all duration-500 ${isCaptured ? 'grayscale opacity-70' : 'filter grayscale group-hover:grayscale-0'}`}
          onError={() => setImgError(true)}
        />
        {/* Captured Overlay */}
        {isCaptured && (
          <div className="absolute inset-0 bg-green-900/60 flex flex-col items-center justify-center">
            <CheckCircle className="w-12 h-12 text-green-400 mb-2" />
            <span className="bg-green-600 text-white text-lg font-bold px-4 py-2 rounded-sm uppercase tracking-wider">
              CAPTURED
            </span>
            {capturedViaWysspr && (
              <span className="text-green-300 text-xs mt-2 text-center px-2">
                Thanks to Wysspr Community
              </span>
            )}
          </div>
        )}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {isTopTen && !isCaptured && (
            <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-sm uppercase tracking-wider shadow-lg">
              TOP 10
            </span>
          )}
          {isHighReward && !isTopTen && !isCaptured && (
            <span className="bg-amber-500 text-black text-xs font-bold px-2 py-1 rounded-sm uppercase tracking-wider shadow-lg">
              $1M+
            </span>
          )}
        </div>
        <div className="absolute top-2 right-2 flex gap-2">
          {isFbiData && !isCaptured && (
            <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-sm uppercase tracking-wider">
              FBI
            </span>
          )}
          {!isCaptured && <span className="status-wanted">{fugitive.status}</span>}
        </div>
        {!isCaptured && <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />}
      </div>
      
      <div className="p-4 space-y-3">
        <div className="flex justify-between items-start">
          <div onClick={onClick}>
            <h3 className="font-heading font-bold text-lg text-white">{fugitive.name}</h3>
            {fugitive.alias && (
              <p className="text-cyan-400 text-sm font-mono truncate max-w-[200px]">"{fugitive.alias}"</p>
            )}
          </div>
          {onTrack && (
            <button
              onClick={(e) => { e.stopPropagation(); onTrack(fugitive.fugitive_id); }}
              className={`p-2 rounded-sm transition-colors ${isTracked ? 'bg-cyan-500/20 text-cyan-400' : 'bg-slate-800 text-slate-500 hover:text-cyan-400'}`}
              data-testid={`track-btn-${fugitive.fugitive_id}`}
            >
              {isTracked ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
            </button>
          )}
        </div>
        
        <div className="flex items-center gap-2 text-amber-400" onClick={onClick}>
          <DollarSign className="w-4 h-4" />
          <span className="font-heading font-bold">
            {(fugitive.reward_amount || 0) >= 1000000 
              ? `$${((fugitive.reward_amount || 0) / 1000000).toFixed(0)}M` 
              : `$${(fugitive.reward_amount || 0).toLocaleString()}`}
          </span>
        </div>
        
        <p className="text-slate-400 text-sm line-clamp-2" onClick={onClick}>{fugitive.description}</p>
        
        <div className="flex items-center gap-2 text-xs text-slate-500" onClick={onClick}>
          <MapPin className="w-3 h-3" />
          <span>{fugitive.last_known_location || "Unknown"}</span>
        </div>
        
        <div className="pt-2 border-t border-slate-800" onClick={onClick}>
          <span className={`text-xs uppercase tracking-wider ${fugitive.danger_level?.includes('EXTREMELY') ? 'text-red-400' : 'text-amber-400'}`}>
            <AlertTriangle className="w-3 h-3 inline mr-1" />
            {fugitive.danger_level}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

const StatCard = ({ icon: Icon, label, value, trend, color = "cyan" }) => (
  <div className="card-tactical p-6">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">{label}</p>
        <p className={`font-heading font-bold text-3xl ${color === 'amber' ? 'text-amber-400' : color === 'red' ? 'text-red-400' : color === 'green' ? 'text-green-400' : 'text-cyan-400'}`}>
          {value}
        </p>
      </div>
      <div className={`p-3 rounded-sm ${color === 'amber' ? 'bg-amber-500/20' : color === 'red' ? 'bg-red-500/20' : color === 'green' ? 'bg-green-500/20' : 'bg-cyan-500/20'}`}>
        <Icon className={`w-6 h-6 ${color === 'amber' ? 'text-amber-400' : color === 'red' ? 'text-red-400' : color === 'green' ? 'text-green-400' : 'text-cyan-400'}`} />
      </div>
    </div>
    {trend && (
      <div className="mt-4 flex items-center gap-1 text-green-400 text-sm">
        <TrendingUp className="w-4 h-4" />
        <span>{trend}</span>
      </div>
    )}
  </div>
);

// Why Join Stats Component - fetches real platform data
const WhyJoinStats = () => {
  const [stats, setStats] = useState({ totalRewards: 0, activeFugitives: 0, topTen: 0 });
  
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get("/fugitives?limit=500");
        const fugitives = response.data;
        const totalRewards = fugitives.reduce((acc, f) => acc + (f.reward_amount || 0), 0);
        const topTen = fugitives.filter(f => (f.charges || []).includes('Ten Most Wanted Fugitives')).length;
        setStats({
          totalRewards,
          activeFugitives: fugitives.length,
          topTen
        });
      } catch (error) {
        console.error("Failed to fetch stats");
      }
    };
    fetchStats();
  }, []);
  
  return (
    <div className="card-tactical p-8 text-center">
      <Trophy className="w-16 h-16 text-amber-400 mx-auto mb-6" />
      <p className="font-heading font-bold text-5xl text-amber-400 mb-2">
        ${(stats.totalRewards / 1000000).toFixed(0)}M+
      </p>
      <p className="text-slate-400 text-lg mb-6">Total Active Rewards</p>
      <div className="grid grid-cols-2 gap-4 text-center">
        <div className="bg-slate-800/50 p-4 rounded-sm">
          <p className="font-heading font-bold text-2xl text-white">{stats.activeFugitives}</p>
          <p className="text-slate-500 text-sm">Active Fugitives</p>
        </div>
        <div className="bg-slate-800/50 p-4 rounded-sm">
          <p className="font-heading font-bold text-2xl text-red-400">{stats.topTen}</p>
          <p className="text-slate-500 text-sm">Top 10 Wanted</p>
        </div>
      </div>
    </div>
  );
};

// ==================== PAGES ====================

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Don't show conversion elements if user is logged in
  const showConversionElements = !user;

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Live Activity Feed - Fixed position */}
      {showConversionElements && <LiveActivityFeed />}
      
      {/* Sticky CTA Bar */}
      {showConversionElements && <StickyCTABar />}
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1586036308218-5ed6553c98b6?w=1920)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-transparent to-slate-950" />
        
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-cyan-400 font-mono text-sm tracking-[0.3em] mb-4">INTELLIGENCE PLATFORM</p>
            <h1 className="font-heading font-bold text-5xl md:text-7xl tracking-tighter uppercase text-white mb-6">
              WYSSPR
            </h1>
            <p className="font-heading text-2xl md:text-3xl text-slate-300 mb-2">
              Silence Speaks Volumes.
            </p>
            <p className="font-heading text-2xl md:text-3xl text-cyan-400 mb-8">
              Justice Hears Everything.
            </p>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto mb-12">
              Advanced AI-powered platform for tracking the FBI's Most Wanted fugitives. 
              Help bring criminals to justice and earn cash rewards for actionable intelligence.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate(user ? '/dashboard' : '/login')}
                className="btn-primary flex items-center justify-center gap-2"
                data-testid="get-started-btn"
              >
                <Target className="w-5 h-5" />
                {user ? 'View Dashboard' : 'Start Hunting - Free'}
              </button>
              {user && (
              <button 
                onClick={() => navigate('/map')}
                className="btn-secondary flex items-center justify-center gap-2"
                data-testid="view-map-btn"
              >
                <Map className="w-5 h-5" />
                View Map
              </button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CONVERSION: Fugitives Near You - Blurred Teasers */}
      {showConversionElements && <FugitivesNearYou />}

      {/* CONVERSION: Featured High-Value Fugitive */}
      {showConversionElements && <FeaturedFugitive />}

      {/* How It Works - 50/50 Cash Split Section */}
      <section id="how-it-works" className="py-24 px-4 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-amber-400 font-mono text-sm tracking-[0.2em] mb-4">EARN CASH REWARDS</p>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight uppercase text-white mb-4">
              How It Works
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Wysspr makes it easy and rewarding to help bring fugitives to justice.
            </p>
          </motion.div>

          {/* Why Wysspr - AI Advantage */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-tactical p-6 md:p-8 mb-12 border-cyan-500/30 bg-cyan-500/5"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="p-3 bg-cyan-500/20 rounded-sm">
                <Zap className="w-8 h-8 text-cyan-400" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-xl md:text-2xl text-white uppercase mb-2">
                  Why Report Through Wysspr?
                </h3>
                <p className="text-slate-400">
                  Your tips have a <span className="text-cyan-400 font-bold">significantly higher probability</span> of leading to capture when submitted through Wysspr compared to reporting directly on your own.
                </p>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-slate-800/50 p-4 rounded-sm">
                <Bot className="w-6 h-6 text-cyan-400 mb-2" />
                <h4 className="text-white font-semibold mb-1">AI-Powered Analysis</h4>
                <p className="text-slate-400 text-sm">Our AI instantly analyzes your tip for credibility, cross-references with known sightings, and identifies patterns you might miss.</p>
              </div>
              <div className="bg-slate-800/50 p-4 rounded-sm">
                <ScanLine className="w-6 h-6 text-cyan-400 mb-2" />
                <h4 className="text-white font-semibold mb-1">Advanced Image Processing</h4>
                <p className="text-slate-400 text-sm">Upload photos and our AI enhances, matches facial features, and extracts critical details that expedite the investigation process.</p>
              </div>
              <div className="bg-slate-800/50 p-4 rounded-sm">
                <Activity className="w-6 h-6 text-cyan-400 mb-2" />
                <h4 className="text-white font-semibold mb-1">Real-Time Intelligence</h4>
                <p className="text-slate-400 text-sm">We aggregate tips from multiple sources, track movement patterns, and provide law enforcement with actionable intelligence packages.</p>
              </div>
            </div>
            <p className="text-slate-500 text-sm mt-6 text-center">
              Individual citizens don't have access to these tools. Wysspr bridges the gap between your sighting and successful capture.
            </p>
          </motion.div>

          {/* Process Steps */}
          <div className="grid md:grid-cols-4 gap-6 mb-16">
            {[
              { step: "1", title: "Browse", desc: "View FBI's Most Wanted fugitives with photos, descriptions, and reward amounts.", icon: Search },
              { step: "2", title: "Spot", desc: "If you see something, report it. Your safety is our priority.", icon: Eye },
              { step: "3", title: "Submit", desc: "Submit your tip anonymously through our secure platform.", icon: Send },
              { step: "4", title: "Earn", desc: "If your tip leads to a capture, receive 50% of the FBI reward.", icon: DollarSign }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="relative inline-flex mb-4">
                  <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center border border-cyan-500/30">
                    <item.icon className="w-8 h-8 text-cyan-400" />
                  </div>
                  <span className="absolute -top-2 -right-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center text-xs font-bold text-black">
                    {item.step}
                  </span>
                </div>
                <h3 className="font-heading font-bold text-lg text-white mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          {/* 50/50 Split Highlight */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-tactical p-8 md:p-12"
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <DollarSign className="w-10 h-10 text-amber-400" />
                  <h3 className="font-heading font-bold text-2xl md:text-3xl text-white uppercase">
                    50/50 Cash Split
                  </h3>
                </div>
                <p className="text-slate-400 mb-6">
                  When your tip leads to a fugitive's capture, you receive <span className="text-amber-400 font-bold">50% of the FBI reward</span>. 
                  Wysspr receives 50% to maintain the platform, fund our AI systems, and continue enhancing capture rates.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    Anonymous tip submission - your identity stays protected
                  </li>
                  <li className="flex items-center gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    AI-enhanced tips have higher success rates
                  </li>
                  <li className="flex items-center gap-3 text-slate-300">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    Track your tips and get real-time status updates
                  </li>
                </ul>
              </div>
              <div className="flex justify-center">
                <div className="relative">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-amber-500/20 border border-amber-500/30 p-6 rounded-sm text-center">
                      <p className="text-amber-400 font-bold text-4xl md:text-5xl mb-2">50%</p>
                      <p className="text-white font-semibold">Your Share</p>
                      <p className="text-slate-400 text-sm">Cash reward paid to you</p>
                    </div>
                    <div className="bg-cyan-500/20 border border-cyan-500/30 p-6 rounded-sm text-center">
                      <p className="text-cyan-400 font-bold text-4xl md:text-5xl mb-2">50%</p>
                      <p className="text-white font-semibold">Wysspr</p>
                      <p className="text-slate-400 text-sm">Platform & AI operations</p>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-slate-800/50 rounded-sm text-center">
                    <p className="text-slate-400 text-sm">Example: $1,000,000 FBI Reward</p>
                    <p className="text-amber-400 font-bold text-lg">You earn: $500,000</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-4 bg-slate-950/50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-cyan-400 font-mono text-sm tracking-[0.2em] mb-4">HOW IT WORKS</p>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight uppercase text-white">
              About Wysspr
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Browse Fugitives", desc: "Access our comprehensive database of FBI's Most Wanted fugitives. View profiles, last known locations, and reward amounts on our interactive map." },
              { step: "02", title: "Submit Intelligence", desc: "Spotted someone? Submit anonymous tips with photos and location data. Our AI system analyzes every tip for credibility and urgency." },
              { step: "03", title: "Earn Rewards", desc: "Tips leading to capture qualify for FBI rewards. Track your submissions, get status updates, and potentially earn substantial rewards for actionable intelligence." }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.15 }}
                className="relative"
              >
                <span className="font-heading font-bold text-6xl text-cyan-500/20 absolute -top-4 -left-2">{item.step}</span>
                <div className="card-tactical p-6 pt-10">
                  <h3 className="font-heading font-bold text-xl text-white mb-3">{item.title}</h3>
                  <p className="text-slate-400">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Team Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-slate-900 via-red-950/10 to-slate-950 border-y border-slate-800/50">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row items-center gap-8"
          >
            <div className="flex-1 text-center md:text-left">
              <p className="text-red-400 font-mono text-sm tracking-[0.2em] mb-3">MULTI-AGENCY DATABASE</p>
              <h2 className="font-heading font-bold text-2xl md:text-4xl tracking-tight uppercase text-white mb-3">
                Browse State & Federal Fugitives
              </h2>
              <p className="text-slate-400 text-base mb-6">
                Access real-time data from FBI, US Marshals, LAPD, Texas DPS, and NY DOCCS.
                Search by name, filter by agency, and help bring fugitives to justice.
              </p>
              <button 
                onClick={() => navigate('/state-fugitives')}
                className="btn-primary flex items-center gap-2 mx-auto md:mx-0"
                data-testid="browse-state-fugitives-btn"
              >
                <Eye className="w-5 h-5" />
                Browse Fugitive Database
              </button>
            </div>
            <div className="flex gap-2 flex-wrap justify-center md:justify-end max-w-xs">
              {[
                { label: "FBI TOP 10", color: "bg-red-600" },
                { label: "US MARSHALS", color: "bg-blue-600" },
                { label: "LAPD", color: "bg-sky-700" },
                { label: "TEXAS DPS", color: "bg-amber-600" },
                { label: "NY DOCCS", color: "bg-orange-600" },
              ].map((src) => (
                <span key={src.label} className={`${src.color} text-white text-xs font-bold px-3 py-1.5 rounded-sm tracking-wider`}>
                  {src.label}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Missing Persons CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-slate-950 via-pink-950/10 to-slate-950 border-b border-slate-800/50">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row items-center gap-8"
          >
            <div className="flex-1 text-center md:text-left">
              <p className="text-pink-400 font-mono text-sm tracking-[0.2em] mb-3">NamUs DATABASE</p>
              <h2 className="font-heading font-bold text-2xl md:text-4xl tracking-tight uppercase text-white mb-3">
                Missing Persons
              </h2>
              <p className="text-slate-400 text-base mb-6">
                Help reunite families. Browse missing persons from the National Missing and 
                Unidentified Persons System. Every tip counts.
              </p>
              <button 
                onClick={() => navigate('/missing-persons')}
                className="bg-pink-500 hover:bg-pink-400 text-white font-bold px-8 py-3 rounded-sm tracking-wider transition-colors flex items-center gap-2 mx-auto md:mx-0"
                data-testid="browse-missing-persons-btn"
              >
                <Users className="w-5 h-5" />
                Browse Missing Persons
              </button>
            </div>
            <div className="text-center">
              <div className="bg-slate-900/80 border border-pink-500/20 rounded-sm p-6">
                <Users className="w-10 h-10 text-pink-400 mx-auto mb-3" />
                <p className="text-3xl font-bold text-white font-mono">99+</p>
                <p className="text-slate-400 text-sm mt-1">Active Missing Persons Cases</p>
                <p className="text-pink-400 text-xs font-mono mt-2 tracking-wider">WITH PHOTOS</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Meet the Team Section */}
      <section id="team" className="py-24 px-4 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-cyan-400 font-mono text-sm tracking-[0.2em] mb-4">LEADERSHIP</p>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight uppercase text-white mb-4">
              Meet the Team
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Founded by entrepreneurs with deep roots in criminal justice and technology, 
              Wysspr combines industry expertise with innovative solutions to help bring fugitives to justice.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Wilkinson Egwu */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="card-tactical p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_f70272a8-a909-4040-a197-763dc17b1215/artifacts/xtqo6v37_Will.png"
                  alt="Wilkinson Egwu"
                  className="w-20 h-20 rounded-full object-cover border-2 border-cyan-500/50"
                />
                <div>
                  <h3 className="font-heading font-bold text-xl text-white">Wilkinson Egwu</h3>
                  <p className="text-cyan-400 font-semibold">Founder & CEO</p>
                </div>
              </div>
              <p className="text-slate-300 leading-relaxed mb-4">
                A serial entrepreneur with a passion for justice, Wilkinson brings the vision and drive behind Wysspr. 
                As a 3X founder with a patent to his name, he combines innovation with execution.
              </p>
              <p className="text-slate-400 text-sm leading-relaxed">
                Wilkinson studied at <span className="text-cyan-400 font-semibold">John Jay College of Criminal Justice</span>, 
                the nation's premier institution for criminal justice education. His academic foundation in law enforcement, 
                combined with his entrepreneurial experience, uniquely positions him to bridge the gap between 
                technology and public safety.
              </p>
              <div className="mt-6 pt-4 border-t border-slate-700 flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-cyan-500/10 text-cyan-400 text-xs rounded-full">3x Founder</span>
                <span className="px-3 py-1 bg-amber-500/10 text-amber-400 text-xs rounded-full">Patent Holder</span>
                <span className="px-3 py-1 bg-purple-500/10 text-purple-400 text-xs rounded-full">John Jay College</span>
              </div>
            </motion.div>

            {/* Chris Smalley */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="card-tactical p-8"
            >
              <div className="flex items-center gap-4 mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_f70272a8-a909-4040-a197-763dc17b1215/artifacts/wuex71nu_Chris.jpg"
                  alt="Chris Smalley"
                  className="w-20 h-20 rounded-full object-cover border-2 border-purple-500/50"
                />
                <div>
                  <h3 className="font-heading font-bold text-xl text-white">Chris Smalley</h3>
                  <p className="text-purple-400 font-semibold">Co-Founder & CTO</p>
                </div>
              </div>
              <p className="text-slate-300 leading-relaxed mb-4">
                The technical architect behind Wysspr, Chris leads all engineering and product development. 
                His blend of management expertise and hands-on coding skills ensures the platform delivers 
                cutting-edge technology with reliability.
              </p>
              <p className="text-slate-400 text-sm leading-relaxed">
                A graduate of <span className="text-purple-400 font-semibold">Murray State University</span>, 
                Chris brings a disciplined approach to building scalable systems. His background in management 
                enables him to lead development teams effectively while his technical prowess drives 
                innovation in AI-powered tip analysis and real-time intelligence gathering.
              </p>
              <div className="mt-6 pt-4 border-t border-slate-700 flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-purple-500/10 text-purple-400 text-xs rounded-full">Technical Lead</span>
                <span className="px-3 py-1 bg-cyan-500/10 text-cyan-400 text-xs rounded-full">Full-Stack Engineer</span>
                <span className="px-3 py-1 bg-green-500/10 text-green-400 text-xs rounded-full">Murray State</span>
              </div>
            </motion.div>
          </div>

          {/* Mission Statement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-12 text-center"
          >
            <div className="card-tactical p-8 border-cyan-500/30 bg-cyan-500/5">
              <p className="text-slate-300 text-lg italic leading-relaxed">
                "We built Wysspr because we believe every citizen can play a role in keeping communities safe. 
                By combining cutting-edge AI with the power of community vigilance, we're creating a 
                platform where technology serves justice."
              </p>
              <p className="text-cyan-400 font-semibold mt-4">— Wilkinson & Chris, Founders</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-cyan-400 font-mono text-sm tracking-[0.2em] mb-4">PLATFORM CAPABILITIES</p>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight uppercase text-white">
              Advanced Intelligence Tools
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: Map, title: "Live Map", desc: "Track fugitive locations on an interactive map with real-time updates." },
              { icon: MessageSquare, title: "AI Tipline", desc: "AI-powered tip analysis that assesses credibility and urgency." },
              { icon: FolderOpen, title: "Case Files", desc: "Track fugitives and get notified when new tips are submitted." },
              { icon: Gift, title: "Bounty Pool", desc: "Contribute to increase rewards and help bring fugitives to justice." }
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="card-tactical p-6 text-center"
              >
                <div className="inline-flex p-4 bg-cyan-500/10 rounded-sm mb-4">
                  <feature.icon className="w-8 h-8 text-cyan-400" />
                </div>
                <h3 className="font-heading font-bold text-lg text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section id="why-join" className="py-24 px-4 bg-slate-950/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-cyan-400 font-mono text-sm tracking-[0.2em] mb-4">JOIN THE MISSION</p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight uppercase text-white mb-6">
                Why Join Wysspr?
              </h2>
              <div className="space-y-6">
                {[
                  { icon: DollarSign, title: "Earn Rewards", desc: "Access to millions in FBI reward money for actionable tips leading to captures." },
                  { icon: Shield, title: "Stay Anonymous", desc: "Military-grade encryption protects your identity. Submit tips without fear." },
                  { icon: Bell, title: "Real-time Alerts", desc: "Get notified instantly when fugitives you're tracking have new activity." },
                  { icon: Bot, title: "AI-Powered Analysis", desc: "Our AI analyzes your tips for credibility and prioritizes actionable intelligence." }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-cyan-500/10 rounded-sm flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-white mb-1">{item.title}</h3>
                      <p className="text-slate-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button 
                onClick={() => navigate('/login')}
                className="btn-primary mt-8"
                data-testid="join-now-btn"
              >
                Join Now - It's Free
              </button>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <WhyJoinStats />
            </motion.div>
          </div>
        </div>
      </section>

      {/* For Law Enforcement Section */}
      <section id="agencies" className="py-24 px-4 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-purple-400 font-mono text-sm tracking-[0.2em] mb-4">FOR LAW ENFORCEMENT</p>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight uppercase text-white mb-4">
              Agency Intelligence Portal
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Access aggregated intelligence, AI-powered predictions, and real-time tip management. 
              Built for US Marshals, FBI, DEA, ATF, and state law enforcement.
            </p>
          </motion.div>

          {/* Benefits Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="card-tactical p-6 border-purple-500/30 hover:border-purple-500/50 transition-colors"
            >
              <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mb-4">
                <Brain className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="font-heading font-bold text-xl text-white mb-2">Predictive AI Analysis</h3>
              <p className="text-slate-400 text-sm">
                Anticipate fugitive movements with AI-powered predictions. Get flight risk scores, 
                48-hour forecasts, and recommended interception points.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="card-tactical p-6 border-purple-500/30 hover:border-purple-500/50 transition-colors"
            >
              <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="font-heading font-bold text-xl text-white mb-2">Multi-Agency Database</h3>
              <p className="text-slate-400 text-sm">
                Unified access to FBI, US Marshals, DEA, ATF, and ICE fugitive databases. 
                One dashboard for all federal and state wanted persons.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="card-tactical p-6 border-purple-500/30 hover:border-purple-500/50 transition-colors"
            >
              <div className="w-12 h-12 bg-purple-500/20 rounded-full flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="font-heading font-bold text-xl text-white mb-2">Crowdsourced Intelligence</h3>
              <p className="text-slate-400 text-sm">
                Receive real-time tips from citizens across America. AI-filtered for credibility 
                with priority scoring and location tracking.
              </p>
            </motion.div>
          </div>

          {/* How It Works for Agencies */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-tactical p-8 mb-12"
          >
            <h3 className="font-heading font-bold text-2xl text-white mb-6 text-center">How The Agency Portal Works</h3>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { step: "1", title: "Register", desc: "Submit your agency details with your official .gov email address", icon: FileText },
                { step: "2", title: "Verify", desc: "Our team verifies your agency through official channels within 48 hours", icon: Shield },
                { step: "3", title: "Access", desc: "Get secure access to the intelligence portal and AI analysis tools", icon: Eye },
                { step: "4", title: "Capture", desc: "Use predictive intelligence to increase capture rates", icon: Target }
              ].map((item, idx) => (
                <div key={idx} className="text-center">
                  <div className="relative inline-flex mb-4">
                    <div className="w-14 h-14 bg-purple-500/20 rounded-full flex items-center justify-center border border-purple-500/30">
                      <item.icon className="w-6 h-6 text-purple-400" />
                    </div>
                    <span className="absolute -top-2 -right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
                      {item.step}
                    </span>
                  </div>
                  <h4 className="font-heading font-bold text-lg text-white mb-1">{item.title}</h4>
                  <p className="text-slate-400 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Stats & CTA */}
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800/50 p-4 rounded-sm text-center">
                  <p className="text-3xl font-bold text-purple-400">5</p>
                  <p className="text-slate-400 text-sm">Federal Databases</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-sm text-center">
                  <p className="text-3xl font-bold text-purple-400">AI</p>
                  <p className="text-slate-400 text-sm">Predictive Analysis</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-sm text-center">
                  <p className="text-3xl font-bold text-purple-400">24/7</p>
                  <p className="text-slate-400 text-sm">Real-time Tips</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-sm text-center">
                  <p className="text-3xl font-bold text-purple-400">100%</p>
                  <p className="text-slate-400 text-sm">Tipster Privacy</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-center md:text-left"
            >
              <h3 className="font-heading font-bold text-2xl text-white mb-4">Ready to Increase Capture Rates?</h3>
              <p className="text-slate-400 mb-6">
                Join law enforcement agencies across America using Wysspr's intelligence platform. 
                Free for verified government agencies.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <Link to="/agency/login" className="btn-primary px-8 py-3 flex items-center justify-center gap-2">
                  <Shield className="w-5 h-5" />
                  Access Agency Portal
                </Link>
                <a href="mailto:support@wysspr.com" className="btn-secondary px-8 py-3 flex items-center justify-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Contact Sales
                </a>
              </div>
              <p className="text-slate-500 text-xs mt-4">
                Verification required. Only authorized law enforcement personnel.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-cyan-400 font-mono text-sm tracking-[0.2em] mb-4">GET IN TOUCH</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight uppercase text-white mb-6">
              Contact Us
            </h2>
            <p className="text-slate-400 text-lg mb-8">
              Have questions about the platform? Need assistance with your account? 
              Our team is here to help.
            </p>
            
            <div className="card-tactical p-8 inline-block">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="w-12 h-12 bg-cyan-500/20 rounded-full flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-cyan-400" />
                </div>
                <div className="text-left">
                  <p className="text-slate-500 text-sm uppercase tracking-wider">Email Support</p>
                  <a href="mailto:support@wysspr.com" className="text-cyan-400 text-xl font-semibold hover:underline">
                    support@wysspr.com
                  </a>
                </div>
              </div>
              <p className="text-slate-500 text-sm">
                We typically respond within 24 hours
              </p>
            </div>
            
            <div className="mt-12 grid md:grid-cols-3 gap-6">
              <div className="card-tactical p-6">
                <Shield className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
                <h3 className="font-heading font-bold text-white mb-2">Security Issues</h3>
                <p className="text-slate-500 text-sm">Report security vulnerabilities or concerns</p>
              </div>
              <div className="card-tactical p-6">
                <FileText className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
                <h3 className="font-heading font-bold text-white mb-2">Tip Support</h3>
                <p className="text-slate-500 text-sm">Questions about submitted tips or rewards</p>
              </div>
              <div className="card-tactical p-6">
                <Users className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
                <h3 className="font-heading font-bold text-white mb-2">Partnerships</h3>
                <p className="text-slate-500 text-sm">Law enforcement or media inquiries</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-12 px-4 pb-24">
        <div className="max-w-7xl mx-auto">
          {/* FBI Disclaimer Banner */}
          <div className="mb-8 p-4 rounded-sm bg-red-500/10 border border-red-500/30">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-red-400 font-semibold text-sm uppercase tracking-wider">Important Notice</p>
                <p className="text-slate-400 text-sm mt-1">
                  For urgent sightings or tips, please contact the FBI directly at{' '}
                  <span className="text-cyan-400 font-bold">1-800-CALL-FBI (1-800-225-5324)</span> or visit{' '}
                  <a href="https://tips.fbi.gov" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">tips.fbi.gov</a>.
                </p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Shield className="w-8 h-8 text-cyan-500" />
                <span className="font-heading font-bold text-xl text-white">WYSSPR</span>
              </div>
              <p className="text-slate-500 text-sm">
                Advanced AI-powered platform for tracking the FBI's Most Wanted fugitives.
              </p>
            </div>
            <div>
              <h4 className="font-heading font-bold text-white mb-4 uppercase tracking-wider text-sm">Platform</h4>
              <ul className="space-y-2 text-slate-500 text-sm">
                <li><Link to="/map" className="hover:text-cyan-400 transition-colors">Live Map</Link></li>
                <li><Link to="/state-fugitives" className="hover:text-cyan-400 transition-colors">State Fugitives</Link></li>
                <li><Link to="/missing-persons" className="hover:text-pink-400 transition-colors">Missing Persons</Link></li>
                <li><Link to="/login" className="hover:text-cyan-400 transition-colors">Submit Tip</Link></li>
                <li><Link to="/login" className="hover:text-cyan-400 transition-colors">Bounty Pool</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-bold text-white mb-4 uppercase tracking-wider text-sm">Company</h4>
              <ul className="space-y-2 text-slate-500 text-sm">
                <li><a href="#about" className="hover:text-cyan-400 transition-colors">About</a></li>
                <li><a href="#team" className="hover:text-cyan-400 transition-colors">Our Team</a></li>
                <li><a href="#contact" className="hover:text-cyan-400 transition-colors">Contact</a></li>
                <li><a href="#why-join" className="hover:text-cyan-400 transition-colors">Why Join</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-bold text-white mb-4 uppercase tracking-wider text-sm">Contact</h4>
              <p className="text-slate-500 text-sm mb-2">Email us at:</p>
              <a href="mailto:support@wysspr.com" className="text-cyan-400 hover:underline">
                support@wysspr.com
              </a>
              <div className="mt-4">
                <p className="text-slate-500 text-sm mb-2">Follow us:</p>
                <div className="flex items-center gap-3">
                  {/* X (Twitter) */}
                  <a 
                    href="https://x.com/wyssprintel" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-9 h-9 bg-slate-800 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-all"
                    data-testid="social-x-link"
                    aria-label="Follow us on X"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                  {/* Facebook */}
                  <a 
                    href="https://www.facebook.com/wysspr" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-9 h-9 bg-slate-800 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-all"
                    data-testid="social-facebook-link"
                    aria-label="Follow us on Facebook"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                  {/* Discord */}
                  <a 
                    href="https://discord.com/channels/1455626782496657673/1455627044179148873" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-9 h-9 bg-slate-800 rounded-full flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700 transition-all"
                    data-testid="social-discord-link"
                    aria-label="Join our Discord"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © 2026 Wysspr Intelligence.
            </p>
            <div className="flex gap-6 text-slate-500 text-sm">
              <Link to="/privacy" className="hover:text-cyan-400 transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-cyan-400 transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const [fugitives, setFugitives] = useState([]);
  const [filteredFugitives, setFilteredFugitives] = useState([]);
  const [trackedIds, setTrackedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, reward: 0, topTenCount: 0, rewardsEarned: 0, captureCount: 0 });
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'top10', 'highReward'
  const [onlineUsers, setOnlineUsers] = useState({ online_count: 0, users: [] });
  const [viewMode, setViewMode] = useState('all'); // 'all', 'foryou', 'threats', 'leaderboard', 'community-wanted'
  const [personalizedData, setPersonalizedData] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [personalizedLoading, setPersonalizedLoading] = useState(false);
  const [threatData, setThreatData] = useState(null);
  const [threatLoading, setThreatLoading] = useState(false);
  const [recentCaptures, setRecentCaptures] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myGamificationStats, setMyGamificationStats] = useState(null);
  const [activityFeed, setActivityFeed] = useState([]);
  const [communityMostWanted, setCommunityMostWanted] = useState([]);
  const [referralData, setReferralData] = useState(null);
  const [stateFugitives, setStateFugitives] = useState([]);
  const [stateFugSources, setStateFugSources] = useState({});
  const [stateFugTotal, setStateFugTotal] = useState(0);
  const [stateFugLoading, setStateFugLoading] = useState(false);
  const [stateFugSearch, setStateFugSearch] = useState('');
  const [stateFugSource, setStateFugSource] = useState('');
  const [stateFugPage, setStateFugPage] = useState(0);
  const [missingPersons, setMissingPersons] = useState([]);
  const [missingTotal, setMissingTotal] = useState(0);
  const [missingLoading, setMissingLoading] = useState(false);
  const [missingSearch, setMissingSearch] = useState('');
  const [missingPage, setMissingPage] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    fetchRecentCaptures();
    fetchActivityFeed();
    fetchLeaderboard();
    fetchCommunityMostWanted();
    if (user) {
      fetchOnlineUsers();
      fetchPersonalizedData();
      fetchThreatData();
      fetchMyGamificationStats();
      fetchReferralData();
      // Refresh online users every 30 seconds
      const interval = setInterval(fetchOnlineUsers, 30000);
      // Refresh activity feed every 20 seconds
      const activityInterval = setInterval(fetchActivityFeed, 20000);
      return () => {
        clearInterval(interval);
        clearInterval(activityInterval);
      };
    }
  }, [user]);

  const fetchActivityFeed = async () => {
    try {
      const res = await api.get('/activity-feed?limit=10');
      setActivityFeed(res.data.activities || []);
    } catch (error) {
      console.error('Failed to fetch activity feed');
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await api.get('/leaderboard?limit=10');
      setLeaderboard(res.data.leaderboard || []);
    } catch (error) {
      console.error('Failed to fetch leaderboard');
    }
  };

  const fetchMyGamificationStats = async () => {
    if (!user) return;
    try {
      const res = await api.get('/gamification/my-stats');
      setMyGamificationStats(res.data);
    } catch (error) {
      console.error('Failed to fetch gamification stats');
    }
  };

  const fetchCommunityMostWanted = async () => {
    try {
      const res = await api.get('/fugitives/most-wanted-community?limit=5');
      setCommunityMostWanted(res.data.most_wanted || []);
    } catch (error) {
      console.error('Failed to fetch community most wanted');
    }
  };

  const fetchReferralData = async () => {
    if (!user) return;
    try {
      const res = await api.get('/referral/my-code');
      setReferralData(res.data);
    } catch (error) {
      console.error('Failed to fetch referral data');
    }
  };

  const fetchStateFugitives = async (source = stateFugSource, search = stateFugSearch, page = stateFugPage) => {
    setStateFugLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '18');
      params.set('skip', String(page * 18));
      if (source) params.set('source', source);
      if (search) params.set('search', search);
      const res = await api.get(`/state-fugitives?${params.toString()}`);
      setStateFugitives(res.data.fugitives || []);
      setStateFugTotal(res.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch state fugitives');
    } finally {
      setStateFugLoading(false);
    }
  };

  const fetchStateFugSources = async () => {
    try {
      const res = await api.get('/state-fugitives/sources');
      setStateFugSources(res.data.sources || {});
    } catch (error) {
      console.error('Failed to fetch state fugitive sources');
    }
  };

  const fetchMissingPersons = async (search = missingSearch, page = missingPage) => {
    setMissingLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '18');
      params.set('skip', String(page * 18));
      if (search) params.set('search', search);
      const res = await api.get(`/missing-persons?${params.toString()}`);
      setMissingPersons(res.data.persons || []);
      setMissingTotal(res.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch missing persons');
    } finally {
      setMissingLoading(false);
    }
  };

  const handleVoteFugitive = async (fugitiveId) => {
    if (!user) {
      toast.error('Please login to vote');
      return;
    }
    try {
      const res = await api.post(`/fugitives/${fugitiveId}/vote`);
      toast.success(res.data.message);
      fetchCommunityMostWanted();
      fetchMyGamificationStats();
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  // Fetch state fugitives when tab is active or filters change
  useEffect(() => {
    if (viewMode === 'state-local') {
      fetchStateFugitives(stateFugSource, stateFugSearch, stateFugPage);
      if (Object.keys(stateFugSources).length === 0) {
        fetchStateFugSources();
      }
    }
  }, [viewMode, stateFugSource, stateFugPage]);

  // Debounced search for state fugitives
  useEffect(() => {
    if (viewMode !== 'state-local') return;
    const timer = setTimeout(() => {
      setStateFugPage(0);
      fetchStateFugitives(stateFugSource, stateFugSearch, 0);
    }, 400);
    return () => clearTimeout(timer);
  }, [stateFugSearch]);

  // Fetch missing persons when tab is active
  useEffect(() => {
    if (viewMode === 'missing') {
      fetchMissingPersons(missingSearch, missingPage);
    }
  }, [viewMode, missingPage]);

  // Debounced search for missing persons
  useEffect(() => {
    if (viewMode !== 'missing') return;
    const timer = setTimeout(() => {
      setMissingPage(0);
      fetchMissingPersons(missingSearch, 0);
    }, 400);
    return () => clearTimeout(timer);
  }, [missingSearch]);

  const copyReferralLink = () => {
    if (referralData?.referral_link) {
      navigator.clipboard.writeText(referralData.referral_link);
      toast.success('Referral link copied to clipboard!');
    }
  };

  const fetchRecentCaptures = async () => {
    try {
      const res = await api.get('/fugitives/captured?limit=3');
      setRecentCaptures(res.data.captured || []);
    } catch (error) {
      console.error('Failed to fetch captures');
    }
  };

  const fetchPersonalizedData = async (region = null) => {
    if (!user) return;
    setPersonalizedLoading(true);
    try {
      const url = region ? `/personalized/fugitives?region=${region}` : '/personalized/fugitives';
      const res = await api.get(url);
      setPersonalizedData(res.data);
      setSelectedRegion(res.data.user_region || 'Northeast');
    } catch (error) {
      console.error('Failed to load personalized data:', error);
    } finally {
      setPersonalizedLoading(false);
    }
  };

  const handleRegionChange = async (newRegion) => {
    setSelectedRegion(newRegion);
    // Update user's preferred region
    try {
      await api.put('/profile/region', null, { params: { region: newRegion } });
      await fetchPersonalizedData(newRegion);
      toast.success(`Region updated to ${newRegion}`);
    } catch {
      toast.error('Failed to update region');
    }
  };

  const fetchThreatData = async () => {
    if (!user) return;
    setThreatLoading(true);
    try {
      const res = await api.get('/ai/threat-dashboard');
      setThreatData(res.data);
    } catch (error) {
      console.error('Failed to load threat data:', error);
    } finally {
      setThreatLoading(false);
    }
  };

  useEffect(() => {
    // Apply filter when activeFilter or fugitives change
    if (activeFilter === 'top10') {
      setFilteredFugitives(fugitives.filter(f => (f.charges || []).includes('Ten Most Wanted Fugitives')));
    } else if (activeFilter === 'highReward') {
      setFilteredFugitives(fugitives.filter(f => (f.reward_amount || 0) >= 1000000));
    } else {
      setFilteredFugitives(fugitives);
    }
  }, [activeFilter, fugitives]);

  const fetchOnlineUsers = async () => {
    try {
      const res = await api.get('/users/online');
      setOnlineUsers(res.data);
    } catch {
      // Silent fail
    }
  };

  const fetchData = async () => {
    try {
      // Trigger FBI sync on first load (will be rate-limited server-side)
      await api.post("/fbi/sync").catch(() => {});
      
      // Fetch fugitives and rewards stats
      const [fugRes, rewardsRes] = await Promise.all([
        api.get("/fugitives?limit=250"),
        api.get("/rewards/stats").catch(() => ({ data: { total_rewards_distributed: 0, total_captures: 0 } }))
      ]);
      
      // Sort fugitives: Ten Most Wanted first, then by reward amount
      const sortedFugitives = fugRes.data.sort((a, b) => {
        const aIsTopTen = (a.charges || []).includes('Ten Most Wanted Fugitives');
        const bIsTopTen = (b.charges || []).includes('Ten Most Wanted Fugitives');
        
        // Top Ten first
        if (aIsTopTen && !bIsTopTen) return -1;
        if (!aIsTopTen && bIsTopTen) return 1;
        
        // Then by reward amount
        return (b.reward_amount || 0) - (a.reward_amount || 0);
      });
      setFugitives(sortedFugitives);
      setFilteredFugitives(sortedFugitives);
      
      const total = sortedFugitives.length;
      const reward = sortedFugitives.reduce((acc, f) => acc + (f.reward_amount || 0), 0);
      const topTenCount = sortedFugitives.filter(f => (f.charges || []).includes('Ten Most Wanted Fugitives')).length;
      const rewardsEarned = rewardsRes.data?.total_rewards_distributed || 0;
      const captureCount = rewardsRes.data?.total_captures || 0;
      setStats({ total, reward, topTenCount, rewardsEarned, captureCount });

      if (user) {
        const caseRes = await api.get("/case-files");
        setTrackedIds(caseRes.data.map(c => c.fugitive_id));
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleTrack = async (fugitiveId) => {
    if (!user) {
      toast.error("Please login to track fugitives");
      navigate("/login");
      return;
    }

    try {
      if (trackedIds.includes(fugitiveId)) {
        // Find and archive case file
        const caseRes = await api.get("/case-files");
        const caseFile = caseRes.data.find(c => c.fugitive_id === fugitiveId);
        if (caseFile) {
          await api.delete(`/case-files/${caseFile.case_id}`);
          setTrackedIds(prev => prev.filter(id => id !== fugitiveId));
          toast.success("Stopped tracking fugitive");
        }
      } else {
        await api.post("/case-files", { fugitive_id: fugitiveId, alert_on_tips: true });
        setTrackedIds(prev => [...prev, fugitiveId]);
        toast.success("Now tracking fugitive!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update tracking");
    }
  };

  // Clickable stat card component for dashboard
  const ClickableDashboardStat = ({ icon: Icon, label, value, color = "cyan", filterName, isActive }) => (
    <div 
      onClick={() => setActiveFilter(filterName)}
      className={`card-tactical p-6 cursor-pointer transition-all hover:scale-[1.02] ${
        isActive ? `ring-2 ring-${color === 'amber' ? 'amber' : color === 'red' ? 'red' : color === 'green' ? 'green' : 'cyan'}-500` : ''
      }`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">{label}</p>
          <p className={`font-heading font-bold text-3xl ${color === 'amber' ? 'text-amber-400' : color === 'red' ? 'text-red-400' : color === 'green' ? 'text-green-400' : 'text-cyan-400'}`}>
            {value}
          </p>
        </div>
        <div className={`p-3 rounded-sm ${color === 'amber' ? 'bg-amber-500/20' : color === 'red' ? 'bg-red-500/20' : color === 'green' ? 'bg-green-500/20' : 'bg-cyan-500/20'}`}>
          <Icon className={`w-6 h-6 ${color === 'amber' ? 'text-amber-400' : color === 'red' ? 'text-red-400' : color === 'green' ? 'text-green-400' : 'text-cyan-400'}`} />
        </div>
      </div>
      {filterName !== 'all' && (
        <p className="text-slate-600 text-xs mt-2">Click to filter</p>
      )}
    </div>
  );

  return (
    <div className="min-h-screen pt-16 pb-12" data-testid="dashboard-page">
      {/* News Ticker at Top */}
      <NewsTicker />
      
      <div className="px-4 pt-6">
        <div className="max-w-7xl mx-auto">
          {/* Main Content + Sidebar Layout */}
          <div className="flex gap-6">
            {/* Main Content */}
            <div className="flex-1 min-w-0">
              <div className="mb-8">
                <p className="text-cyan-400 font-mono text-sm tracking-[0.2em] mb-2">CONTROL ROOM</p>
                <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tight uppercase text-white">
                  Most Wanted
                </h1>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <ClickableDashboardStat icon={Target} label="Active Targets" value={stats.total} filterName="all" isActive={activeFilter === 'all'} />
                <ClickableDashboardStat icon={DollarSign} label="Total Rewards" value={`$${(stats.reward / 1000000).toFixed(0)}M+`} color="amber" filterName="all" isActive={false} />
                <ClickableDashboardStat icon={AlertTriangle} label="Top 10 Wanted" value={stats.topTenCount} color="red" filterName="top10" isActive={activeFilter === 'top10'} />
                <ClickableDashboardStat icon={Trophy} label="Rewards Earned" value={stats.rewardsEarned > 0 ? `$${(stats.rewardsEarned / 1000000).toFixed(1)}M` : '$0'} color="green" filterName="all" isActive={false} />
              </div>

              {/* Launch Progress Section */}
              <div className="mb-8 card-tactical p-5 border-cyan-500/20" data-testid="launch-progress-section">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-cyan-400" />
                    <h3 className="font-heading font-bold text-sm text-white uppercase tracking-wider">Launch Progress</h3>
                  </div>
                  <span className="text-cyan-400 font-mono text-sm font-bold" data-testid="launch-progress-percent">
                    {(() => {
                      const milestones = [
                        { done: stats.total > 0 },
                        { done: true },
                        { done: true },
                        { done: true },
                        { done: true },
                        { done: stats.topTenCount > 0 },
                      ];
                      return Math.round((milestones.filter(m => m.done).length / milestones.length) * 100);
                    })()}%
                  </span>
                </div>
                {/* Progress bar */}
                <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden mb-5">
                  <div
                    className="h-full bg-gradient-to-r from-cyan-500 to-green-400 rounded-full transition-all duration-1000"
                    style={{ width: `${(() => {
                      const milestones = [
                        { done: stats.total > 0 },
                        { done: true },
                        { done: true },
                        { done: true },
                        { done: true },
                        { done: stats.topTenCount > 0 },
                      ];
                      return Math.round((milestones.filter(m => m.done).length / milestones.length) * 100);
                    })()}%` }}
                    data-testid="launch-progress-bar"
                  />
                </div>
                {/* Milestones grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {[
                    { label: 'FBI Database', icon: Target, value: stats.total || 0, target: 100, unit: 'targets', border: 'border-red-500/30', bg: 'bg-red-500/5', text: 'text-red-400', bar: 'bg-red-500', done: stats.total > 0 },
                    { label: 'State/Local DB', icon: Shield, value: 274, target: 500, unit: 'fugitives', border: 'border-green-500/30', bg: 'bg-green-500/5', text: 'text-green-400', bar: 'bg-green-500', done: true },
                    { label: 'LEA Sources', icon: Building, value: 5, target: 10, unit: 'agencies', border: 'border-blue-500/30', bg: 'bg-blue-500/5', text: 'text-blue-400', bar: 'bg-blue-500', done: true },
                    { label: 'Security Suite', icon: Lock, value: 6, target: 6, unit: 'modules', border: 'border-cyan-500/30', bg: 'bg-cyan-500/5', text: 'text-cyan-400', bar: 'bg-cyan-500', done: true },
                    { label: 'AI Intel', icon: Brain, value: 3, target: 5, unit: 'models', border: 'border-purple-500/30', bg: 'bg-purple-500/5', text: 'text-purple-400', bar: 'bg-purple-500', done: true },
                    { label: 'Rewards Pool', icon: DollarSign, value: `$${(stats.reward / 1000000).toFixed(0)}M`, target: null, unit: '', border: 'border-amber-500/30', bg: 'bg-amber-500/5', text: 'text-amber-400', bar: 'bg-amber-500', done: stats.reward > 0 },
                  ].map((m, i) => (
                    <div key={i} className={`relative p-3 rounded-sm border transition-all ${m.done ? `${m.border} ${m.bg}` : 'border-slate-700 bg-slate-800/30'}`}>
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <m.icon className={`w-3 h-3 ${m.done ? m.text : 'text-slate-600'}`} />
                        <span className={`text-[10px] font-mono tracking-wider ${m.done ? m.text : 'text-slate-600'}`}>{m.label.toUpperCase()}</span>
                      </div>
                      <p className={`text-lg font-bold font-mono ${m.done ? 'text-white' : 'text-slate-600'}`}>
                        {typeof m.value === 'number' ? m.value.toLocaleString() : m.value}
                      </p>
                      {m.target && (
                        <div className="mt-1">
                          <div className="w-full h-1 bg-slate-700 rounded-full overflow-hidden">
                            <div className={`h-full ${m.bar} rounded-full`} style={{ width: `${Math.min(100, (typeof m.value === 'number' ? m.value : 0) / m.target * 100)}%` }} />
                          </div>
                          <p className="text-slate-600 text-[9px] font-mono mt-0.5">{m.target} {m.unit} goal</p>
                        </div>
                      )}
                      {m.done && (
                        <div className="absolute top-2 right-2">
                          <CheckCircle className={`w-3 h-3 ${m.text}`} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

        {/* Active Hunters Section */}
        {user && (
          <div className="mb-8 card-tactical p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-sm text-white uppercase tracking-wider flex items-center gap-2">
                <Users className="w-4 h-4 text-cyan-400" />
                Active Hunters
              </h3>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                  </span>
                  <span className="text-green-400 font-bold">{onlineUsers.online_count}</span>
                  <span className="text-slate-400 text-sm">online now</span>
                </div>
                <button 
                  onClick={fetchOnlineUsers}
                  className="text-slate-500 hover:text-cyan-400 transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className="w-3 h-3" />
                </button>
              </div>
            </div>
            
            {onlineUsers.users.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {onlineUsers.users.slice(0, 15).map((hunter, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-sm text-xs ${
                      hunter.is_you 
                        ? 'bg-cyan-500/20 border border-cyan-500/30 text-cyan-400' 
                        : 'bg-slate-800/50 text-slate-400'
                    }`}
                  >
                    <span className="relative flex h-1.5 w-1.5">
                      {hunter.is_online && (
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      )}
                      <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${
                        hunter.is_online ? 'bg-green-500' : 'bg-slate-500'
                      }`}></span>
                    </span>
                    {hunter.is_you ? 'You' : hunter.anonymous_id}
                  </div>
                ))}
                {onlineUsers.users.length > 15 && (
                  <div className="flex items-center px-2 py-1 text-xs text-slate-500">
                    +{onlineUsers.users.length - 15} more
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Live Activity Feed */}
        {activityFeed.length > 0 && (
          <div className="mb-6 card-tactical p-4 border-green-500/20">
            <div className="flex items-center gap-2 mb-3">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              <h3 className="text-white font-semibold text-sm">Live Activity Feed</h3>
            </div>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {activityFeed.slice(0, 5).map((activity, idx) => (
                <div key={activity.activity_id || idx} className="flex items-center gap-2 text-sm animate-fade-in">
                  <span className="text-cyan-400">→</span>
                  <span className="text-slate-300">{activity.message}</span>
                  <span className="text-slate-600 text-xs ml-auto">
                    {new Date(activity.created_at).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* View Mode Tabs - All Fugitives vs For You */}
        {user && (
          <div className="mb-6 flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setViewMode('all')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                viewMode === 'all'
                  ? 'bg-slate-700 text-white border border-cyan-500/50'
                  : 'bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 border border-slate-700/50'
              }`}
            >
              <Target className="w-4 h-4" />
              All Fugitives
            </button>
            <button
              onClick={() => setViewMode('foryou')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                viewMode === 'foryou'
                  ? 'bg-slate-700 text-white border border-cyan-500/50'
                  : 'bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 border border-slate-700/50'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              For You
            </button>
            <button
              onClick={() => setViewMode('threats')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                viewMode === 'threats'
                  ? 'bg-slate-700 text-white border border-red-500/50'
                  : 'bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 border border-slate-700/50'
              }`}
            >
              <AlertTriangle className="w-4 h-4" />
              Threat Analysis
            </button>
            <button
              onClick={() => setViewMode('leaderboard')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                viewMode === 'leaderboard'
                  ? 'bg-slate-700 text-white border border-amber-500/50'
                  : 'bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 border border-slate-700/50'
              }`}
            >
              <Trophy className="w-4 h-4" />
              Leaderboard
            </button>
            <button
              onClick={() => setViewMode('community-wanted')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                viewMode === 'community-wanted'
                  ? 'bg-slate-700 text-white border border-pink-500/50'
                  : 'bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 border border-slate-700/50'
              }`}
            >
              <Heart className="w-4 h-4" />
              Most Wanted
            </button>
            <button
              onClick={() => setViewMode('intelligence')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                viewMode === 'intelligence'
                  ? 'bg-slate-700 text-white border border-cyan-500/50'
                  : 'bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 border border-slate-700/50'
              }`}
            >
              <Radar className="w-4 h-4" />
              Intelligence
            </button>
            <button
              onClick={() => setViewMode('state-local')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                viewMode === 'state-local'
                  ? 'bg-slate-700 text-white border border-green-500/50'
                  : 'bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 border border-slate-700/50'
              }`}
              data-testid="tab-state-local"
            >
              <MapPin className="w-4 h-4" />
              State/Local
            </button>
            <button
              onClick={() => setViewMode('missing')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                viewMode === 'missing'
                  ? 'bg-slate-700 text-white border border-pink-500/50'
                  : 'bg-slate-800/50 text-slate-400 hover:text-white hover:bg-slate-700/50 border border-slate-700/50'
              }`}
              data-testid="tab-missing-persons"
            >
              <Users className="w-4 h-4" />
              Missing Persons
            </button>
          </div>
        )}

        {/* FOR YOU - Personalized Section */}
        {user && viewMode === 'foryou' && (
          <div className="space-y-8">
            {/* Region Selector */}
            <div className="card-tactical p-4 border-purple-500/30">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/20 rounded">
                    <MapPin className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-white">Your Region</h3>
                    <p className="text-slate-400 text-sm">AI-powered recommendations based on your location</p>
                  </div>
                </div>
                <select
                  value={selectedRegion}
                  onChange={(e) => handleRegionChange(e.target.value)}
                  className="bg-slate-800 border border-slate-700 text-white rounded px-4 py-2 focus:outline-none focus:border-cyan-500"
                >
                  {personalizedData?.available_regions?.map(region => (
                    <option key={region} value={region}>{region}</option>
                  )) || ['Northeast', 'Southeast', 'Midwest', 'Southwest', 'West'].map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>
            </div>

            {personalizedLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400"></div>
              </div>
            ) : personalizedData ? (
              <>
                {/* Near You Section */}
                {personalizedData.near_you?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <MapPin className="w-5 h-5 text-cyan-400" />
                      <h3 className="font-heading font-bold text-xl text-white uppercase tracking-wider">
                        Near You in {selectedRegion}
                      </h3>
                      <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 text-xs rounded">
                        {personalizedData.near_you.length} Fugitives
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {personalizedData.near_you.slice(0, 4).map(fugitive => (
                        <FugitiveCard
                          key={fugitive.fugitive_id}
                          fugitive={fugitive}
                          onClick={() => navigate(`/fugitive/${fugitive.fugitive_id}`)}
                          isTracked={trackedIds.includes(fugitive.fugitive_id)}
                          onTrack={handleTrack}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* High Value Section */}
                {personalizedData.high_value?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <DollarSign className="w-5 h-5 text-amber-400" />
                      <h3 className="font-heading font-bold text-xl text-white uppercase tracking-wider">
                        High Value Targets
                      </h3>
                      <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs rounded">
                        $100K+ Rewards
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {personalizedData.high_value.slice(0, 4).map(fugitive => (
                        <FugitiveCard
                          key={fugitive.fugitive_id}
                          fugitive={fugitive}
                          onClick={() => navigate(`/fugitive/${fugitive.fugitive_id}`)}
                          isTracked={trackedIds.includes(fugitive.fugitive_id)}
                          onTrack={handleTrack}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommended Based on History */}
                {personalizedData.recommended?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <Sparkles className="w-5 h-5 text-purple-400" />
                      <h3 className="font-heading font-bold text-xl text-white uppercase tracking-wider">
                        Recommended For You
                      </h3>
                      <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded">
                        Based on History
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {personalizedData.recommended.slice(0, 4).map(fugitive => (
                        <FugitiveCard
                          key={fugitive.fugitive_id}
                          fugitive={fugitive}
                          onClick={() => navigate(`/fugitive/${fugitive.fugitive_id}`)}
                          isTracked={trackedIds.includes(fugitive.fugitive_id)}
                          onTrack={handleTrack}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Trending Section */}
                {personalizedData.trending?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <TrendingUp className="w-5 h-5 text-red-400" />
                      <h3 className="font-heading font-bold text-xl text-white uppercase tracking-wider">
                        Trending This Week
                      </h3>
                      <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded">
                        Most Tipped
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {personalizedData.trending.slice(0, 4).map(fugitive => (
                        <FugitiveCard
                          key={fugitive.fugitive_id}
                          fugitive={fugitive}
                          onClick={() => navigate(`/fugitive/${fugitive.fugitive_id}`)}
                          isTracked={trackedIds.includes(fugitive.fugitive_id)}
                          onTrack={handleTrack}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* No Data State */}
                {!personalizedData.near_you?.length && !personalizedData.high_value?.length && 
                 !personalizedData.recommended?.length && !personalizedData.trending?.length && (
                  <div className="card-tactical p-8 text-center">
                    <Sparkles className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <h3 className="font-heading font-bold text-xl text-white mb-2">Building Your Profile</h3>
                    <p className="text-slate-400 mb-4">
                      Browse fugitives and submit tips to get personalized recommendations
                    </p>
                    <button
                      onClick={() => setViewMode('all')}
                      className="btn-primary"
                    >
                      Explore Fugitives
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="card-tactical p-8 text-center">
                <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <p className="text-slate-400">Failed to load personalized data. Please try again.</p>
                <button
                  onClick={() => fetchPersonalizedData()}
                  className="btn-secondary mt-4"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        )}

        {/* Threat Assessment Dashboard */}
        {user && viewMode === 'threats' && (
          <div className="space-y-6">
            {/* Threat Summary Header */}
            <div className="card-tactical p-4 border-red-500/30 bg-gradient-to-r from-red-500/10 to-orange-500/10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-500/20 rounded">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-white">Dynamic Threat Assessment</h3>
                  <p className="text-slate-400 text-sm">AI-powered threat analysis based on criminal profiles, charges, and behavioral patterns</p>
                </div>
              </div>
            </div>

            {threatLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-400"></div>
              </div>
            ) : threatData ? (
              <>
                {/* Threat Level Summary */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {[
                    { level: 'CRITICAL', count: threatData.summary.threat_breakdown.CRITICAL, color: 'red', icon: AlertTriangle },
                    { level: 'HIGH', count: threatData.summary.threat_breakdown.HIGH, color: 'orange', icon: AlertTriangle },
                    { level: 'ELEVATED', count: threatData.summary.threat_breakdown.ELEVATED, color: 'yellow', icon: Activity },
                    { level: 'MODERATE', count: threatData.summary.threat_breakdown.MODERATE, color: 'blue', icon: Shield },
                    { level: 'LOW', count: threatData.summary.threat_breakdown.LOW, color: 'green', icon: CheckCircle }
                  ].map(({ level, count, color, icon: Icon }) => (
                    <div key={level} className={`card-tactical p-4 border-${color}-500/30`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-slate-400 text-xs uppercase tracking-wider">{level}</p>
                          <p className={`font-heading font-bold text-2xl text-${color}-400`}>{count}</p>
                        </div>
                        <Icon className={`w-6 h-6 text-${color}-400 opacity-50`} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Critical Threats Section */}
                {threatData.critical_threats?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      <h3 className="font-heading font-bold text-xl text-white uppercase tracking-wider">
                        Critical Threats
                      </h3>
                      <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded animate-pulse">
                        EXTREME DANGER
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {threatData.critical_threats.map(threat => (
                        <div 
                          key={threat.fugitive_id}
                          className="card-tactical p-4 border-red-500/50 bg-red-500/5 cursor-pointer hover:border-red-400 transition-all"
                          onClick={() => navigate(`/fugitive/${threat.fugitive_id}`)}
                        >
                          <div className="flex gap-4">
                            <img 
                              src={threat.image_url || 'https://via.placeholder.com/100x100?text=No+Image'} 
                              alt={threat.name}
                              className="w-20 h-20 object-cover rounded border border-red-500/30"
                            />
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-heading font-bold text-white">{threat.name}</h4>
                                <div className="flex items-center gap-2">
                                  <span className="text-2xl font-bold text-red-400">{threat.score}</span>
                                  <span className="text-xs text-red-400">/100</span>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {threat.top_factors?.slice(0, 3).map((factor, idx) => (
                                  <span key={idx} className="px-2 py-0.5 bg-red-500/20 text-red-300 text-xs rounded">
                                    {factor.factor}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* High Risk Section */}
                {threatData.high_threats?.length > 0 && (
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <AlertTriangle className="w-5 h-5 text-orange-400" />
                      <h3 className="font-heading font-bold text-xl text-white uppercase tracking-wider">
                        High Risk Fugitives
                      </h3>
                      <span className="px-2 py-1 bg-orange-500/20 text-orange-400 text-xs rounded">
                        HIGH DANGER
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {threatData.high_threats.map(threat => (
                        <div 
                          key={threat.fugitive_id}
                          className="card-tactical p-3 border-orange-500/30 cursor-pointer hover:border-orange-400 transition-all"
                          onClick={() => navigate(`/fugitive/${threat.fugitive_id}`)}
                        >
                          <div className="flex items-center gap-3">
                            <img 
                              src={threat.image_url || 'https://via.placeholder.com/60x60?text=N/A'} 
                              alt={threat.name}
                              className="w-12 h-12 object-cover rounded border border-orange-500/30"
                            />
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-white text-sm truncate">{threat.name}</h4>
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gradient-to-r from-orange-500 to-red-500"
                                    style={{ width: `${threat.score}%` }}
                                  />
                                </div>
                                <span className="text-orange-400 text-xs font-bold">{threat.score}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Refresh Button */}
                <div className="flex justify-center">
                  <button
                    onClick={fetchThreatData}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Refresh Analysis
                  </button>
                </div>
              </>
            ) : (
              <div className="card-tactical p-8 text-center">
                <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <p className="text-slate-400">Failed to load threat data. Please try again.</p>
                <button onClick={fetchThreatData} className="btn-secondary mt-4">
                  Retry
                </button>
              </div>
            )}
          </div>
        )}

        {/* LEADERBOARD Section */}
        {viewMode === 'leaderboard' && (
          <div className="space-y-6">
            {/* My Stats Card */}
            {user && myGamificationStats && (
              <div className="card-tactical p-6 border-amber-500/30 bg-gradient-to-r from-amber-500/5 to-yellow-500/5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading font-bold text-lg text-white flex items-center gap-2">
                    <span className="text-2xl">{myGamificationStats.rank_icon}</span>
                    Your Hunter Profile
                  </h3>
                  <span className={`px-3 py-1 rounded-full text-sm font-bold uppercase`} style={{backgroundColor: `${myGamificationStats.rank_color}20`, color: myGamificationStats.rank_color}}>
                    {myGamificationStats.rank} Rank
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="bg-slate-800/50 p-3 rounded">
                    <p className="text-slate-400 text-xs">Total Points</p>
                    <p className="text-amber-400 font-bold text-xl">{myGamificationStats.points?.toLocaleString()}</p>
                  </div>
                  <div className="bg-slate-800/50 p-3 rounded">
                    <p className="text-slate-400 text-xs">Tips Submitted</p>
                    <p className="text-cyan-400 font-bold text-xl">{myGamificationStats.tips_submitted}</p>
                  </div>
                  <div className="bg-slate-800/50 p-3 rounded">
                    <p className="text-slate-400 text-xs">Tips Verified</p>
                    <p className="text-green-400 font-bold text-xl">{myGamificationStats.tips_verified}</p>
                  </div>
                  <div className="bg-slate-800/50 p-3 rounded">
                    <p className="text-slate-400 text-xs">Referrals</p>
                    <p className="text-purple-400 font-bold text-xl">{myGamificationStats.referrals}</p>
                  </div>
                </div>
                {myGamificationStats.next_rank && (
                  <div className="bg-slate-800/30 p-3 rounded">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-slate-400">Progress to {myGamificationStats.next_rank}</span>
                      <span className="text-amber-400">{myGamificationStats.points_to_next_rank} points to go</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2">
                      <div className="bg-gradient-to-r from-amber-500 to-yellow-500 h-2 rounded-full" style={{width: `${Math.min(100, (myGamificationStats.points / (myGamificationStats.points + myGamificationStats.points_to_next_rank)) * 100)}%`}}></div>
                    </div>
                  </div>
                )}
                {/* Badges */}
                {myGamificationStats.badges?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-slate-400 text-sm mb-2">Your Badges</p>
                    <div className="flex flex-wrap gap-2">
                      {myGamificationStats.badges.map((badge, idx) => (
                        <div key={idx} className="px-3 py-1 bg-slate-800 rounded-full text-sm flex items-center gap-1" title={badge.description}>
                          <span>{badge.icon}</span>
                          <span className="text-white">{badge.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Referral Program Card */}
            {user && referralData && (
              <div className="card-tactical p-6 border-purple-500/30">
                <h3 className="font-heading font-bold text-lg text-white mb-4 flex items-center gap-2">
                  <Gift className="w-5 h-5 text-purple-400" />
                  Referral Program
                </h3>
                <p className="text-slate-400 text-sm mb-4">Invite friends and earn 100 points for each signup!</p>
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="text"
                    value={referralData.referral_link}
                    readOnly
                    className="flex-1 bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm"
                  />
                  <button onClick={copyReferralLink} className="btn-primary px-4 py-2">
                    Copy Link
                  </button>
                </div>
                <div className="flex gap-4 text-center">
                  <div className="flex-1 bg-slate-800/50 p-3 rounded">
                    <p className="text-2xl font-bold text-purple-400">{referralData.referrals_count}</p>
                    <p className="text-slate-400 text-xs">Friends Referred</p>
                  </div>
                  <div className="flex-1 bg-slate-800/50 p-3 rounded">
                    <p className="text-2xl font-bold text-amber-400">{referralData.rewards_earned}</p>
                    <p className="text-slate-400 text-xs">Points Earned</p>
                  </div>
                </div>
              </div>
            )}

            {/* Points Structure & Rewards Card */}
            <div className="card-tactical p-6 border-green-500/30">
              <h3 className="font-heading font-bold text-lg text-white mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-green-400" />
                How to Earn Points
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                <div className="bg-slate-800/50 p-3 rounded text-center">
                  <p className="text-green-400 font-bold text-lg">+10</p>
                  <p className="text-slate-400 text-xs">Submit a Tip</p>
                </div>
                <div className="bg-slate-800/50 p-3 rounded text-center">
                  <p className="text-green-400 font-bold text-lg">+50</p>
                  <p className="text-slate-400 text-xs">Tip Verified</p>
                </div>
                <div className="bg-slate-800/50 p-3 rounded text-center">
                  <p className="text-green-400 font-bold text-lg">+500</p>
                  <p className="text-slate-400 text-xs">Capture Contribution</p>
                </div>
                <div className="bg-slate-800/50 p-3 rounded text-center">
                  <p className="text-green-400 font-bold text-lg">+100</p>
                  <p className="text-slate-400 text-xs">Refer a Friend</p>
                </div>
                <div className="bg-slate-800/50 p-3 rounded text-center">
                  <p className="text-green-400 font-bold text-lg">+5</p>
                  <p className="text-slate-400 text-xs">Community Post</p>
                </div>
                <div className="bg-slate-800/50 p-3 rounded text-center">
                  <p className="text-green-400 font-bold text-lg">+1</p>
                  <p className="text-slate-400 text-xs">Vote for Fugitive</p>
                </div>
              </div>
              
              {/* Ranks */}
              <h4 className="text-white font-semibold text-sm mb-3">Hunter Ranks</h4>
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="px-3 py-1 bg-slate-700/50 rounded-full text-xs text-slate-400">🎯 Rookie (0+)</span>
                <span className="px-3 py-1 bg-amber-900/30 rounded-full text-xs text-amber-600">🥉 Bronze (100+)</span>
                <span className="px-3 py-1 bg-slate-600/50 rounded-full text-xs text-slate-300">🥈 Silver (1,000+)</span>
                <span className="px-3 py-1 bg-amber-500/20 rounded-full text-xs text-amber-400">🥇 Gold (5,000+)</span>
                <span className="px-3 py-1 bg-cyan-500/20 rounded-full text-xs text-cyan-300">💎 Platinum (10,000+)</span>
              </div>
              
              {/* Prize Drawing */}
              <div className="bg-gradient-to-r from-amber-500/10 to-green-500/10 border border-amber-500/30 rounded p-4">
                <div className="flex items-center gap-3">
                  <div className="text-3xl">🎉</div>
                  <div>
                    <h4 className="text-amber-400 font-bold">Monthly $100 Prize Drawing!</h4>
                    <p className="text-slate-400 text-sm">Reach 10,000 points to enter the monthly drawing for a chance to win $100!</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Leaderboard Table */}
            <div className="card-tactical p-6">
              <h3 className="font-heading font-bold text-lg text-white mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-400" />
                Top Hunters Leaderboard
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="py-3 px-4 text-left text-slate-400 text-sm">#</th>
                      <th className="py-3 px-4 text-left text-slate-400 text-sm">Hunter</th>
                      <th className="py-3 px-4 text-left text-slate-400 text-sm">Rank</th>
                      <th className="py-3 px-4 text-right text-slate-400 text-sm">Points</th>
                      <th className="py-3 px-4 text-right text-slate-400 text-sm">Tips</th>
                      <th className="py-3 px-4 text-right text-slate-400 text-sm">Badges</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.map((entry, idx) => (
                      <tr key={idx} className={`border-b border-slate-800 ${idx < 3 ? 'bg-amber-500/5' : ''}`}>
                        <td className="py-3 px-4">
                          {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : <span className="text-slate-500">{entry.position}</span>}
                        </td>
                        <td className="py-3 px-4 font-mono text-cyan-400">{entry.anonymous_id}</td>
                        <td className="py-3 px-4">
                          <span className="flex items-center gap-1">
                            {entry.rank_icon} <span className="text-slate-300 capitalize">{entry.rank}</span>
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right text-amber-400 font-bold">{entry.points.toLocaleString()}</td>
                        <td className="py-3 px-4 text-right text-slate-300">{entry.tips_submitted}</td>
                        <td className="py-3 px-4 text-right text-purple-400">{entry.badge_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* COMMUNITY MOST WANTED Section */}
        {viewMode === 'community-wanted' && (
          <div className="space-y-6">
            <div className="card-tactical p-6 border-pink-500/30">
              <h3 className="font-heading font-bold text-lg text-white mb-2 flex items-center gap-2">
                <Heart className="w-5 h-5 text-pink-400" />
                Community's Most Wanted
              </h3>
              <p className="text-slate-400 text-sm mb-6">Vote for the fugitives you want to see captured most. Your voice matters!</p>
              
              {communityMostWanted.length === 0 ? (
                <div className="text-center py-8">
                  <Heart className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No votes yet. Be the first to vote!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {communityMostWanted.map((fugitive, idx) => (
                    <div key={fugitive.fugitive_id} className="flex items-center gap-4 p-4 bg-slate-800/50 rounded-lg hover:bg-slate-800 transition-colors">
                      <div className="text-2xl font-bold text-pink-400 w-8">#{idx + 1}</div>
                      <img 
                        src={fugitive.image_url || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100'} 
                        alt={fugitive.name}
                        className="w-16 h-16 rounded object-cover"
                      />
                      <div className="flex-1">
                        <h4 className="text-white font-semibold">{fugitive.name}</h4>
                        <p className="text-amber-400 text-sm">${(fugitive.reward_amount || 0).toLocaleString()} Reward</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-pink-400">{fugitive.community_votes || 0}</p>
                        <p className="text-slate-500 text-xs">votes</p>
                      </div>
                      <button
                        onClick={() => handleVoteFugitive(fugitive.fugitive_id)}
                        className="px-4 py-2 bg-pink-500/20 text-pink-400 rounded hover:bg-pink-500/30 transition-colors flex items-center gap-2"
                      >
                        <Heart className="w-4 h-4" />
                        Vote
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* All Fugitives Grid for Voting */}
            <div>
              <h3 className="font-heading font-bold text-lg text-white mb-4">Vote for Any Fugitive</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {fugitives.slice(0, 12).map((fugitive) => (
                  <div key={fugitive.fugitive_id} className="card-tactical p-4 flex items-center gap-3">
                    <img 
                      src={fugitive.image_url || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100'} 
                      alt={fugitive.name}
                      className="w-12 h-12 rounded object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-semibold text-sm truncate">{fugitive.name}</h4>
                      <p className="text-slate-500 text-xs">{fugitive.community_votes || 0} votes</p>
                    </div>
                    <button
                      onClick={() => handleVoteFugitive(fugitive.fugitive_id)}
                      className="p-2 bg-pink-500/10 text-pink-400 rounded hover:bg-pink-500/20 transition-colors"
                    >
                      <Heart className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Intelligence Dashboard View */}
        {viewMode === 'intelligence' && user && (
          <IntelligenceDashboard token={localStorage.getItem('token')} />
        )}

        {/* STATE/LOCAL FUGITIVES Tab */}
        {viewMode === 'state-local' && (
          <div className="space-y-5" data-testid="state-local-section">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h2 className="font-heading font-bold text-xl text-white uppercase tracking-wider flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-400" />
                  State & Local Fugitives
                </h2>
                <p className="text-slate-400 text-sm mt-1">
                  Live data from {Object.keys(stateFugSources).length} law enforcement agencies &mdash; {stateFugTotal.toLocaleString()} fugitives
                </p>
              </div>
              <button
                onClick={() => navigate('/state-fugitives')}
                className="btn-secondary flex items-center gap-2 text-sm py-2 px-4"
                data-testid="view-full-database-btn"
              >
                <ExternalLink className="w-4 h-4" />
                Full Database
              </button>
            </div>

            {/* Source badges */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => { setStateFugSource(''); setStateFugPage(0); }}
                className={`px-3 py-1.5 text-xs font-mono tracking-wider rounded-sm border transition-all ${
                  !stateFugSource ? 'bg-green-500/20 border-green-500/50 text-green-400' : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-600'
                }`}
                data-testid="state-filter-all"
              >
                ALL
              </button>
              {Object.entries(stateFugSources).map(([key, val]) => {
                const cfgMap = {
                  fbi_top_ten: { label: 'FBI TOP 10', active: 'bg-red-500/20 border-red-500/50 text-red-400' },
                  texas_dps: { label: 'TEXAS DPS', active: 'bg-amber-500/20 border-amber-500/50 text-amber-400' },
                  us_marshals: { label: 'US MARSHALS', active: 'bg-blue-500/20 border-blue-500/50 text-blue-400' },
                  lapd: { label: 'LAPD', active: 'bg-sky-500/20 border-sky-500/50 text-sky-400' },
                  ny_doccs: { label: 'NY DOCCS', active: 'bg-orange-500/20 border-orange-500/50 text-orange-400' },
                };
                const cfg = cfgMap[key] || { label: key.toUpperCase(), active: 'bg-slate-500/20 border-slate-500/50 text-slate-400' };
                return (
                  <button
                    key={key}
                    onClick={() => { setStateFugSource(stateFugSource === key ? '' : key); setStateFugPage(0); }}
                    className={`px-3 py-1.5 text-xs font-mono tracking-wider rounded-sm border transition-all ${
                      stateFugSource === key ? cfg.active : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-600'
                    }`}
                    data-testid={`state-filter-${key}`}
                  >
                    {cfg.label} ({val.count})
                  </button>
                );
              })}
            </div>

            {/* Search */}
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                placeholder="Search by name..."
                value={stateFugSearch}
                onChange={(e) => setStateFugSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-sm text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-green-500 transition-colors font-mono"
                data-testid="state-search-input"
              />
            </div>

            {/* Grid */}
            {stateFugLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="card-tactical animate-pulse">
                    <div className="flex gap-3 p-4">
                      <div className="w-16 h-16 bg-slate-800 rounded flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 bg-slate-800 rounded w-3/4" />
                        <div className="h-2 bg-slate-800 rounded w-1/2" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : stateFugitives.length === 0 ? (
              <div className="card-tactical p-8 text-center">
                <Shield className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No state fugitives found. Try adjusting your search.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {stateFugitives.map((f, i) => {
                  const srcCfgMap = {
                    fbi_top_ten: { label: 'FBI', color: 'bg-red-600' },
                    texas_dps: { label: 'TX DPS', color: 'bg-amber-600' },
                    us_marshals: { label: 'USMS', color: 'bg-blue-600' },
                    lapd: { label: 'LAPD', color: 'bg-sky-700' },
                    ny_doccs: { label: 'NY', color: 'bg-orange-600' },
                  };
                  const src = srcCfgMap[f.source] || { label: f.source, color: 'bg-slate-600' };
                  const hasPhoto = f.photo_url && f.photo_url !== 'N/A';
                  return (
                    <div key={`${f.source}-${f.name}-${i}`} className="card-tactical hover:border-green-500/30 transition-colors group" data-testid={`state-fug-card-${i}`}>
                      <div className="flex gap-3 p-3">
                        {/* Photo */}
                        <div className="w-14 h-14 rounded overflow-hidden bg-slate-800 flex-shrink-0">
                          {hasPhoto ? (
                            <img src={f.photo_url} alt={f.name} className="w-full h-full object-cover" loading="lazy"
                              onError={(e) => { e.target.style.display='none'; }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-600">
                              <Users className="w-6 h-6" />
                            </div>
                          )}
                        </div>
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="text-white font-semibold text-sm truncate group-hover:text-green-400 transition-colors">{f.name}</h4>
                            <span className={`${src.color} text-white text-[9px] font-bold px-1.5 py-0.5 rounded-sm tracking-wider flex-shrink-0`}>{src.label}</span>
                          </div>
                          {f.state && (
                            <p className="text-slate-500 text-xs flex items-center gap-1 mt-0.5">
                              <MapPin className="w-3 h-3" />{f.last_known_location || f.state}
                            </p>
                          )}
                          {f.reward_amount && (
                            <p className="text-amber-400 text-xs font-mono font-bold mt-0.5 truncate">
                              {typeof f.reward_amount === 'string' ? f.reward_amount.substring(0, 60) : `$${f.reward_amount.toLocaleString()}`}
                            </p>
                          )}
                          <div className="flex gap-3 mt-1.5">
                            {f.details_url && (
                              <a href={f.details_url} target="_blank" rel="noopener noreferrer"
                                className="text-[10px] text-slate-500 hover:text-cyan-400 font-mono tracking-wider flex items-center gap-0.5"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink className="w-3 h-3" />SOURCE
                              </a>
                            )}
                            <button
                              onClick={() => navigate(user ? '/tips' : '/login')}
                              className="text-[10px] text-red-400 hover:text-red-300 font-mono tracking-wider flex items-center gap-0.5"
                              data-testid={`state-tip-btn-${i}`}
                            >
                              <Send className="w-3 h-3" />TIP
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {stateFugTotal > 18 && (
              <div className="flex items-center justify-center gap-4 pt-2">
                <button
                  onClick={() => setStateFugPage(Math.max(0, stateFugPage - 1))}
                  disabled={stateFugPage === 0}
                  className="px-4 py-2 border border-slate-700 rounded-sm text-xs font-mono text-slate-400 hover:border-green-500 hover:text-green-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                  data-testid="state-prev-page"
                >
                  <ChevronLeft className="w-3 h-3" /> PREV
                </button>
                <span className="text-xs font-mono text-slate-500">
                  PAGE {stateFugPage + 1} OF {Math.ceil(stateFugTotal / 18)}
                </span>
                <button
                  onClick={() => setStateFugPage(stateFugPage + 1)}
                  disabled={stateFugPage >= Math.ceil(stateFugTotal / 18) - 1}
                  className="px-4 py-2 border border-slate-700 rounded-sm text-xs font-mono text-slate-400 hover:border-green-500 hover:text-green-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                  data-testid="state-next-page"
                >
                  NEXT <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* MISSING PERSONS Tab */}
        {viewMode === 'missing' && (
          <div className="space-y-5" data-testid="missing-persons-section">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h2 className="font-heading font-bold text-xl text-white uppercase tracking-wider flex items-center gap-2">
                  <Users className="w-5 h-5 text-pink-400" />
                  Missing Persons
                </h2>
                <p className="text-slate-400 text-sm mt-1">
                  NamUs database &mdash; {missingTotal.toLocaleString()} missing persons with photos
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="px-3 py-1.5 text-xs font-mono tracking-wider rounded-sm bg-pink-500/20 border border-pink-500/50 text-pink-400">
                  NamUs
                </span>
              </div>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="text"
                placeholder="Search missing persons by name..."
                value={missingSearch}
                onChange={(e) => setMissingSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-sm text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-pink-500 transition-colors font-mono"
                data-testid="missing-search-input"
              />
            </div>

            {/* Grid */}
            {missingLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="card-tactical animate-pulse">
                    <div className="flex gap-3 p-4">
                      <div className="w-16 h-16 bg-slate-800 rounded flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 bg-slate-800 rounded w-3/4" />
                        <div className="h-2 bg-slate-800 rounded w-1/2" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : missingPersons.length === 0 ? (
              <div className="card-tactical p-8 text-center">
                <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No missing persons found. Try adjusting your search.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {missingPersons.map((p, i) => (
                  <div key={`namus-${p.namus_id}-${i}`} className="card-tactical hover:border-pink-500/30 transition-colors group" data-testid={`missing-card-${i}`}>
                    <div className="flex gap-3 p-3">
                      {/* Photo */}
                      <div className="w-14 h-14 rounded overflow-hidden bg-slate-800 flex-shrink-0">
                        {p.photo_url ? (
                          <img src={`${process.env.REACT_APP_BACKEND_URL}${p.photo_url}`} alt={p.name} className="w-full h-full object-cover" loading="lazy"
                            onError={(e) => { e.target.style.display='none'; }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-600">
                            <Users className="w-6 h-6" />
                          </div>
                        )}
                      </div>
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="text-white font-semibold text-sm truncate group-hover:text-pink-400 transition-colors">{p.name}</h4>
                          <span className="bg-pink-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-sm tracking-wider flex-shrink-0">MISSING</span>
                        </div>
                        <p className="text-slate-500 text-xs mt-0.5">{p.namus_id}</p>
                        {(p.age_min || p.age_max) && (
                          <p className="text-slate-400 text-xs mt-0.5">
                            Age: {p.age_min === p.age_max ? p.age_min : `${p.age_min}-${p.age_max}`}
                            {p.gender ? ` | ${p.gender}` : ''}
                          </p>
                        )}
                        {p.city_last_contact && (
                          <p className="text-slate-500 text-xs flex items-center gap-1 mt-0.5">
                            <MapPin className="w-3 h-3" />{p.city_last_contact}{p.state_last_contact ? `, ${p.state_last_contact}` : ''}
                          </p>
                        )}
                        <div className="flex gap-3 mt-1.5">
                          <a href={p.details_url} target="_blank" rel="noopener noreferrer"
                            className="text-[10px] text-slate-500 hover:text-pink-400 font-mono tracking-wider flex items-center gap-0.5"
                          >
                            <ExternalLink className="w-3 h-3" />NamUs
                          </a>
                          <button
                            onClick={() => navigate(user ? '/tips' : '/login')}
                            className="text-[10px] text-amber-400 hover:text-amber-300 font-mono tracking-wider flex items-center gap-0.5"
                            data-testid={`missing-tip-btn-${i}`}
                          >
                            <Send className="w-3 h-3" />HAVE INFO?
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {missingTotal > 18 && (
              <div className="flex items-center justify-center gap-4 pt-2">
                <button
                  onClick={() => setMissingPage(Math.max(0, missingPage - 1))}
                  disabled={missingPage === 0}
                  className="px-4 py-2 border border-slate-700 rounded-sm text-xs font-mono text-slate-400 hover:border-pink-500 hover:text-pink-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                  data-testid="missing-prev-page"
                >
                  <ChevronLeft className="w-3 h-3" /> PREV
                </button>
                <span className="text-xs font-mono text-slate-500">
                  PAGE {missingPage + 1} OF {Math.ceil(missingTotal / 18)}
                </span>
                <button
                  onClick={() => setMissingPage(missingPage + 1)}
                  disabled={missingPage >= Math.ceil(missingTotal / 18) - 1}
                  className="px-4 py-2 border border-slate-700 rounded-sm text-xs font-mono text-slate-400 hover:border-pink-500 hover:text-pink-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                  data-testid="missing-next-page"
                >
                  NEXT <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Standard Dashboard View (All Fugitives) */}
        {(viewMode === 'all' || !user) && (
          <>
        {/* Active Filter Indicator */}
        {activeFilter !== 'all' && (
          <div className="mb-6 flex items-center gap-4">
            <div className={`px-4 py-2 rounded-sm ${activeFilter === 'top10' ? 'bg-red-500/20 border border-red-500/30' : 'bg-green-500/20 border border-green-500/30'}`}>
              <span className={`text-sm font-semibold ${activeFilter === 'top10' ? 'text-red-400' : 'text-green-400'}`}>
                {activeFilter === 'top10' ? '🎯 Showing FBI Top 10 Most Wanted Only' : '💰 Showing $1M+ Rewards Only'}
              </span>
            </div>
            <button 
              onClick={() => setActiveFilter('all')}
              className="text-slate-400 hover:text-white text-sm flex items-center gap-1"
            >
              <XCircle className="w-4 h-4" /> Clear Filter
            </button>
          </div>
        )}

        {/* FBI Disclaimer Banner */}
        <div className="mb-6 card-tactical p-4 border-red-500/30 bg-red-500/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-red-400 font-semibold">FBI Official Tip Line</p>
              <p className="text-slate-400 text-sm">
                For urgent sightings, contact the FBI directly at <span className="text-cyan-400 font-bold">1-800-CALL-FBI (1-800-225-5324)</span> or submit tips at{' '}
                <a href="https://tips.fbi.gov" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">tips.fbi.gov</a>
              </p>
            </div>
          </div>
        </div>

        <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
          <h2 className="font-heading font-bold text-xl text-white uppercase tracking-wider">
            {activeFilter === 'top10' ? 'FBI Top 10 Most Wanted' : activeFilter === 'highReward' ? 'High Reward Fugitives ($1M+)' : 'FBI Most Wanted Fugitives'}
          </h2>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/search')}
              className="btn-secondary flex items-center gap-2 text-sm py-2 px-4"
              data-testid="advanced-search-btn"
            >
              <Search className="w-4 h-4" />
              Advanced Search
            </button>
            <span className="text-slate-500 text-sm font-mono">
              <Clock className="w-4 h-4 inline mr-1" />
              Updated: {new Date().toLocaleDateString()}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="card-tactical h-80 animate-pulse">
                <div className="h-48 bg-slate-800" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-slate-800 rounded w-3/4" />
                  <div className="h-3 bg-slate-800 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredFugitives.map((fugitive) => (
              <FugitiveCard 
                key={fugitive.fugitive_id} 
                fugitive={fugitive}
                onClick={() => navigate(`/fugitive/${fugitive.fugitive_id}`)}
                isTracked={trackedIds.includes(fugitive.fugitive_id)}
                onTrack={handleTrack}
              />
            ))}
          </div>
        )}
          </>
        )}
            </div>
            
            {/* News Sidebar - Only show on larger screens */}
            <div className="hidden xl:block w-80 flex-shrink-0">
              <NewsSidebar />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FugitiveMap = () => {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFugitive, setSelectedFugitive] = useState(null);
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [selectedStateMarker, setSelectedStateMarker] = useState(null);
  const [showPredictions, setShowPredictions] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showStateFugitives, setShowStateFugitives] = useState(true);
  const [stateMarkers, setStateMarkers] = useState([]);
  const [analyzingPrediction, setAnalyzingPrediction] = useState(false);
  const [detailedAnalysis, setDetailedAnalysis] = useState(null);
  const navigate = useNavigate();

  // Approximate center coordinates for each source
  const SOURCE_COORDS = {
    texas_dps: { lat: 31.9686, lng: -99.9018, label: 'Texas DPS' },
    lapd: { lat: 34.0522, lng: -118.2437, label: 'LAPD' },
    ny_doccs: { lat: 42.1657, lng: -74.9481, label: 'NY DOCCS' },
    fbi_top_ten: { lat: 38.8977, lng: -77.0365, label: 'FBI Top 10' },
    us_marshals: { lat: 38.9072, lng: -77.0369, label: 'US Marshals' },
  };

  useEffect(() => {
    fetchData();
    fetchStateMarkers();
  }, []);

  const fetchStateMarkers = async () => {
    try {
      const res = await api.get('/state-fugitives/sources');
      const sources = res.data.sources || {};
      const markers = Object.entries(sources).map(([key, val]) => ({
        source: key,
        count: val.count,
        ...(SOURCE_COORDS[key] || { lat: 39.8283, lng: -98.5795, label: key }),
      }));
      setStateMarkers(markers);
    } catch {
      console.error('Failed to fetch state markers');
    }
  };

  const fetchData = async () => {
    try {
      const [locRes, predRes, heatRes] = await Promise.all([
        api.get("/map/fugitives"),
        api.get("/ai/predictions/map"),
        api.get("/ai/predictions/heatmap")
      ]);
      setLocations(locRes.data);
      setPredictions(predRes.data.predictions || []);
      setHeatmapData(heatRes.data.heatmap || []);
    } catch {
      toast.error("Failed to load map data");
    } finally {
      setLoading(false);
    }
  };

  const analyzeSpecificFugitive = async (fugitiveId) => {
    if (!user) {
      toast.error("Please login to use AI analysis");
      return;
    }
    setAnalyzingPrediction(true);
    try {
      const res = await api.post(`/ai/predictions/analyze/${fugitiveId}`);
      setDetailedAnalysis(res.data);
      toast.success("AI analysis complete!");
    } catch {
      toast.error("Failed to generate analysis");
    } finally {
      setAnalyzingPrediction(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 px-4 pb-12" data-testid="map-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-cyan-400 font-mono text-sm tracking-[0.2em] mb-2">AI-POWERED INTELLIGENCE</p>
            <h1 className="font-heading font-bold text-3xl md:text-5xl tracking-tight uppercase text-white">
              Predictive Map
            </h1>
            <p className="text-slate-400 text-sm mt-2">
              AI analyzes patterns to predict likely fugitive locations
            </p>
          </div>
          
          {/* Map Controls */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowPredictions(!showPredictions)}
              className={`px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2 transition-colors ${
                showPredictions ? 'bg-purple-500 text-white' : 'bg-slate-800 text-slate-400'
              }`}
            >
              <Brain className="w-4 h-4" />
              AI Predictions
            </button>
            <button
              onClick={() => setShowHeatmap(!showHeatmap)}
              className={`px-4 py-4 rounded-sm text-sm font-semibold flex items-center gap-2 transition-colors ${
                showHeatmap ? 'bg-red-500 text-white' : 'bg-slate-800 text-slate-400'
              }`}
            >
              <Activity className="w-4 h-4" />
              Heat Zones
            </button>
            <button
              onClick={() => setShowStateFugitives(!showStateFugitives)}
              className={`px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2 transition-colors ${
                showStateFugitives ? 'bg-green-500 text-white' : 'bg-slate-800 text-slate-400'
              }`}
              data-testid="toggle-state-fugitives-map"
            >
              <MapPin className="w-4 h-4" />
              State/Local
            </button>
            <button
              onClick={fetchData}
              className="px-4 py-2 rounded-sm text-sm font-semibold bg-slate-800 text-slate-400 hover:bg-slate-700 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 card-tactical overflow-hidden" style={{ height: '600px' }}>
            {loading ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <Brain className="w-12 h-12 text-purple-500 mx-auto mb-4 animate-pulse" />
                  <p className="text-slate-400">AI analyzing patterns...</p>
                </div>
              </div>
            ) : (
              <ComposableMap projection="geoAlbersUsa" style={{ width: '100%', height: '100%' }}>
                <ZoomableGroup>
                  <Geographies geography={geoUrl}>
                    {({ geographies }) =>
                      geographies.map((geo) => (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill="#1e293b"
                          stroke="#334155"
                          strokeWidth={0.5}
                        />
                      ))
                    }
                  </Geographies>
                  
                  {/* Heatmap circles (larger, more transparent) */}
                  {showHeatmap && heatmapData.map((zone, idx) => (
                    <Marker key={`heat-${idx}`} coordinates={[zone.lng, zone.lat]}>
                      <circle
                        r={20 + (zone.intensity / 5)}
                        fill={zone.high_risk ? "rgba(239, 68, 68, 0.3)" : "rgba(234, 179, 8, 0.3)"}
                        stroke={zone.high_risk ? "#ef4444" : "#eab308"}
                        strokeWidth={1}
                        strokeOpacity={0.5}
                      />
                    </Marker>
                  ))}
                  
                  {/* Known locations (solid markers) */}
                  {locations.map((loc) => (
                    <Marker
                      key={loc.fugitive_id}
                      coordinates={[loc.longitude, loc.latitude]}
                      onClick={() => {
                        setSelectedFugitive(loc);
                        setSelectedPrediction(null);
                        setDetailedAnalysis(null);
                      }}
                    >
                      <circle
                        r={8}
                        fill={loc.danger_level?.includes('EXTREMELY') ? '#ef4444' : '#f59e0b'}
                        stroke="#fff"
                        strokeWidth={2}
                        className="cursor-pointer hover:r-10 transition-all"
                      />
                    </Marker>
                  ))}
                  
                  {/* AI Prediction markers (pulsing, different style) */}
                  {showPredictions && predictions.map((pred) => (
                    <Marker
                      key={pred.prediction_id}
                      coordinates={[pred.predicted_lng, pred.predicted_lat]}
                      onClick={() => {
                        setSelectedPrediction(pred);
                        setSelectedFugitive(null);
                        setSelectedStateMarker(null);
                        setDetailedAnalysis(null);
                      }}
                    >
                      <g>
                        <circle
                          r={12}
                          fill="rgba(168, 85, 247, 0.3)"
                          className="animate-ping"
                        />
                        <circle
                          r={6}
                          fill="#a855f7"
                          stroke="#e9d5ff"
                          strokeWidth={2}
                          className="cursor-pointer"
                        />
                        <text
                          textAnchor="middle"
                          y={-15}
                          style={{ fontSize: '8px', fill: '#a855f7', fontWeight: 'bold' }}
                        >
                          {pred.confidence}%
                        </text>
                      </g>
                    </Marker>
                  ))}

                  {/* State/Local Fugitive cluster markers */}
                  {showStateFugitives && stateMarkers.map((marker) => {
                    const colorMap = {
                      fbi_top_ten: { fill: '#dc2626', stroke: '#fca5a5' },
                      texas_dps: { fill: '#d97706', stroke: '#fcd34d' },
                      us_marshals: { fill: '#2563eb', stroke: '#93c5fd' },
                      lapd: { fill: '#0369a1', stroke: '#7dd3fc' },
                      ny_doccs: { fill: '#ea580c', stroke: '#fdba74' },
                    };
                    const c = colorMap[marker.source] || { fill: '#22c55e', stroke: '#86efac' };
                    return (
                      <Marker
                        key={`state-${marker.source}`}
                        coordinates={[marker.lng, marker.lat]}
                        onClick={() => {
                          setSelectedStateMarker(marker);
                          setSelectedFugitive(null);
                          setSelectedPrediction(null);
                          setDetailedAnalysis(null);
                        }}
                      >
                        <g className="cursor-pointer">
                          <circle r={18} fill={`${c.fill}40`} />
                          <circle r={12} fill={c.fill} stroke={c.stroke} strokeWidth={2} />
                          <text
                            textAnchor="middle"
                            y={4}
                            style={{ fontSize: '9px', fill: '#fff', fontWeight: 'bold' }}
                          >
                            {marker.count}
                          </text>
                          <text
                            textAnchor="middle"
                            y={-20}
                            style={{ fontSize: '7px', fill: c.stroke, fontWeight: 'bold' }}
                          >
                            {marker.label}
                          </text>
                        </g>
                      </Marker>
                    );
                  })}
                </ZoomableGroup>
              </ComposableMap>
            )}
          </div>

          <div className="space-y-4">
            {/* Selected Prediction Details */}
            {selectedPrediction && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-tactical p-4 border-purple-500/50"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Brain className="w-5 h-5 text-purple-400" />
                  <h3 className="font-heading font-bold text-white uppercase tracking-wider text-sm">
                    AI Prediction
                  </h3>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-white font-semibold">{selectedPrediction.fugitive_name}</p>
                    <p className="text-slate-500 text-sm">Predicted: {selectedPrediction.predicted_location}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400 text-sm">Confidence:</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${
                            selectedPrediction.confidence > 70 ? 'bg-green-500' :
                            selectedPrediction.confidence > 40 ? 'bg-amber-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${selectedPrediction.confidence}%` }}
                        />
                      </div>
                      <span className="text-white font-bold">{selectedPrediction.confidence}%</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${
                      selectedPrediction.risk_level === 'HIGH' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
                    }`}>
                      {selectedPrediction.risk_level} RISK
                    </span>
                    {selectedPrediction.reward_amount > 0 && (
                      <span className="text-amber-400 font-bold">
                        ${(selectedPrediction.reward_amount / 1000000).toFixed(1)}M
                      </span>
                    )}
                  </div>
                  <p className="text-slate-500 text-xs">{selectedPrediction.reasoning}</p>
                  
                  {user && (
                    <button
                      onClick={() => analyzeSpecificFugitive(selectedPrediction.fugitive_id)}
                      disabled={analyzingPrediction}
                      className="w-full btn-primary py-2 text-sm flex items-center justify-center gap-2"
                    >
                      {analyzingPrediction ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Brain className="w-4 h-4" />
                          Deep AI Analysis
                        </>
                      )}
                    </button>
                  )}
                </div>
              </motion.div>
            )}

            {/* Detailed AI Analysis */}
            {detailedAnalysis && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-tactical p-4 border-cyan-500/50 max-h-80 overflow-y-auto"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-5 h-5 text-cyan-400" />
                  <h3 className="font-heading font-bold text-white uppercase tracking-wider text-sm">
                    Deep Analysis
                  </h3>
                </div>
                <div className="prose prose-sm prose-invert">
                  <p className="text-slate-300 text-sm whitespace-pre-wrap">{detailedAnalysis.analysis}</p>
                </div>
              </motion.div>
            )}

            {/* Selected Fugitive Details */}
            {selectedFugitive && !selectedPrediction && !selectedStateMarker && (
              <div className="card-tactical p-4">
                <h3 className="font-heading font-bold text-white mb-4 uppercase tracking-wider">
                  Known Location
                </h3>
                <div className="space-y-3">
                  {selectedFugitive.image_url && (
                    <img 
                      src={selectedFugitive.image_url} 
                      alt={selectedFugitive.name}
                      className="w-full h-40 object-cover rounded-sm"
                    />
                  )}
                  <h4 className="font-heading font-bold text-lg text-white">{selectedFugitive.name}</h4>
                  <div className={`flex items-center gap-2 ${(selectedFugitive.reward_amount || 0) > 0 ? 'text-amber-400' : 'text-slate-500'}`}>
                    <DollarSign className="w-4 h-4" />
                    <span className="font-bold">
                      {(selectedFugitive.reward_amount || 0) === 0 
                        ? 'No Reward Listed'
                        : `$${(selectedFugitive.reward_amount || 0).toLocaleString()}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400 text-sm">
                    <MapPin className="w-4 h-4" />
                    <span>{selectedFugitive.location_name}</span>
                  </div>
                  {user && (
                    <button
                      onClick={() => navigate(`/fugitive/${selectedFugitive.fugitive_id}`)}
                      className="btn-primary w-full text-sm"
                    >
                      View Full Profile
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Selected State/Local Marker Details */}
            {selectedStateMarker && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="card-tactical p-4 border-green-500/50"
                data-testid="state-marker-info"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-5 h-5 text-green-400" />
                  <h3 className="font-heading font-bold text-white uppercase tracking-wider text-sm">
                    {selectedStateMarker.label}
                  </h3>
                </div>
                <div className="space-y-3">
                  <div className="bg-slate-800/50 rounded p-3 text-center">
                    <p className="text-3xl font-bold text-green-400">{selectedStateMarker.count}</p>
                    <p className="text-slate-500 text-xs">Active Fugitives</p>
                  </div>
                  <button
                    onClick={() => navigate('/state-fugitives')}
                    className="w-full btn-primary py-2 text-sm flex items-center justify-center gap-2"
                    data-testid="map-view-state-fugitives-btn"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Browse {selectedStateMarker.label} Fugitives
                  </button>
                </div>
              </motion.div>
            )}

            {/* Legend */}
            <div className="card-tactical p-4">
              <h3 className="font-heading font-bold text-white mb-3 uppercase tracking-wider text-sm">
                Map Legend
              </h3>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full bg-red-500" />
                  <span className="text-slate-300 text-sm">Extremely Dangerous</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full bg-amber-500" />
                  <span className="text-slate-300 text-sm">Last Known Location</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full bg-purple-500 animate-pulse" />
                  <span className="text-slate-300 text-sm">AI Predicted Location</span>
                </div>
                {showHeatmap && (
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded-full bg-yellow-500/30 border border-yellow-500" />
                    <span className="text-slate-300 text-sm">Activity Heat Zone</span>
                  </div>
                )}
                {showStateFugitives && (
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded-full bg-green-500 border border-green-300" />
                    <span className="text-slate-300 text-sm">State/Local Agency</span>
                  </div>
                )}
              </div>
            </div>

            {/* Prediction Stats */}
            <div className="card-tactical p-4">
              <h3 className="font-heading font-bold text-white mb-3 uppercase tracking-wider text-sm">
                AI Stats
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-2 bg-slate-800/50 rounded">
                  <p className="text-2xl font-bold text-purple-400">{predictions.length}</p>
                  <p className="text-slate-500 text-xs">Predictions</p>
                </div>
                <div className="text-center p-2 bg-slate-800/50 rounded">
                  <p className="text-2xl font-bold text-red-400">
                    {predictions.filter(p => p.risk_level === 'HIGH').length}
                  </p>
                  <p className="text-slate-500 text-xs">High Risk</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FugitiveDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [fugitive, setFugitive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isTracked, setIsTracked] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Require login to view fugitive profiles
    if (!user) {
      toast.error("Please login to view fugitive profiles");
      navigate("/login");
      return;
    }
    fetchFugitive();
  }, [id, user, navigate]);

  const fetchFugitive = async () => {
    if (!user) return;
    try {
      const response = await api.get(`/fugitives/${id}`);
      setFugitive(response.data);
      
      const caseRes = await api.get("/case-files");
      setIsTracked(caseRes.data.some(c => c.fugitive_id === id));
    } catch {
      toast.error("Fugitive not found");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleTrack = async () => {
    if (!user) {
      toast.error("Please login to track fugitives");
      navigate("/login");
      return;
    }

    try {
      if (isTracked) {
        const caseRes = await api.get("/case-files");
        const caseFile = caseRes.data.find(c => c.fugitive_id === id);
        if (caseFile) {
          await api.delete(`/case-files/${caseFile.case_id}`);
          setIsTracked(false);
          toast.success("Stopped tracking");
        }
      } else {
        await api.post("/case-files", { fugitive_id: id, alert_on_tips: true });
        setIsTracked(true);
        toast.success("Now tracking! You'll be notified of new tips.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!fugitive) return null;

  return (
    <div className="min-h-screen pt-20 px-4 pb-12" data-testid="fugitive-detail-page">
      <div className="max-w-5xl mx-auto">
        <button 
          onClick={() => navigate("/dashboard")}
          className="text-slate-400 hover:text-cyan-400 mb-6 flex items-center gap-2"
          data-testid="back-btn"
        >
          <ChevronRight className="w-4 h-4 rotate-180" />
          Back to Dashboard
        </button>

        {/* FBI Source Badge and Disclaimer */}
        {fugitive.fugitive_id?.startsWith('fbi_') && (
          <div className="mb-6 card-tactical p-4 border-red-500/30 bg-red-500/5">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="bg-red-600 text-white text-xs font-bold px-2 py-0.5 rounded-sm uppercase">FBI Data</span>
                  <span className="text-red-400 font-semibold text-sm">Official FBI Most Wanted</span>
                </div>
                <p className="text-slate-400 text-sm">
                  For urgent sightings, contact <span className="text-cyan-400 font-bold">1-800-CALL-FBI</span> or visit{' '}
                  <a href="https://tips.fbi.gov" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">tips.fbi.gov</a>
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-1">
            <div className="card-fugitive overflow-hidden">
              <div className="relative">
                <img 
                  src={fugitive.image_url || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400"}
                  alt={fugitive.name}
                  className="w-full aspect-square object-cover"
                  onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400"; }}
                />
                {fugitive.fugitive_id?.startsWith('fbi_') && (
                  <div className="absolute top-2 left-2">
                    <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-sm uppercase tracking-wider">
                      FBI
                    </span>
                  </div>
                )}
              </div>
              <div className="p-4 flex justify-between items-center">
                <span className="status-wanted">{fugitive.status}</span>
                <button
                  onClick={handleTrack}
                  className={`flex items-center gap-2 px-3 py-1 rounded-sm text-sm ${
                    isTracked ? 'bg-cyan-500/20 text-cyan-400' : 'bg-slate-800 text-slate-400 hover:text-cyan-400'
                  }`}
                  data-testid="track-fugitive-btn"
                >
                  {isTracked ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                  {isTracked ? 'Tracking' : 'Track'}
                </button>
              </div>
            </div>
          </div>

          <div className="md:col-span-2 space-y-6">
            <div>
              <p className="text-cyan-400 font-mono text-sm mb-1">FUGITIVE PROFILE</p>
              <h1 className="font-heading font-bold text-3xl md:text-4xl text-white">{fugitive.name}</h1>
              {fugitive.alias && (
                <p className="text-cyan-400 font-mono text-lg mt-1">"{fugitive.alias}"</p>
              )}
            </div>

            <div className="card-tactical p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-slate-500 uppercase text-sm tracking-wider">Reward</span>
                <span className="font-heading font-bold text-3xl text-amber-400">
                  ${fugitive.reward_amount?.toLocaleString()}
                </span>
              </div>
              <button 
                onClick={() => navigate(`/tips?fugitive=${fugitive.fugitive_id}`)}
                className="btn-primary w-full"
                data-testid="submit-tip-btn"
              >
                Submit Tip on This Fugitive
              </button>
            </div>

            <div className="card-tactical p-6 space-y-4">
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-sm ${
                fugitive.danger_level?.includes('EXTREMELY') ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
              }`}>
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm uppercase tracking-wider">{fugitive.danger_level}</span>
              </div>

              <div>
                <h3 className="text-slate-500 uppercase text-sm tracking-wider mb-2">Description</h3>
                <p className="text-slate-300">{fugitive.description}</p>
              </div>

              <div>
                <h3 className="text-slate-500 uppercase text-sm tracking-wider mb-2">Charges</h3>
                <div className="flex flex-wrap gap-2">
                  {fugitive.charges?.map((charge, i) => (
                    <span key={i} className="bg-slate-800 text-slate-300 px-3 py-1 text-sm rounded-sm">
                      {charge}
                    </span>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-slate-500 uppercase text-sm tracking-wider mb-2">Last Known Location</h3>
                  <p className="text-slate-300 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-cyan-400" />
                    {fugitive.last_known_location || "Unknown"}
                  </p>
                </div>
                <div>
                  <h3 className="text-slate-500 uppercase text-sm tracking-wider mb-2">Physical Description</h3>
                  <p className="text-slate-300 text-sm">{fugitive.physical_description || "See profile image"}</p>
                </div>
              </div>

              {fugitive.ai_summary && (
                <div className="border-t border-slate-800 pt-4 mt-4">
                  <h3 className="text-cyan-400 uppercase text-sm tracking-wider mb-2 flex items-center gap-2">
                    <Bot className="w-4 h-4" />
                    AI Intelligence Summary
                  </h3>
                  <p className="text-slate-300 text-sm">{fugitive.ai_summary}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TipSubmission = () => {
  const { user } = useAuth();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [fugitives, setFugitives] = useState([]);
  const [selectedFugitive, setSelectedFugitive] = useState("");
  const [content, setContent] = useState("");
  const [location, setLocation] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [imageData, setImageData] = useState(null);
  const [imageName, setImageName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [imageAnalysis, setImageAnalysis] = useState(null);
  const [searchParams] = useState(new URLSearchParams(window.location.search));
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const locationState = useLocation();
  const fromProfile = locationState.state?.fromProfile;

  useEffect(() => {
    fetchFugitives();
    const fugitiveId = searchParams.get("fugitive");
    if (fugitiveId) setSelectedFugitive(fugitiveId);
    // Check if terms were previously accepted
    const accepted = localStorage.getItem("wysspr_tip_terms_accepted");
    if (accepted) setTermsAccepted(true);
  }, [searchParams]);

  const fetchFugitives = async () => {
    try {
      const response = await api.get("/fugitives");
      setFugitives(response.data);
    } catch {
      toast.error("Failed to load fugitives");
    }
  };

  const handleAcceptTerms = () => {
    setTermsAccepted(true);
    localStorage.setItem("wysspr_tip_terms_accepted", "true");
    setShowTermsModal(false);
    toast.success("Terms accepted! You can now submit tips.");
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageData(reader.result);
        setImageName(file.name);
        setImageAnalysis(null); // Clear previous analysis
      };
      reader.readAsDataURL(file);
    }
  };

  // Analyze image with OpenAI Vision before or after submission
  const analyzeImage = async (tipId = null) => {
    if (!imageData && !fileInputRef.current?.files?.[0]) {
      toast.error("No image to analyze");
      return;
    }
    
    setAnalyzing(true);
    try {
      // Use FormData for file upload to Vision API
      const formData = new FormData();
      
      // If we have a file reference, use it directly
      if (fileInputRef.current?.files?.[0]) {
        formData.append('image', fileInputRef.current.files[0]);
      } else if (imageData) {
        // Convert base64 to blob
        const response = await fetch(imageData);
        const blob = await response.blob();
        formData.append('image', blob, imageName || 'image.jpg');
      }
      
      // Add context
      if (selectedFugitive) {
        formData.append('fugitive_id', selectedFugitive);
      }
      if (content) {
        formData.append('context', content);
      }
      
      const analysisResponse = await api.post("/ai/analyze-image-vision", formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setImageAnalysis(analysisResponse.data);
      toast.success("AI Vision analysis complete!");
      return analysisResponse.data;
    } catch (error) {
      // Fallback to old endpoint if vision fails
      try {
        const fallbackResponse = await api.post("/analyze-image", {
          image_data: imageData,
          tip_id: tipId
        });
        setImageAnalysis(fallbackResponse.data);
        toast.success("AI analysis complete!");
        return fallbackResponse.data;
      } catch {
        toast.error("Failed to analyze image");
        console.error("Image analysis error:", error);
        return null;
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if terms are accepted
    if (!termsAccepted) {
      setShowTermsModal(true);
      return;
    }
    
    if (!selectedFugitive || !content) {
      toast.error("Please select a fugitive and provide tip details");
      return;
    }

    setSubmitting(true);
    try {
      // Get reCAPTCHA token (v3 - invisible)
      let recaptchaToken = null;
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha('submit_tip');
      }
      
      const response = await api.post("/tips/with-image", {
        fugitive_id: selectedFugitive,
        content,
        location,
        is_anonymous: isAnonymous,
        image_data: imageData,
        image_name: imageName,
        recaptcha_token: recaptchaToken
      });
      
      toast.success("Tip submitted successfully!");
      
      // If there's an image, automatically run AI analysis
      if (imageData && response.data.tip_id) {
        toast.info("Running AI image analysis...", { duration: 2000 });
        const analysis = await analyzeImage(response.data.tip_id);
        if (analysis) {
          // Show analysis results in a more detailed toast
          toast.success(
            `AI Analysis Complete - Confidence: ${analysis.confidence}`,
            { duration: 6000 }
          );
        }
      }
      
      if (response.data.ai_analysis) {
        toast.info(`AI Analysis: Priority - ${response.data.priority}`, { duration: 5000 });
      }
      
      // Reset form
      setContent("");
      setLocation("");
      setImageData(null);
      setImageName("");
      setImageAnalysis(null);
      
    } catch (error) {
      toast.error("Failed to submit tip");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 px-4 pb-12" data-testid="tip-submission-page">
      {/* Terms of Service Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">
            <h2 className="font-heading font-bold text-2xl text-white mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-cyan-400" />
              Tip Submission Terms & Reward Eligibility
            </h2>
            
            <div className="space-y-4 text-slate-300 text-sm mb-6">
              <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded">
                <h3 className="text-amber-400 font-bold mb-2">⚠️ IMPORTANT: Reward Eligibility</h3>
                <p>To be eligible for any rewards associated with the capture of a fugitive, your tip <strong>MUST</strong> be submitted through Wysspr.com. Tips submitted directly to law enforcement agencies without going through Wysspr are <strong>NOT eligible</strong> for Wysspr community rewards.</p>
              </div>
              
              <div>
                <h3 className="text-white font-semibold mb-2">By submitting a tip, you agree to:</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-400">
                  <li>Provide truthful and accurate information to the best of your knowledge</li>
                  <li>Understand that false tips may result in legal consequences</li>
                  <li>Allow Wysspr to forward your tip to appropriate law enforcement agencies</li>
                  <li>Acknowledge that reward distribution is subject to verification of tip contribution to capture</li>
                  <li>Accept that Wysspr serves as a platform and does not guarantee rewards</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-white font-semibold mb-2">Reward Distribution Process:</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-400">
                  <li>Tips that contribute to a capture will be verified by our team</li>
                  <li>Rewards are distributed proportionally among verified contributing tipsters</li>
                  <li>You will be notified via your account if your tip contributed to a capture</li>
                  <li>Identity verification may be required for reward claim</li>
                </ul>
              </div>
              
              <div className="bg-green-500/10 border border-green-500/30 p-4 rounded">
                <h3 className="text-green-400 font-bold mb-2">🛡️ Your Safety First</h3>
                <p>Do NOT attempt to apprehend or approach any fugitive yourself. All tips are forwarded to professional law enforcement. Your safety is paramount.</p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <button
                onClick={() => setShowTermsModal(false)}
                className="flex-1 px-4 py-3 bg-slate-700 text-white rounded hover:bg-slate-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAcceptTerms}
                className="flex-1 px-4 py-3 bg-cyan-500 text-white rounded hover:bg-cyan-400 transition-colors font-semibold"
              >
                I Accept These Terms
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-3xl mx-auto">
        {/* Terms Acceptance Banner */}
        {!termsAccepted && (
          <div className="mb-6 bg-amber-500/10 border border-amber-500/30 p-4 rounded-lg">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0" />
              <div>
                <p className="text-amber-400 font-semibold">Review Terms Required</p>
                <p className="text-slate-400 text-sm">You must accept our tip submission terms to be eligible for rewards.</p>
              </div>
              <button
                onClick={() => setShowTermsModal(true)}
                className="ml-auto px-4 py-2 bg-amber-500 text-black rounded font-semibold text-sm hover:bg-amber-400"
              >
                Review Terms
              </button>
            </div>
          </div>
        )}

        {/* Back to Profile Button */}
        {fromProfile && (
          <button
            onClick={() => navigate('/profile')}
            className="mb-6 flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Profile
          </button>
        )}

        <div className="mb-8">
          <p className="text-cyan-400 font-mono text-sm tracking-[0.2em] mb-2">INTELLIGENCE SUBMISSION</p>
          <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tight uppercase text-white">
            Submit a Tip
          </h1>
          <p className="text-slate-400 mt-2">
            Your information is secure. Anonymous submissions are accepted.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="card-tactical p-6 space-y-6">
            <div>
              <label className="block text-slate-400 text-sm uppercase tracking-wider mb-2">
                Select Fugitive *
              </label>
              <select
                value={selectedFugitive}
                onChange={(e) => setSelectedFugitive(e.target.value)}
                className="w-full input-tactical p-3"
                data-testid="fugitive-select"
              >
                <option value="">-- Select a fugitive --</option>
                {fugitives.map((f) => (
                  <option key={f.fugitive_id} value={f.fugitive_id}>
                    {f.name} {f.alias ? `(${f.alias})` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-slate-400 text-sm uppercase tracking-wider mb-2">
                Tip Details *
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={6}
                placeholder="Describe what you observed - location, time, appearance, activities..."
                className="w-full input-tactical p-3 resize-none"
                data-testid="tip-content"
              />
            </div>

            <div>
              <label className="block text-slate-400 text-sm uppercase tracking-wider mb-2">
                Location (if applicable)
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="City, State or specific address"
                className="w-full input-tactical p-3"
                data-testid="tip-location"
              />
            </div>

            <div>
              <label className="block text-slate-400 text-sm uppercase tracking-wider mb-2">
                Evidence Image (optional)
              </label>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="btn-secondary flex items-center gap-2 text-sm"
                  data-testid="upload-image-btn"
                >
                  <Upload className="w-4 h-4" />
                  Upload Image
                </button>
                {imageData && (
                  <div className="flex items-center gap-2 text-green-400 text-sm">
                    <Image className="w-4 h-4" />
                    <span>{imageName}</span>
                    <button
                      type="button"
                      onClick={() => { setImageData(null); setImageName(""); }}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              {imageData && (
                <div className="mt-4 space-y-3">
                  <img src={imageData} alt="Preview" className="max-h-40 rounded-sm" />
                  
                  {/* AI Analysis Button */}
                  <button
                    type="button"
                    onClick={() => analyzeImage()}
                    disabled={analyzing}
                    className="btn-secondary flex items-center gap-2 text-sm"
                  >
                    {analyzing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Bot className="w-4 h-4" />
                        Analyze with AI
                      </>
                    )}
                  </button>
                  
                  {/* AI Analysis Results */}
                  {imageAnalysis && (
                    <div className="card-tactical p-4 border-cyan-500/30 bg-cyan-500/5">
                      <div className="flex items-center gap-2 mb-3">
                        <Bot className="w-5 h-5 text-cyan-400" />
                        <span className="text-cyan-400 font-semibold">AI Analysis Report</span>
                        <span className={`ml-auto text-xs px-2 py-1 rounded ${
                          imageAnalysis.confidence === 'HIGH' ? 'bg-green-500/20 text-green-400' :
                          imageAnalysis.confidence === 'MEDIUM' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {imageAnalysis.confidence} CONFIDENCE
                        </span>
                      </div>
                      
                      <p className="text-slate-300 text-sm mb-3">{imageAnalysis.analysis}</p>
                      
                      {imageAnalysis.key_observations && (
                        <div className="mb-3">
                          <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Key Observations</p>
                          <ul className="text-slate-300 text-sm space-y-1">
                            {imageAnalysis.key_observations.map((obs, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <CheckCircle className="w-3 h-3 text-green-400 mt-1 flex-shrink-0" />
                                {obs}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {imageAnalysis.recommended_actions && (
                        <div>
                          <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">Recommended Actions</p>
                          <ul className="text-slate-300 text-sm space-y-1">
                            {imageAnalysis.recommended_actions.map((action, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <ArrowRight className="w-3 h-3 text-cyan-400 mt-1 flex-shrink-0" />
                                {action}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="anonymous"
                checked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
                className="w-5 h-5 bg-slate-950 border-slate-700 rounded"
                data-testid="anonymous-checkbox"
              />
              <label htmlFor="anonymous" className="text-slate-300">
                Submit anonymously
              </label>
            </div>
          </div>

          <div className="card-tactical p-4 border-amber-500/30 bg-amber-500/5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <p className="text-amber-400 font-semibold">Safety Warning</p>
                <p className="text-slate-400 text-sm">
                  Never attempt to apprehend or approach a fugitive. Report information only.
                </p>
              </div>
            </div>
          </div>

          {/* FBI Official Disclaimer */}
          <div className="card-tactical p-4 border-red-500/30 bg-red-500/5">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-red-400 mt-0.5" />
              <div>
                <p className="text-red-400 font-semibold">FBI Official Channels</p>
                <p className="text-slate-400 text-sm mb-2">
                  For urgent matters or if you have witnessed a fugitive, please also contact the FBI directly:
                </p>
                <ul className="text-slate-400 text-sm space-y-1">
                  <li>📞 <span className="text-cyan-400 font-bold">1-800-CALL-FBI (1-800-225-5324)</span></li>
                  <li>🌐 <a href="https://tips.fbi.gov" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">tips.fbi.gov</a></li>
                  <li>📍 <a href="https://www.fbi.gov/wanted" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">www.fbi.gov/wanted</a></li>
                </ul>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting || analyzing}
            className="btn-primary w-full flex items-center justify-center gap-2"
            data-testid="submit-tip-form-btn"
          >
            {submitting ? (
              <>
                <span className="animate-spin w-5 h-5 border-2 border-black border-t-transparent rounded-full" />
                {imageData ? 'Submitting & Analyzing...' : 'Submitting...'}
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                {imageData ? 'Submit Tip & Analyze Image' : 'Submit Tip'}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

// ==================== USER PROFILE PAGE ====================

const ProfilePage = () => {
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editNotificationEmail, setEditNotificationEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState({ online_count: 0, users: [] });
  const [isOnline, setIsOnline] = useState(true);
  const fileInputRef = useRef(null);
  const heartbeatRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchProfile();
    fetchOnlineUsers();
    
    // Send heartbeat every 30 seconds to maintain online status
    heartbeatRef.current = setInterval(() => {
      sendHeartbeat();
    }, 30000);
    
    // Initial heartbeat
    sendHeartbeat();
    
    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
    };
  }, [user, navigate]);

  const sendHeartbeat = async () => {
    try {
      const res = await api.post('/users/heartbeat');
      setIsOnline(res.data.is_online);
    } catch {
      setIsOnline(false);
    }
  };

  const fetchOnlineUsers = async () => {
    try {
      const res = await api.get('/users/online');
      setOnlineUsers(res.data);
    } catch {
      // Silent fail
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await api.get('/profile');
      setProfile(res.data);
      // Parse existing name into first/last if not already separated
      const nameParts = (res.data.name || '').split(' ');
      setEditFirstName(res.data.first_name || nameParts[0] || '');
      setEditLastName(res.data.last_name || nameParts.slice(1).join(' ') || '');
      setEditNotificationEmail(res.data.notification_email || res.data.email || '');
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    // Validate notification email
    if (editNotificationEmail) {
      const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailPattern.test(editNotificationEmail)) {
        toast.error('Please enter a valid email address');
        return;
      }
    }

    setSaving(true);
    try {
      await api.put('/profile', { 
        first_name: editFirstName,
        last_name: editLastName,
        notification_email: editNotificationEmail
      });
      const fullName = `${editFirstName} ${editLastName}`.trim();
      setProfile(prev => ({ 
        ...prev, 
        first_name: editFirstName,
        last_name: editLastName,
        name: fullName,
        notification_email: editNotificationEmail
      }));
      setUser(prev => ({ ...prev, name: fullName }));
      setEditing(false);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setUploadingPicture(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/profile/picture', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfile(prev => ({ ...prev, profile_picture: res.data.picture_url }));
      toast.success('Profile picture uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleDeletePicture = async () => {
    if (!window.confirm('Are you sure you want to delete your profile picture?')) return;

    try {
      await api.delete('/profile/picture');
      setProfile(prev => ({ ...prev, profile_picture: null }));
      toast.success('Profile picture deleted');
    } catch (error) {
      toast.error('Failed to delete picture');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const profilePicture = profile?.profile_picture || profile?.picture;

  return (
    <div className="min-h-screen pt-20 px-4 pb-12" data-testid="profile-page">
      <div className="max-w-4xl mx-auto">
        {/* Back to Dashboard Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="mb-6 flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        <div className="mb-8">
          <p className="text-cyan-400 font-mono text-sm tracking-[0.2em] mb-2">ACCOUNT</p>
          <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tight uppercase text-white">
            My Profile
          </h1>
        </div>

        {/* Privacy Disclaimer */}
        <div className="mb-6 card-tactical p-4 border-green-500/30 bg-green-500/5">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-green-400 font-semibold">Your Privacy is Protected</p>
              <p className="text-slate-400 text-sm">
                Your account details are only visible to you. To other users and on the leaderboard, 
                you appear completely anonymous (e.g., "Tipster #4782"). Your email, name, and personal 
                information are never shared publicly.
              </p>
            </div>
          </div>
        </div>

        {/* Profile Card */}
        <div className="card-tactical mb-8">
          <div className="p-6 border-b border-slate-800">
            <div className="flex items-start gap-6">
              {/* Profile Picture */}
              <div className="relative group">
                <div className="w-24 h-24 bg-cyan-500/20 rounded-full flex items-center justify-center overflow-hidden">
                  {profilePicture ? (
                    <img src={profilePicture} alt={profile?.name} className="w-24 h-24 rounded-full object-cover" />
                  ) : (
                    <User className="w-12 h-12 text-cyan-400" />
                  )}
                </div>
                {/* Upload/Delete overlay */}
                <div className="absolute inset-0 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPicture}
                    className="p-2 bg-cyan-500 rounded-full hover:bg-cyan-400 transition-colors"
                    title="Upload picture"
                  >
                    {uploadingPicture ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4 text-white" />
                    )}
                  </button>
                  {profilePicture && (
                    <button
                      onClick={handleDeletePicture}
                      className="p-2 bg-red-500 rounded-full hover:bg-red-400 transition-colors"
                      title="Delete picture"
                    >
                      <Trash2 className="w-4 h-4 text-white" />
                    </button>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handlePictureUpload}
                  className="hidden"
                />
              </div>

              {/* Profile Info */}
              <div className="flex-1">
                {editing ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-slate-400 text-xs uppercase tracking-wider mb-1">First Name</label>
                        <input
                          type="text"
                          value={editFirstName}
                          onChange={(e) => setEditFirstName(e.target.value)}
                          className="w-full input-tactical px-3 py-2"
                          placeholder="First name"
                        />
                      </div>
                      <div>
                        <label className="block text-slate-400 text-xs uppercase tracking-wider mb-1">Last Name</label>
                        <input
                          type="text"
                          value={editLastName}
                          onChange={(e) => setEditLastName(e.target.value)}
                          className="w-full input-tactical px-3 py-2"
                          placeholder="Last name"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-slate-400 text-xs uppercase tracking-wider mb-1">
                        Notification Email
                        <span className="text-slate-500 text-xs ml-2 normal-case">(for tip status updates)</span>
                      </label>
                      <input
                        type="email"
                        value={editNotificationEmail}
                        onChange={(e) => setEditNotificationEmail(e.target.value)}
                        className="w-full input-tactical px-3 py-2"
                        placeholder="email@example.com"
                      />
                    </div>
                    <div className="flex items-center gap-3 pt-2">
                      <button 
                        onClick={handleUpdateProfile} 
                        disabled={saving}
                        className="btn-primary text-sm px-4 py-2 flex items-center gap-2"
                      >
                        {saving ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Saving...
                          </>
                        ) : 'Save Changes'}
                      </button>
                      <button 
                        onClick={() => {
                          setEditing(false);
                          // Reset to original values
                          const nameParts = (profile?.name || '').split(' ');
                          setEditFirstName(profile?.first_name || nameParts[0] || '');
                          setEditLastName(profile?.last_name || nameParts.slice(1).join(' ') || '');
                          setEditNotificationEmail(profile?.notification_email || profile?.email || '');
                        }} 
                        className="btn-secondary text-sm px-4 py-2"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3 mb-1">
                      <h2 className="font-heading font-bold text-2xl text-white">{profile?.name || 'Anonymous User'}</h2>
                      {/* Online Status Heartbeat */}
                      <div className="flex items-center gap-1.5">
                        <span className={`relative flex h-3 w-3 ${isOnline ? '' : 'opacity-50'}`}>
                          {isOnline && (
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                          )}
                          <span className={`relative inline-flex rounded-full h-3 w-3 ${isOnline ? 'bg-green-500' : 'bg-slate-500'}`}></span>
                        </span>
                        <span className={`text-xs ${isOnline ? 'text-green-400' : 'text-slate-500'}`}>
                          {isOnline ? 'Online' : 'Offline'}
                        </span>
                      </div>
                      <button onClick={() => setEditing(true)} className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center gap-1 ml-auto">
                        <span>Edit Profile</span>
                      </button>
                    </div>
                    <p className="text-slate-400">{profile?.email}</p>
                    {profile?.notification_email && profile.notification_email !== profile.email && (
                      <p className="text-slate-500 text-sm flex items-center gap-1">
                        <Bell className="w-3 h-3" />
                        Notifications: {profile.notification_email}
                      </p>
                    )}
                    <p className="text-slate-500 text-sm mt-1">
                      Member since {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Unknown'}
                    </p>
                    <p className="text-slate-600 text-xs mt-2">
                      Hover over your profile picture to upload or delete
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Online Hunters Section */}
          <div className="p-6 border-t border-slate-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <Users className="w-5 h-5 text-cyan-400" />
                Active Hunters
              </h3>
              <button 
                onClick={fetchOnlineUsers}
                className="text-slate-400 hover:text-cyan-400 text-sm flex items-center gap-1"
              >
                <RefreshCw className="w-3 h-3" />
                Refresh
              </button>
            </div>
            <p className="text-slate-500 text-sm mb-4">
              See who's actively hunting fugitives right now. All identities are anonymous for safety.
            </p>
            
            <div className="flex items-center gap-4 mb-4">
              <div className="bg-green-500/10 border border-green-500/30 px-4 py-2 rounded-sm">
                <span className="text-green-400 font-bold text-2xl">{onlineUsers.online_count}</span>
                <span className="text-slate-400 text-sm ml-2">hunters online</span>
              </div>
            </div>
            
            {onlineUsers.users.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {onlineUsers.users.slice(0, 20).map((hunter, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center gap-2 p-2 rounded-sm ${
                      hunter.is_you 
                        ? 'bg-cyan-500/10 border border-cyan-500/30' 
                        : 'bg-slate-800/50'
                    }`}
                  >
                    <span className="relative flex h-2 w-2">
                      {hunter.is_online && (
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      )}
                      <span className={`relative inline-flex rounded-full h-2 w-2 ${
                        hunter.is_online ? 'bg-green-500' : 'bg-slate-500'
                      }`}></span>
                    </span>
                    <span className={`text-xs ${hunter.is_you ? 'text-cyan-400 font-semibold' : 'text-slate-400'}`}>
                      {hunter.is_you ? 'You' : hunter.anonymous_id}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="p-6 border-t border-slate-800">
            <h3 className="font-heading font-bold text-lg text-white mb-4 uppercase tracking-wider">Your Activity</h3>
            <p className="text-slate-500 text-sm mb-4">Your activity updates in real-time as you submit tips, get verified, and contribute to captures.</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-slate-800/50 p-4 rounded-sm text-center">
                <p className="text-3xl font-bold text-cyan-400">{profile?.stats?.tips_submitted || 0}</p>
                <p className="text-slate-500 text-sm">Tips Submitted</p>
              </div>
              <div className="bg-slate-800/50 p-4 rounded-sm text-center">
                <p className="text-3xl font-bold text-green-400">{profile?.stats?.verified_tips || 0}</p>
                <p className="text-slate-500 text-sm">Verified Tips</p>
              </div>
              <div className="bg-slate-800/50 p-4 rounded-sm text-center">
                <p className="text-3xl font-bold text-amber-400">{profile?.stats?.tips_led_to_capture || 0}</p>
                <p className="text-slate-500 text-sm">Led to Capture</p>
              </div>
              <div className="bg-slate-800/50 p-4 rounded-sm text-center">
                <p className="text-3xl font-bold text-cyan-400">{profile?.stats?.case_files_tracking || 0}</p>
                <p className="text-slate-500 text-sm">Tracking</p>
              </div>
              <div className="bg-slate-800/50 p-4 rounded-sm text-center">
                <p className="text-3xl font-bold text-amber-400">${profile?.stats?.total_bounty_contributed || 0}</p>
                <p className="text-slate-500 text-sm">Contributed</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="card-tactical p-6">
          <h3 className="font-heading font-bold text-lg text-white mb-4 uppercase tracking-wider">Quick Actions</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/tips', { state: { fromProfile: true } })}
              className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-sm hover:bg-cyan-500/20 transition-colors text-left"
            >
              <Send className="w-6 h-6 text-cyan-400 mb-2" />
              <p className="text-white font-semibold">Submit a Tip</p>
              <p className="text-slate-400 text-sm">Report fugitive sightings</p>
            </button>
            <button
              onClick={() => navigate('/case-files', { state: { fromProfile: true } })}
              className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm hover:bg-amber-500/20 transition-colors text-left"
            >
              <FileText className="w-6 h-6 text-amber-400 mb-2" />
              <p className="text-white font-semibold">My Case Files</p>
              <p className="text-slate-400 text-sm">Fugitives you're tracking</p>
            </button>
            <button
              onClick={() => navigate('/rewards', { state: { fromProfile: true } })}
              className="p-4 bg-green-500/10 border border-green-500/30 rounded-sm hover:bg-green-500/20 transition-colors text-left"
            >
              <Trophy className="w-6 h-6 text-green-400 mb-2" />
              <p className="text-white font-semibold">My Rewards</p>
              <p className="text-slate-400 text-sm">Track your earnings</p>
            </button>
          </div>
        </div>

        {/* Reward Info */}
        <div className="card-tactical p-6 mt-8">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-amber-500/20 rounded-sm">
              <DollarSign className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg text-white mb-2">50/50 Reward Split</h3>
              <p className="text-slate-400 text-sm">
                When your tip leads to a capture, you receive <span className="text-amber-400 font-bold">50%</span> of the FBI reward. 
                Visit the <button onClick={() => navigate('/rewards')} className="text-cyan-400 hover:underline">Rewards page</button> to learn more and see the leaderboard.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CaseFilesPage = () => {
  const { user } = useAuth();
  const [caseFiles, setCaseFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const locationState = useLocation();
  const fromProfile = locationState.state?.fromProfile;

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    fetchCaseFiles();
  }, [user, navigate]);

  const fetchCaseFiles = async () => {
    try {
      const response = await api.get("/case-files");
      setCaseFiles(response.data);
    } catch {
      toast.error("Failed to load case files");
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (caseId) => {
    try {
      await api.delete(`/case-files/${caseId}`);
      setCaseFiles(prev => prev.filter(c => c.case_id !== caseId));
      toast.success("Case file archived");
    } catch {
      toast.error("Failed to archive case file");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 pb-12" data-testid="case-files-page">
      <div className="max-w-6xl mx-auto">
        {/* Back to Profile Button */}
        {fromProfile && (
          <button
            onClick={() => navigate('/profile')}
            className="mb-6 flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Profile
          </button>
        )}

        <div className="mb-8">
          <p className="text-cyan-400 font-mono text-sm tracking-[0.2em] mb-2">YOUR INVESTIGATIONS</p>
          <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tight uppercase text-white">
            Case Files
          </h1>
          <p className="text-slate-400 mt-2">
            Track fugitives and receive alerts when new tips are submitted.
          </p>
        </div>

        {caseFiles.length === 0 ? (
          <div className="card-tactical p-12 text-center">
            <FolderOpen className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Case Files Yet</h3>
            <p className="text-slate-400 mb-6">
              Start tracking fugitives to receive alerts on new intelligence.
            </p>
            <button onClick={() => navigate("/dashboard")} className="btn-primary">
              Browse Fugitives
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {caseFiles.map((caseFile) => (
              <div key={caseFile.case_id} className="card-tactical overflow-hidden">
                <div 
                  className="cursor-pointer"
                  onClick={() => navigate(`/fugitive/${caseFile.fugitive_id}`)}
                >
                  <img 
                    src={caseFile.fugitive?.image_url || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400"}
                    alt={caseFile.fugitive?.name}
                    className="w-full h-40 object-cover"
                  />
                </div>
                <div className="p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-heading font-bold text-white">{caseFile.fugitive?.name}</h3>
                      {caseFile.fugitive?.alias && (
                        <p className="text-cyan-400 text-sm font-mono">"{caseFile.fugitive.alias}"</p>
                      )}
                    </div>
                    <span className="text-amber-400 font-bold text-sm">
                      ${caseFile.fugitive?.reward_amount?.toLocaleString()}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs">
                    {caseFile.alert_on_tips ? (
                      <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded-sm flex items-center gap-1">
                        <Bell className="w-3 h-3" /> Alerts On
                      </span>
                    ) : (
                      <span className="bg-slate-700 text-slate-400 px-2 py-1 rounded-sm">Alerts Off</span>
                    )}
                    <span className="text-slate-500">
                      Since {new Date(caseFile.created_at).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="flex gap-2 pt-2 border-t border-slate-800">
                    <button
                      onClick={() => navigate(`/tips?fugitive=${caseFile.fugitive_id}`)}
                      className="flex-1 btn-primary text-xs py-2"
                    >
                      Submit Tip
                    </button>
                    <button
                      onClick={() => handleArchive(caseFile.case_id)}
                      className="px-3 py-2 bg-slate-800 text-slate-400 hover:text-red-400 rounded-sm transition-colors"
                      data-testid={`archive-case-${caseFile.case_id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tips, setTips] = useState([]);
  const [users, setUsers] = useState([]);
  const [fugitives, setFugitives] = useState([]);
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [activeView, setActiveView] = useState(null); // 'users', 'fugitives', 'top10', 'tips', etc.
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    fetchData();
  }, [user, navigate]);

  useEffect(() => {
    if (activeView === 'tips' || activeView === 'pending' || activeView === 'under_review' || activeView === 'verified' || activeView === 'dismissed') {
      fetchTipsByStatus();
    }
  }, [statusFilter, activeView]);

  const fetchData = async () => {
    try {
      const statsRes = await api.get("/admin/stats");
      setStats(statsRes.data);
      setAccessDenied(false);
    } catch (error) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error("Failed to load admin data");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTipsByStatus = async () => {
    try {
      const tipsRes = await api.get(`/admin/tips?status=${statusFilter}&limit=100`);
      setTips(tipsRes.data);
    } catch (error) {
      toast.error("Failed to load tips");
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get("/admin/users");
      setUsers(res.data);
    } catch (error) {
      toast.error("Failed to load users");
    }
  };

  const viewUserProfile = async (userId) => {
    setLoadingProfile(true);
    try {
      const res = await api.get(`/profile/${userId}`);
      setSelectedUserProfile(res.data);
    } catch (error) {
      toast.error("Failed to load user profile");
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetchFugitives = async (filter = null) => {
    try {
      const res = await api.get("/fugitives?limit=500");
      let data = res.data;
      if (filter === 'top10') {
        data = data.filter(f => (f.charges || []).includes('Ten Most Wanted Fugitives'));
      }
      setFugitives(data);
    } catch (error) {
      toast.error("Failed to load fugitives");
    }
  };

  const handleStatClick = async (view) => {
    setActiveView(view);
    if (view === 'users') {
      await fetchUsers();
    } else if (view === 'fugitives') {
      await fetchFugitives();
    } else if (view === 'top10') {
      await fetchFugitives('top10');
    } else if (view === 'tips') {
      setStatusFilter('PENDING');
      await fetchTipsByStatus();
    } else if (view === 'pending') {
      setStatusFilter('PENDING');
      await fetchTipsByStatus();
    } else if (view === 'under_review') {
      setStatusFilter('UNDER_REVIEW');
      await fetchTipsByStatus();
    } else if (view === 'verified') {
      setStatusFilter('VERIFIED');
      await fetchTipsByStatus();
    } else if (view === 'dismissed') {
      setStatusFilter('DISMISSED');
      await fetchTipsByStatus();
    }
  };

  const updateTipStatus = async (tipId, newStatus) => {
    try {
      await api.put(`/admin/tips/${tipId}/status?status=${newStatus}`);
      toast.success(`Tip marked as ${newStatus}`);
      fetchTipsByStatus();
      fetchData();
    } catch {
      toast.error("Failed to update tip status");
    }
  };

  const deleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete user "${userEmail}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success(`User ${userEmail} deleted successfully`);
      fetchUsers();
      fetchData();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || "Cannot delete this user");
      } else {
        toast.error("Failed to delete user");
      }
    }
  };

  const ClickableStatCard = ({ icon: Icon, label, value, color = "cyan", viewName }) => (
    <div 
      onClick={() => handleStatClick(viewName)}
      className={`card-tactical p-4 cursor-pointer hover:ring-2 hover:ring-${color === 'amber' ? 'amber' : color === 'red' ? 'red' : color === 'green' ? 'green' : 'cyan'}-500/50 transition-all ${activeView === viewName ? `ring-2 ring-${color === 'amber' ? 'amber' : color === 'red' ? 'red' : color === 'green' ? 'green' : 'cyan'}-500` : ''}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-slate-500 text-xs uppercase tracking-wider mb-1">{label}</p>
          <p className={`font-heading font-bold text-2xl ${color === 'amber' ? 'text-amber-400' : color === 'red' ? 'text-red-400' : color === 'green' ? 'text-green-400' : 'text-cyan-400'}`}>
            {value}
          </p>
        </div>
        <div className={`p-2 rounded-sm ${color === 'amber' ? 'bg-amber-500/20' : color === 'red' ? 'bg-red-500/20' : color === 'green' ? 'bg-green-500/20' : 'bg-cyan-500/20'}`}>
          <Icon className={`w-5 h-5 ${color === 'amber' ? 'text-amber-400' : color === 'red' ? 'text-red-400' : color === 'green' ? 'text-green-400' : 'text-cyan-400'}`} />
        </div>
      </div>
      <p className="text-slate-600 text-xs mt-2">Click to view details</p>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen pt-20 px-4 pb-12" data-testid="admin-page">
        <div className="max-w-2xl mx-auto text-center">
          <div className="card-tactical p-8">
            <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="font-heading font-bold text-2xl text-white mb-2">Access Denied</h1>
            <p className="text-slate-400 mb-6">
              Super Admin access is required to view this dashboard. 
              Please contact the administrator if you believe this is an error.
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              className="btn-tactical"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 pb-12" data-testid="admin-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-sm uppercase tracking-wider">
              Super Admin
            </span>
            <p className="text-cyan-400 font-mono text-sm tracking-[0.2em]">ADMINISTRATIVE CONTROL</p>
          </div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tight uppercase text-white">
            Admin Dashboard
          </h1>
          <p className="text-slate-500 text-sm mt-1">Logged in as: {user?.email}</p>
        </div>

        {/* Clickable Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8">
            <ClickableStatCard icon={Users} label="Total Users" value={stats.total_users} viewName="users" />
            <ClickableStatCard icon={Target} label="Fugitives" value={stats.total_fugitives} viewName="fugitives" />
            <ClickableStatCard icon={AlertTriangle} label="Top 10 Wanted" value={stats.top_ten_wanted || 0} color="red" viewName="top10" />
            <ClickableStatCard icon={FileText} label="Total Tips" value={stats.total_tips} viewName="tips" />
            <ClickableStatCard icon={Clock} label="Pending" value={stats.pending_tips} color="amber" viewName="pending" />
            <ClickableStatCard icon={Eye} label="Under Review" value={stats.under_review_tips || 0} color="cyan" viewName="under_review" />
            <ClickableStatCard icon={CheckCircle} label="Verified" value={stats.verified_tips} color="green" viewName="verified" />
            <ClickableStatCard icon={XCircle} label="Dismissed" value={stats.dismissed_tips} color="red" viewName="dismissed" />
          </div>
        )}

        {/* Detail Views */}
        {activeView && (
          <div className="card-tactical mb-8">
            <div className="p-4 border-b border-slate-800 flex justify-between items-center">
              <h2 className="font-heading font-bold text-xl text-white uppercase tracking-wider">
                {activeView === 'users' && 'All Users'}
                {activeView === 'fugitives' && 'All Fugitives'}
                {activeView === 'top10' && 'FBI Top 10 Most Wanted'}
                {activeView === 'tips' && 'All Tips'}
                {activeView === 'pending' && 'Pending Tips'}
                {activeView === 'under_review' && 'Tips Under Review'}
                {activeView === 'verified' && 'Verified Tips'}
                {activeView === 'dismissed' && 'Dismissed Tips'}
              </h2>
              <button 
                onClick={() => setActiveView(null)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Users View */}
            {activeView === 'users' && (
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50 sticky top-0">
                    <tr>
                      <th className="text-left p-3 text-slate-400 text-sm font-medium">Name</th>
                      <th className="text-left p-3 text-slate-400 text-sm font-medium">Email</th>
                      <th className="text-left p-3 text-slate-400 text-sm font-medium">Tips</th>
                      <th className="text-left p-3 text-slate-400 text-sm font-medium">Joined</th>
                      <th className="text-left p-3 text-slate-400 text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {users.map((u) => (
                      <tr key={u.user_id} className="hover:bg-slate-800/30">
                        <td className="p-3 text-white">{u.name || 'N/A'}</td>
                        <td className="p-3 text-slate-400">{u.email}</td>
                        <td className="p-3 text-cyan-400">{u.tip_count || 0}</td>
                        <td className="p-3 text-slate-500 text-sm">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="p-3 flex gap-2">
                          <button
                            onClick={() => viewUserProfile(u.user_id)}
                            className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-sm text-xs hover:bg-cyan-500/30 flex items-center gap-1"
                            title="View profile"
                            data-testid={`view-profile-${u.user_id}`}
                          >
                            <Eye className="w-3 h-3" />
                            View
                          </button>
                          <button
                            onClick={() => deleteUser(u.user_id, u.email)}
                            className="px-3 py-1 bg-red-500/20 text-red-400 rounded-sm text-xs hover:bg-red-500/30 flex items-center gap-1"
                            title="Delete user"
                          >
                            <Trash2 className="w-3 h-3" />
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {users.length === 0 && (
                  <p className="p-8 text-center text-slate-500">No users found</p>
                )}
              </div>
            )}

            {/* Fugitives View */}
            {(activeView === 'fugitives' || activeView === 'top10') && (
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50 sticky top-0">
                    <tr>
                      <th className="text-left p-3 text-slate-400 text-sm font-medium">Name</th>
                      <th className="text-left p-3 text-slate-400 text-sm font-medium">Status</th>
                      <th className="text-left p-3 text-slate-400 text-sm font-medium">Reward</th>
                      <th className="text-left p-3 text-slate-400 text-sm font-medium">Location</th>
                      <th className="text-left p-3 text-slate-400 text-sm font-medium">Danger Level</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {fugitives.map((f) => (
                      <tr 
                        key={f.fugitive_id} 
                        className="hover:bg-slate-800/30 cursor-pointer"
                        onClick={() => navigate(`/fugitive/${f.fugitive_id}`)}
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <span className="text-white">{f.name}</span>
                            {(f.charges || []).includes('Ten Most Wanted Fugitives') && (
                              <span className="bg-red-600 text-white text-xs px-1.5 py-0.5 rounded-sm">TOP 10</span>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded-sm text-xs ${f.status === 'CAPTURED' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                            {f.status}
                          </span>
                        </td>
                        <td className="p-3 text-amber-400 font-bold">
                          {(f.reward_amount || 0) >= 1000000 
                            ? `$${((f.reward_amount || 0) / 1000000).toFixed(0)}M` 
                            : `$${(f.reward_amount || 0).toLocaleString()}`}
                        </td>
                        <td className="p-3 text-slate-400 text-sm">{f.last_known_location || 'Unknown'}</td>
                        <td className="p-3">
                          <span className={`text-xs ${f.danger_level?.includes('EXTREMELY') ? 'text-red-400' : 'text-amber-400'}`}>
                            {f.danger_level}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {fugitives.length === 0 && (
                  <p className="p-8 text-center text-slate-500">No fugitives found</p>
                )}
              </div>
            )}

            {/* Tips View */}
            {(activeView === 'tips' || activeView === 'pending' || activeView === 'under_review' || activeView === 'verified' || activeView === 'dismissed') && (
              <div>
                {/* Status Filter Tabs */}
                <div className="p-4 border-b border-slate-800 flex gap-2 flex-wrap">
                  {["PENDING", "UNDER_REVIEW", "VERIFIED", "DISMISSED"].map((status) => (
                    <button
                      key={status}
                      onClick={() => {
                        setStatusFilter(status);
                        handleStatClick(status.toLowerCase());
                      }}
                      className={`px-3 py-1 rounded-sm text-sm uppercase tracking-wider transition-colors ${
                        statusFilter === status 
                          ? 'bg-cyan-500 text-black' 
                          : 'bg-slate-800 text-slate-400 hover:text-white'
                      }`}
                    >
                      {status.replace("_", " ")}
                    </button>
                  ))}
                </div>
                <div className="max-h-96 overflow-y-auto divide-y divide-slate-800">
                  {tips.length === 0 ? (
                    <p className="p-8 text-center text-slate-500">No tips with status: {statusFilter}</p>
                  ) : (
                    tips.map((tip) => (
                      <div key={tip.tip_id} className="p-4 hover:bg-slate-800/30">
                        <div className="flex flex-wrap gap-4 items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-2 py-0.5 rounded-sm text-xs uppercase ${
                                tip.priority === 'CRITICAL' ? 'bg-red-500/20 text-red-400' :
                                tip.priority === 'HIGH' ? 'bg-amber-500/20 text-amber-400' :
                                'bg-slate-700 text-slate-400'
                              }`}>
                                {tip.priority}
                              </span>
                              <span className="text-slate-500 text-sm">
                                {tip.fugitive?.name || 'Unknown Fugitive'}
                              </span>
                              {tip.has_image && (
                                <span className="text-cyan-400 text-xs flex items-center gap-1">
                                  <Image className="w-3 h-3" /> Has Image
                                </span>
                              )}
                            </div>
                            <p className="text-white mb-2">{tip.content}</p>
                            {tip.location && (
                              <p className="text-slate-500 text-sm flex items-center gap-1">
                                <MapPin className="w-3 h-3" /> {tip.location}
                              </p>
                            )}
                            <p className="text-slate-600 text-xs mt-2">
                              {new Date(tip.created_at).toLocaleString()} • {tip.is_anonymous ? 'Anonymous' : 'Identified'}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {tip.status === 'PENDING' && (
                              <>
                                <button
                                  onClick={() => updateTipStatus(tip.tip_id, 'UNDER_REVIEW')}
                                  className="px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-sm text-sm hover:bg-cyan-500/30"
                                >
                                  Review
                                </button>
                                <button
                                  onClick={() => updateTipStatus(tip.tip_id, 'DISMISSED')}
                                  className="px-3 py-1 bg-red-500/20 text-red-400 rounded-sm text-sm hover:bg-red-500/30"
                                >
                                  Dismiss
                                </button>
                              </>
                            )}
                            {tip.status === 'UNDER_REVIEW' && (
                              <>
                                <button
                                  onClick={() => updateTipStatus(tip.tip_id, 'VERIFIED')}
                                  className="px-3 py-1 bg-green-500/20 text-green-400 rounded-sm text-sm hover:bg-green-500/30"
                                >
                                  Verify
                                </button>
                                <button
                                  onClick={() => updateTipStatus(tip.tip_id, 'DISMISSED')}
                                  className="px-3 py-1 bg-red-500/20 text-red-400 rounded-sm text-sm hover:bg-red-500/30"
                                >
                                  Dismiss
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Quick Actions */}
        {!activeView && (
          <div className="card-tactical p-6">
            <h2 className="font-heading font-bold text-xl text-white uppercase tracking-wider mb-4">
              Quick Actions
            </h2>
            <p className="text-slate-400 mb-4">
              Click on any stat card above to view detailed information.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <button
                onClick={() => handleStatClick('pending')}
                className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm hover:bg-amber-500/20 transition-colors text-left"
              >
                <Clock className="w-6 h-6 text-amber-400 mb-2" />
                <p className="text-white font-semibold">Review Pending Tips</p>
                <p className="text-slate-400 text-sm">{stats?.pending_tips || 0} tips awaiting review</p>
              </button>
              <button
                onClick={() => handleStatClick('users')}
                className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-sm hover:bg-cyan-500/20 transition-colors text-left"
              >
                <Users className="w-6 h-6 text-cyan-400 mb-2" />
                <p className="text-white font-semibold">Manage Users</p>
                <p className="text-slate-400 text-sm">{stats?.total_users || 0} registered users</p>
              </button>
              <button
                onClick={() => handleStatClick('top10')}
                className="p-4 bg-red-500/10 border border-red-500/30 rounded-sm hover:bg-red-500/20 transition-colors text-left"
              >
                <AlertTriangle className="w-6 h-6 text-red-400 mb-2" />
                <p className="text-white font-semibold">Top 10 Wanted</p>
                <p className="text-slate-400 text-sm">{stats?.top_ten_wanted || 0} high-priority fugitives</p>
              </button>
            </div>
          </div>
        )}

        {/* User Profile Modal */}
        <AnimatePresence>
          {selectedUserProfile && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedUserProfile(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-slate-900 border border-slate-700 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-slate-800 flex justify-between items-start">
                  <div className="flex items-center gap-4">
                    {selectedUserProfile.picture ? (
                      <img src={selectedUserProfile.picture} alt="" className="w-16 h-16 rounded-full object-cover" />
                    ) : (
                      <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center">
                        <User className="w-8 h-8 text-cyan-400" />
                      </div>
                    )}
                    <div>
                      <h2 className="text-xl font-bold text-white">{selectedUserProfile.name || 'Unknown'}</h2>
                      <p className="text-slate-400">{selectedUserProfile.email}</p>
                      <p className="text-slate-500 text-sm">ID: {selectedUserProfile.user_id}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedUserProfile(null)}
                    className="text-slate-400 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="p-6">
                  {/* User Stats */}
                  <h3 className="text-lg font-semibold text-white mb-4">Activity Stats</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-slate-800/50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-cyan-400">{selectedUserProfile.stats?.tips_submitted || 0}</p>
                      <p className="text-slate-400 text-sm">Tips Submitted</p>
                    </div>
                    <div className="bg-slate-800/50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-400">{selectedUserProfile.stats?.verified_tips || 0}</p>
                      <p className="text-slate-400 text-sm">Verified Tips</p>
                    </div>
                    <div className="bg-slate-800/50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-amber-400">{selectedUserProfile.stats?.tips_led_to_capture || 0}</p>
                      <p className="text-slate-400 text-sm">Led to Capture</p>
                    </div>
                    <div className="bg-slate-800/50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-400">{selectedUserProfile.stats?.case_files_tracking || 0}</p>
                      <p className="text-slate-400 text-sm">Tracking Cases</p>
                    </div>
                    <div className="bg-slate-800/50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-emerald-400">${(selectedUserProfile.stats?.total_bounty_contributed || 0).toLocaleString()}</p>
                      <p className="text-slate-400 text-sm">Bounty Contributed</p>
                    </div>
                  </div>

                  {/* Account Info */}
                  <h3 className="text-lg font-semibold text-white mb-4">Account Information</h3>
                  <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Joined</span>
                      <span className="text-white">{selectedUserProfile.created_at ? new Date(selectedUserProfile.created_at).toLocaleDateString() : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Region</span>
                      <span className="text-white">{selectedUserProfile.region || 'Not set'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Online Status</span>
                      <span className={selectedUserProfile.is_online ? 'text-green-400' : 'text-slate-500'}>
                        {selectedUserProfile.is_online ? 'Online' : 'Offline'}
                      </span>
                    </div>
                    {selectedUserProfile.last_seen && (
                      <div className="flex justify-between">
                        <span className="text-slate-400">Last Seen</span>
                        <span className="text-white">{new Date(selectedUserProfile.last_seen).toLocaleString()}</span>
                      </div>
                    )}
                    {selectedUserProfile.referral_code && (
                      <div className="flex justify-between">
                        <span className="text-slate-400">Referral Code</span>
                        <span className="text-cyan-400 font-mono">{selectedUserProfile.referral_code}</span>
                      </div>
                    )}
                  </div>

                  {/* Admin Info (if available) */}
                  {selectedUserProfile.admin_info && (
                    <>
                      <h3 className="text-lg font-semibold text-white mb-4 mt-6">Recent Login History</h3>
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        {selectedUserProfile.admin_info.recent_logins?.length > 0 ? (
                          <div className="space-y-2">
                            {selectedUserProfile.admin_info.recent_logins.map((login, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span className="text-slate-400">{new Date(login.timestamp).toLocaleString()}</span>
                                <span className="text-slate-500">{login.ip_address}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-500 text-sm">No recent login history available</p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loading Profile Overlay */}
        {loadingProfile && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
            <div className="bg-slate-800 p-6 rounded-lg flex items-center gap-3">
              <RefreshCw className="w-5 h-5 text-cyan-400 animate-spin" />
              <span className="text-white">Loading profile...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AIChat = () => {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hello, I'm the Wysspr AI assistant. How can I help you today? I can provide information about fugitives, guide you on submitting tips, or analyze potential sightings." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [fugitives, setFugitives] = useState([]);
  const [selectedFugitive, setSelectedFugitive] = useState("");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchFugitives();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchFugitives = async () => {
    try {
      const response = await api.get("/fugitives");
      setFugitives(response.data);
    } catch {
      console.error("Failed to load fugitives");
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await api.post("/chat", {
        message: userMessage,
        fugitive_id: selectedFugitive || null
      });

      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: response.data.response,
        tipSuggested: response.data.tip_suggested,
        priority: response.data.priority_assessment
      }]);
    } catch {
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I apologize, but I'm having trouble processing your request. Please try again." 
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 px-4 pb-12" data-testid="chat-page">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <p className="text-cyan-400 font-mono text-sm tracking-[0.2em] mb-2">AI INTELLIGENCE ASSISTANT</p>
          <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tight uppercase text-white">
            Wysspr AI
          </h1>
        </div>

        <div className="mb-4">
          <select
            value={selectedFugitive}
            onChange={(e) => setSelectedFugitive(e.target.value)}
            className="w-full input-tactical p-3 text-sm"
            data-testid="chat-fugitive-select"
          >
            <option value="">General Chat (No specific fugitive)</option>
            {fugitives.map((f) => (
              <option key={f.fugitive_id} value={f.fugitive_id}>
                Discuss: {f.name} {f.alias ? `(${f.alias})` : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="card-tactical h-[500px] flex flex-col">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] ${
                  msg.role === "user" 
                    ? "bg-cyan-500/20 border border-cyan-500/30" 
                    : "bg-slate-800 border border-slate-700"
                } p-4 rounded-sm`}>
                  {msg.role === "assistant" && (
                    <div className="flex items-center gap-2 mb-2 text-cyan-400 text-sm">
                      <Bot className="w-4 h-4" />
                      <span className="font-mono">WYSSPR AI</span>
                    </div>
                  )}
                  <p className="text-slate-200 whitespace-pre-wrap">{msg.content}</p>
                  {msg.tipSuggested && (
                    <div className="mt-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded-sm">
                      <p className="text-amber-400 text-sm">
                        <AlertTriangle className="w-4 h-4 inline mr-1" />
                        This sounds like actionable intelligence. Consider submitting a formal tip.
                        {msg.priority && <span className="ml-2 font-bold">Priority: {msg.priority}</span>}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-800 border border-slate-700 p-4 rounded-sm">
                  <div className="flex items-center gap-2">
                    <span className="animate-pulse w-2 h-2 bg-cyan-400 rounded-full" />
                    <span className="animate-pulse w-2 h-2 bg-cyan-400 rounded-full animation-delay-100" />
                    <span className="animate-pulse w-2 h-2 bg-cyan-400 rounded-full animation-delay-200" />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={sendMessage} className="border-t border-slate-800 p-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 input-tactical p-3"
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="btn-primary px-6"
                data-testid="chat-send-btn"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ==================== REWARDS & LEADERBOARD PAGE ====================

const RewardsPage = () => {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [myRewards, setMyRewards] = useState(null);
  const [captures, setCaptures] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const locationState = useLocation();
  const fromProfile = locationState.state?.fromProfile;

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [leaderboardRes, capturesRes] = await Promise.all([
        api.get("/leaderboard?limit=10"),
        api.get("/captures?limit=10")
      ]);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
      // captures endpoint returns array directly
      setCaptures(Array.isArray(capturesRes.data) ? capturesRes.data : []);

      if (user) {
        const rewardsRes = await api.get("/my-rewards");
        setMyRewards(rewardsRes.data);
      }
    } catch (error) {
      console.error("Failed to fetch rewards data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 pb-12" data-testid="rewards-page">
      <div className="max-w-7xl mx-auto">
        {/* Back to Profile Button */}
        {fromProfile && (
          <button
            onClick={() => navigate('/profile')}
            className="mb-6 flex items-center gap-2 text-slate-400 hover:text-cyan-400 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Profile
          </button>
        )}

        <div className="mb-8">
          <p className="text-cyan-400 font-mono text-sm tracking-[0.2em] mb-2">COMMUNITY IMPACT</p>
          <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tight uppercase text-white">
            Rewards & Leaderboard
          </h1>
        </div>

        {/* Reward Split Info */}
        <div className="card-tactical p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-amber-500/20 rounded-sm">
              <DollarSign className="w-8 h-8 text-amber-400" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-xl text-white mb-2">50/50 Reward Split</h2>
              <p className="text-slate-400 mb-4">
                When your tip leads to a fugitive's capture, you receive <span className="text-amber-400 font-bold">50%</span> of the FBI reward. 
                Wysspr receives 50% to maintain the platform and support future captures. 
                This partnership ensures we can continue helping bring fugitives to justice together.
              </p>
              <div className="grid grid-cols-2 gap-4 max-w-md">
                <div className="bg-slate-800/50 p-3 rounded-sm text-center">
                  <p className="text-amber-400 font-bold text-2xl">50%</p>
                  <p className="text-slate-500 text-sm">Your Share</p>
                </div>
                <div className="bg-slate-800/50 p-3 rounded-sm text-center">
                  <p className="text-cyan-400 font-bold text-2xl">50%</p>
                  <p className="text-slate-500 text-sm">Wysspr Platform</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* My Rewards Section */}
          {user && myRewards && (
            <div className="card-tactical">
              <div className="p-4 border-b border-slate-800">
                <h2 className="font-heading font-bold text-xl text-white uppercase tracking-wider">
                  Your Rewards
                </h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-slate-800/50 p-4 rounded-sm text-center">
                    <p className="text-3xl font-bold text-cyan-400">{myRewards.total_tips_submitted}</p>
                    <p className="text-slate-500 text-sm">Tips Submitted</p>
                  </div>
                  <div className="bg-slate-800/50 p-4 rounded-sm text-center">
                    <p className="text-3xl font-bold text-green-400">{myRewards.verified_tips}</p>
                    <p className="text-slate-500 text-sm">Verified Tips</p>
                  </div>
                  <div className="bg-slate-800/50 p-4 rounded-sm text-center">
                    <p className="text-3xl font-bold text-amber-400">{myRewards.tips_contributed_to_capture}</p>
                    <p className="text-slate-500 text-sm">Led to Capture</p>
                  </div>
                  <div className="bg-slate-800/50 p-4 rounded-sm text-center">
                    <p className="text-3xl font-bold text-amber-400">
                      ${myRewards.total_rewards_earned?.toLocaleString() || 0}
                    </p>
                    <p className="text-slate-500 text-sm">Rewards Earned</p>
                  </div>
                </div>

                {myRewards.potential_rewards > 0 && (
                  <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm">
                    <p className="text-amber-400 font-semibold">Potential Rewards</p>
                    <p className="text-2xl font-bold text-white">
                      ${myRewards.potential_rewards?.toLocaleString()}
                    </p>
                    <p className="text-slate-400 text-sm">From verified tips awaiting capture</p>
                  </div>
                )}

                {myRewards.captures?.length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-heading font-bold text-white mb-3">Your Successful Tips</h3>
                    <div className="space-y-2">
                      {myRewards.captures.map((cap, idx) => (
                        <div key={idx} className="flex justify-between items-center p-3 bg-green-500/10 border border-green-500/30 rounded-sm">
                          <div>
                            <p className="text-white font-semibold">{cap.fugitive_name}</p>
                            <p className="text-slate-400 text-xs">Captured: {new Date(cap.capture_date).toLocaleDateString()}</p>
                          </div>
                          <p className="text-green-400 font-bold">${cap.your_share?.toLocaleString()}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Leaderboard */}
          <div className="card-tactical">
            <div className="p-4 border-b border-slate-800">
              <h2 className="font-heading font-bold text-xl text-white uppercase tracking-wider flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-400" />
                Top Tipsters (Anonymized)
              </h2>
            </div>
            <div className="divide-y divide-slate-800">
              {leaderboard.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Trophy className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No successful captures yet.</p>
                  <p className="text-sm">Be the first to help capture a fugitive!</p>
                </div>
              ) : (
                leaderboard.map((entry) => (
                  <div key={entry.rank} className="p-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      entry.rank === 1 ? 'bg-amber-500 text-black' :
                      entry.rank === 2 ? 'bg-slate-400 text-black' :
                      entry.rank === 3 ? 'bg-amber-700 text-white' :
                      'bg-slate-700 text-white'
                    }`}>
                      {entry.rank}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-semibold">{entry.anonymous_id}</p>
                      <p className="text-slate-500 text-xs">Member since {entry.joined_date}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-amber-400 font-bold">{entry.successful_tips} captures</p>
                      <p className="text-slate-500 text-xs">${entry.total_rewards_earned?.toLocaleString()} earned</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Recent Captures */}
        <div className="mt-8">
          <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-wider mb-6 flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-400" />
            Recent Captures - Powered by Wysspr Community
          </h2>
          
          {captures.length === 0 ? (
            <div className="card-tactical p-12 text-center">
              <Target className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-heading font-bold text-white mb-2">No Captures Yet</h3>
              <p className="text-slate-400 mb-6">
                Help bring fugitives to justice by submitting tips. Your information could lead to a capture!
              </p>
              <button onClick={() => navigate('/tips')} className="btn-primary">
                Submit a Tip
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {captures.map((capture) => (
                <div key={capture.capture_id} className="card-tactical overflow-hidden">
                  <div className="relative">
                    <img 
                      src={capture.fugitive_image || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400"}
                      alt={capture.fugitive_name}
                      className="w-full h-40 object-cover grayscale"
                    />
                    <div className="absolute inset-0 bg-green-900/70 flex items-center justify-center">
                      <div className="text-center">
                        <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-2" />
                        <span className="bg-green-600 text-white font-bold px-3 py-1 rounded-sm uppercase text-sm">
                          CAPTURED
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading font-bold text-lg text-white">{capture.fugitive_name}</h3>
                    <p className="text-slate-400 text-sm mb-3">
                      Captured: {new Date(capture.capture_date).toLocaleDateString()}
                    </p>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-500">FBI Reward:</span>
                      <span className="text-amber-400 font-bold">${capture.fbi_reward_amount?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm mt-1">
                      <span className="text-slate-500">Community Contributors:</span>
                      <span className="text-cyan-400">{capture.contributing_user_count || 0} tipsters</span>
                    </div>
                    <div className="mt-3 p-2 bg-green-500/10 border border-green-500/30 rounded-sm">
                      <p className="text-green-400 text-xs text-center">
                        ✓ Captured through Wysspr & community effort
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== BOUNTY POOL PAGE ====================

const BountyPoolPage = () => {
  const { user } = useAuth();
  const [poolStats, setPoolStats] = useState(null);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchData();
    // Check for success redirect
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (sessionId && location.pathname.includes('/success')) {
      checkPaymentStatus(sessionId);
    }
  }, [location]);

  const fetchData = async () => {
    try {
      const [statsRes, packagesRes] = await Promise.all([
        api.get("/bounty/pool/stats"),
        api.get("/bounty/packages")
      ]);
      setPoolStats(statsRes.data);
      setPackages(packagesRes.data.packages);
    } catch {
      toast.error("Failed to load bounty data");
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const res = await api.get(`/bounty/checkout/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        toast.success("Thank you for your contribution to the Bounty Pool!");
        navigate('/bounty', { replace: true });
        fetchData();
      }
    } catch {
      console.error("Failed to check payment status");
    }
  };

  const handleContribute = async (packageId) => {
    if (!user) {
      toast.error("Please login to contribute");
      navigate("/login");
      return;
    }

    setProcessing(true);
    try {
      let amount = null;
      if (packageId === "custom") {
        if (!customAmount || parseFloat(customAmount) < 5) {
          toast.error("Minimum custom contribution is $5");
          setProcessing(false);
          return;
        }
        amount = parseFloat(customAmount);
      }

      const res = await api.post(`/bounty/checkout?package_id=${packageId}${amount ? `&custom_amount=${amount}` : ''}`);
      
      // Redirect to Stripe checkout
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 pb-12" data-testid="bounty-page">
      <div className="max-w-6xl mx-auto">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">
            COMMUNITY BOUNTY POOL
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Contribute to help capture fugitives. Your donations increase rewards and fund justice.
          </p>
        </motion.div>

        {/* Pool Stats */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
        >
          <motion.div 
            whileHover={{ scale: 1.03, y: -4 }}
            className="card-tactical p-6 text-center border-green-500/30 cursor-pointer hover:border-green-400 hover:shadow-lg hover:shadow-green-500/20 transition-all group"
          >
            <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-green-500/30 transition-colors">
              <DollarSign className="w-8 h-8 text-green-400" />
            </div>
            <p className="text-4xl font-bold text-green-400 mb-1">
              ${(poolStats?.total_amount || 0).toLocaleString()}
            </p>
            <p className="text-slate-400 text-sm font-medium">Total Pool Amount</p>
            <p className="text-green-400/60 text-xs mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
              Click to contribute →
            </p>
          </motion.div>
          <motion.div 
            whileHover={{ scale: 1.03, y: -4 }}
            className="card-tactical p-6 text-center border-cyan-500/30 cursor-pointer hover:border-cyan-400 hover:shadow-lg hover:shadow-cyan-500/20 transition-all group"
          >
            <div className="w-14 h-14 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-cyan-500/30 transition-colors">
              <Gift className="w-8 h-8 text-cyan-400" />
            </div>
            <p className="text-4xl font-bold text-cyan-400 mb-1">
              {poolStats?.total_contributions || 0}
            </p>
            <p className="text-slate-400 text-sm font-medium">Total Contributions</p>
            <p className="text-cyan-400/60 text-xs mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
              View top contributors →
            </p>
          </motion.div>
          <motion.div 
            whileHover={{ scale: 1.03, y: -4 }}
            className="card-tactical p-6 text-center border-amber-500/30 cursor-pointer hover:border-amber-400 hover:shadow-lg hover:shadow-amber-500/20 transition-all group"
          >
            <div className="w-14 h-14 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-amber-500/30 transition-colors">
              <Trophy className="w-8 h-8 text-amber-400" />
            </div>
            <p className="text-4xl font-bold text-amber-400 mb-1">
              {poolStats?.top_contributors?.length || 0}
            </p>
            <p className="text-slate-400 text-sm font-medium">Active Hunters</p>
            <p className="text-amber-400/60 text-xs mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
              Join the hunt →
            </p>
          </motion.div>
        </motion.div>

        {/* Quick Action CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.12 }}
          className="bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-cyan-500/10 border border-cyan-500/30 rounded-lg p-6 mb-12 text-center"
        >
          <h3 className="text-xl font-bold text-white mb-2">Ready to Help Capture Fugitives?</h3>
          <p className="text-slate-400 mb-4">Every contribution brings us closer to justice. Start with as little as $10.</p>
          <button 
            onClick={() => handleContribute(packages[0]?.id)}
            className="btn-primary px-8 py-3 text-lg font-semibold"
          >
            <Gift className="w-5 h-5 inline mr-2" />
            Contribute Now
          </button>
        </motion.div>

        {/* How It Works Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="card-tactical p-6 mb-12 border-slate-700"
        >
          <h2 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            HOW THE BOUNTY POOL WORKS
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <DollarSign className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-white font-semibold mb-2">1. Contribute</h3>
              <p className="text-slate-400 text-sm">
                Choose a contribution package or enter a custom amount. Your contribution adds to the community bounty pool.
              </p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Target className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="text-white font-semibold mb-2">2. Pool Grows</h3>
              <p className="text-slate-400 text-sm">
                All contributions are pooled together to increase rewards for fugitive captures. Higher rewards = more motivation.
              </p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Trophy className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-white font-semibold mb-2">3. Rewards Paid</h3>
              <p className="text-slate-400 text-sm">
                When a fugitive is captured through tips from our platform, rewards are distributed: <strong className="text-amber-400">50% to tipster</strong>, remainder supports operations.
              </p>
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-slate-800">
            <p className="text-slate-500 text-xs text-center">
              💡 Your contributions help bring dangerous criminals to justice. All funds are securely held and distributed upon verified captures.
            </p>
          </div>
        </motion.div>

        {/* Contribution Packages */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-12"
        >
          <h2 className="font-heading text-2xl font-bold text-white mb-6 text-center">
            CHOOSE YOUR CONTRIBUTION
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {packages.map((pkg, idx) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 * idx }}
                className={`card-tactical p-6 cursor-pointer transition-all hover:scale-105 ${
                  selectedPackage === pkg.id ? 'border-cyan-400 ring-2 ring-cyan-400/50' : ''
                } ${pkg.id === 'bounty_hunter' ? 'border-amber-500/50' : ''}`}
                onClick={() => setSelectedPackage(pkg.id)}
              >
                {pkg.id === 'bounty_hunter' && (
                  <div className="text-xs text-amber-400 font-bold mb-2 flex items-center gap-1">
                    <Zap className="w-3 h-3" /> POPULAR
                  </div>
                )}
                <h3 className="font-heading font-bold text-lg text-white mb-2">{pkg.name}</h3>
                <p className="text-3xl font-bold text-green-400 mb-2">${pkg.amount}</p>
                <p className="text-slate-500 text-sm mb-4">{pkg.description}</p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleContribute(pkg.id);
                  }}
                  disabled={processing}
                  className="w-full btn-primary py-2 text-sm flex items-center justify-center gap-2"
                >
                  {processing ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <DollarSign className="w-4 h-4" />
                      Contribute
                    </>
                  )}
                </button>
              </motion.div>
            ))}

            {/* Custom Amount */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className={`card-tactical p-6 ${
                selectedPackage === 'custom' ? 'border-cyan-400 ring-2 ring-cyan-400/50' : ''
              }`}
            >
              <h3 className="font-heading font-bold text-lg text-white mb-2">Custom Amount</h3>
              <p className="text-slate-500 text-sm mb-4">Choose your own contribution</p>
              <div className="relative mb-4">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-green-400">$</span>
                <input
                  type="number"
                  min="5"
                  max="10000"
                  placeholder="Min $5"
                  value={customAmount}
                  onChange={(e) => {
                    setCustomAmount(e.target.value);
                    setSelectedPackage('custom');
                  }}
                  className="w-full input-tactical pl-8 py-2"
                />
              </div>
              <button
                onClick={() => handleContribute('custom')}
                disabled={processing || !customAmount}
                className="w-full btn-secondary py-2 text-sm flex items-center justify-center gap-2"
              >
                {processing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <DollarSign className="w-4 h-4" />
                    Custom Contribution
                  </>
                )}
              </button>
            </motion.div>
          </div>
        </motion.div>

        {/* Top Contributors */}
        {poolStats?.top_contributors?.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card-tactical p-6"
          >
            <h2 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-400" />
              TOP CONTRIBUTORS
            </h2>
            <div className="space-y-3">
              {poolStats.top_contributors.map((contributor, idx) => (
                <div key={idx} className="flex items-center justify-between bg-slate-800/50 p-3 rounded-sm">
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      idx === 0 ? 'bg-amber-500 text-black' :
                      idx === 1 ? 'bg-slate-400 text-black' :
                      idx === 2 ? 'bg-orange-600 text-white' :
                      'bg-slate-700 text-white'
                    }`}>
                      {idx + 1}
                    </span>
                    <span className="text-white font-semibold">{contributor.name}</span>
                  </div>
                  <span className="text-green-400 font-bold">${contributor.amount.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Security Notice */}
        <div className="mt-8 text-center">
          <p className="text-slate-600 text-xs">
            🔒 Secure payments processed by Stripe. Your financial information is never stored on our servers.
          </p>
        </div>
      </div>
    </div>
  );
};

// ==================== COMMUNITY PAGE ====================

const CommunityPage = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState({ type: 'sighting', content: '', fugitive_id: '', location: '' });
  const [fugitives, setFugitives] = useState([]);
  const [showPostForm, setShowPostForm] = useState(false);
  const [filter, setFilter] = useState('all');
  const [expandedComments, setExpandedComments] = useState({});
  const [commentText, setCommentText] = useState({});
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [deletingPost, setDeletingPost] = useState(null);
  const navigate = useNavigate();

  // Check if user is super admin
  useEffect(() => {
    const checkSuperAdmin = async () => {
      if (user) {
        try {
          const res = await api.get("/admin/stats");
          setIsSuperAdmin(res.data.is_super_admin || false);
        } catch {
          setIsSuperAdmin(false);
        }
      }
    };
    checkSuperAdmin();
  }, [user]);

  useEffect(() => {
    fetchPosts();
    fetchFugitives();
  }, [filter]);

  const fetchPosts = async () => {
    try {
      const params = filter !== 'all' ? `?type=${filter}` : '';
      const res = await api.get(`/community/posts${params}`);
      setPosts(res.data.posts || []);
    } catch (error) {
      console.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchFugitives = async () => {
    try {
      const res = await api.get('/fugitives?limit=100');
      setFugitives(res.data);
    } catch (error) {
      console.error('Failed to load fugitives');
    }
  };

  const handleSubmitPost = async (e) => {
    e.preventDefault();
    if (!newPost.content.trim()) return;
    
    try {
      await api.post('/community/posts', newPost);
      toast.success('Post shared with the community!');
      setNewPost({ type: 'sighting', content: '', fugitive_id: '', location: '' });
      setShowPostForm(false);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    }
  };

  const handleUpvote = async (postId) => {
    if (!user) {
      toast.error('Please login to upvote');
      return;
    }
    try {
      await api.post(`/community/posts/${postId}/upvote`);
      fetchPosts();
    } catch (error) {
      toast.error('Failed to upvote');
    }
  };

  const handleComment = async (postId) => {
    if (!commentText[postId]?.trim()) return;
    
    try {
      await api.post(`/community/posts/${postId}/comment`, { content: commentText[postId] });
      setCommentText(prev => ({ ...prev, [postId]: '' }));
      fetchPosts();
      toast.success('Comment added!');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!isSuperAdmin) {
      toast.error('Super Admin access required');
      return;
    }
    
    try {
      setDeletingPost(postId);
      await api.delete(`/community/posts/${postId}`);
      toast.success('Post deleted successfully');
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    } finally {
      setDeletingPost(null);
    }
  };

  const toggleComments = (postId) => {
    setExpandedComments(prev => ({ ...prev, [postId]: !prev[postId] }));
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'sighting': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'tip': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
      case 'discussion': return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
      default: return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'sighting': return Eye;
      case 'tip': return Target;
      case 'discussion': return MessageCircle;
      default: return FileText;
    }
  };

  return (
    <div className="min-h-screen pt-20 px-4 pb-12">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-cyan-400 font-mono text-sm tracking-[0.2em] mb-2">INTEL NETWORK</p>
            <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tight uppercase text-white">
              Community
            </h1>
            <p className="text-slate-400 text-sm mt-2">Share anonymous intel and collaborate with fellow hunters</p>
          </div>
          
          {user && (
            <button
              onClick={() => setShowPostForm(true)}
              className="btn-primary flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              Share Intel
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { value: 'all', label: 'All Posts' },
            { value: 'sighting', label: 'Sightings' },
            { value: 'tip', label: 'Tips' },
            { value: 'discussion', label: 'Discussions' }
          ].map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-4 py-2 rounded-sm text-sm font-semibold whitespace-nowrap transition-all ${
                filter === f.value
                  ? 'bg-cyan-500 text-slate-900'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* New Post Form Modal */}
        {showPostForm && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="card-tactical max-w-lg w-full p-6"
            >
              <h3 className="font-heading font-bold text-xl text-white mb-4">Share Intel</h3>
              <form onSubmit={handleSubmitPost} className="space-y-4">
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Type</label>
                  <select
                    value={newPost.type}
                    onChange={(e) => setNewPost(p => ({ ...p, type: e.target.value }))}
                    className="w-full input-tactical p-3"
                  >
                    <option value="sighting">🔴 Sighting Report</option>
                    <option value="tip">🟡 General Tip</option>
                    <option value="discussion">🔵 Discussion</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Related Fugitive (Optional)</label>
                  <select
                    value={newPost.fugitive_id}
                    onChange={(e) => setNewPost(p => ({ ...p, fugitive_id: e.target.value }))}
                    className="w-full input-tactical p-3"
                  >
                    <option value="">None - General Post</option>
                    {fugitives.map(f => (
                      <option key={f.fugitive_id} value={f.fugitive_id}>{f.name}</option>
                    ))}
                  </select>
                </div>
                
                {newPost.type === 'sighting' && (
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Location</label>
                    <input
                      type="text"
                      value={newPost.location}
                      onChange={(e) => setNewPost(p => ({ ...p, location: e.target.value }))}
                      placeholder="City, State"
                      className="w-full input-tactical p-3"
                    />
                  </div>
                )}
                
                <div>
                  <label className="block text-slate-400 text-sm mb-2">Details</label>
                  <textarea
                    value={newPost.content}
                    onChange={(e) => setNewPost(p => ({ ...p, content: e.target.value }))}
                    placeholder="Share your intel..."
                    rows={4}
                    className="w-full input-tactical p-3 resize-none"
                    required
                  />
                </div>
                
                <div className="flex gap-3">
                  <button type="button" onClick={() => setShowPostForm(false)} className="flex-1 btn-secondary">
                    Cancel
                  </button>
                  <button type="submit" className="flex-1 btn-primary">
                    Post Anonymously
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}

        {/* Posts List */}
        {loading ? (
          <div className="space-y-4">
            {[1,2,3].map(i => (
              <div key={i} className="card-tactical p-4 animate-pulse">
                <div className="h-4 bg-slate-700 rounded w-1/4 mb-3" />
                <div className="h-16 bg-slate-700 rounded mb-3" />
                <div className="h-3 bg-slate-700 rounded w-1/3" />
              </div>
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="card-tactical p-8 text-center">
            <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-white mb-2">No Posts Yet</h3>
            <p className="text-slate-400 mb-4">Be the first to share intel with the community!</p>
            {user && (
              <button onClick={() => setShowPostForm(true)} className="btn-primary">
                Create First Post
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {posts.map(post => {
              const TypeIcon = getTypeIcon(post.type);
              return (
                <motion.div
                  key={post.post_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card-tactical p-4"
                >
                  {/* Post Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`px-2 py-1 rounded text-xs font-semibold border ${getTypeColor(post.type)}`}>
                        <TypeIcon className="w-3 h-3 inline mr-1" />
                        {(post.type || 'discussion').toUpperCase()}
                      </div>
                      <span className="text-slate-500 text-sm">{post.anonymous_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 text-xs">
                        {new Date(post.created_at).toLocaleDateString()}
                      </span>
                      {/* Super Admin Delete Button */}
                      {isSuperAdmin && (
                        <button
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this post?')) {
                              handleDeletePost(post.post_id);
                            }
                          }}
                          disabled={deletingPost === post.post_id}
                          className="p-1.5 rounded bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                          title="Delete Post (Admin)"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Fugitive Reference */}
                  {post.fugitive && (
                    <div 
                      className="flex items-center gap-2 mb-3 p-2 bg-slate-800/50 rounded cursor-pointer hover:bg-slate-800"
                      onClick={() => navigate(`/fugitive/${post.fugitive.fugitive_id}`)}
                    >
                      {post.fugitive.image_url && (
                        <img src={post.fugitive.image_url} alt="" className="w-10 h-10 rounded object-cover" />
                      )}
                      <div>
                        <p className="text-white text-sm font-semibold">{post.fugitive.name}</p>
                        <p className="text-amber-400 text-xs">${(post.fugitive.reward_amount || 0).toLocaleString()} Reward</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Location */}
                  {post.location && (
                    <div className="flex items-center gap-2 text-slate-400 text-sm mb-3">
                      <MapPin className="w-4 h-4" />
                      {post.location}
                    </div>
                  )}
                  
                  {/* Content */}
                  <p className="text-slate-300 mb-4">{post.content}</p>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-4 pt-3 border-t border-slate-700">
                    <button
                      onClick={() => handleUpvote(post.post_id)}
                      className={`flex items-center gap-2 text-sm transition-colors ${
                        post.user_upvoted ? 'text-cyan-400' : 'text-slate-400 hover:text-cyan-400'
                      }`}
                    >
                      <ThumbsUp className="w-4 h-4" />
                      <span>{post.upvotes || 0}</span>
                    </button>
                    
                    <button
                      onClick={() => toggleComments(post.post_id)}
                      className="flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 transition-colors"
                    >
                      <MessageCircle className="w-4 h-4" />
                      <span>{post.comments?.length || 0} Comments</span>
                      {expandedComments[post.post_id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                  </div>
                  
                  {/* Comments Section */}
                  {expandedComments[post.post_id] && (
                    <div className="mt-4 pt-4 border-t border-slate-700">
                      {/* Comment Input */}
                      {user && (
                        <div className="flex gap-2 mb-4">
                          <input
                            type="text"
                            value={commentText[post.post_id] || ''}
                            onChange={(e) => setCommentText(prev => ({ ...prev, [post.post_id]: e.target.value }))}
                            placeholder="Add a comment..."
                            className="flex-1 input-tactical p-2 text-sm"
                          />
                          <button
                            onClick={() => handleComment(post.post_id)}
                            className="btn-secondary px-3 py-2"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      
                      {/* Comments List */}
                      {post.comments?.length > 0 ? (
                        <div className="space-y-3">
                          {post.comments.map((comment, idx) => (
                            <div key={idx} className="bg-slate-800/50 rounded p-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-slate-400 text-xs">{comment.anonymous_name}</span>
                                <span className="text-slate-600 text-xs">{new Date(comment.created_at).toLocaleDateString()}</span>
                              </div>
                              <p className="text-slate-300 text-sm">{comment.content}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-500 text-sm text-center">No comments yet</p>
                      )}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== ADVANCED SEARCH PAGE ====================

const AdvancedSearchPage = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [filters, setFilters] = useState({
    minReward: "",
    maxReward: "",
    dangerLevel: "",
    status: "",
    location: ""
  });
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const navigate = useNavigate();

  const fetchSuggestions = async (q) => {
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const res = await api.get(`/fugitives/search/suggestions?q=${encodeURIComponent(q)}`);
      setSuggestions(res.data);
    } catch {
      setSuggestions([]);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    setSearched(true);
    setSuggestions([]);
    try {
      const res = await api.post("/fugitives/search", {
        query: query || null,
        min_reward: filters.minReward ? parseFloat(filters.minReward) : null,
        max_reward: filters.maxReward ? parseFloat(filters.maxReward) : null,
        danger_level: filters.dangerLevel || null,
        status: filters.status || null,
        location: filters.location || null,
        sort_by: "reward_amount",
        sort_order: "desc"
      });
      setResults(res.data.results);
    } catch {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      minReward: "",
      maxReward: "",
      dangerLevel: "",
      status: "",
      location: ""
    });
    setQuery("");
    setResults([]);
    setSearched(false);
  };

  return (
    <div className="min-h-screen pt-20 px-4 pb-12" data-testid="search-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <p className="text-cyan-400 font-mono text-sm tracking-[0.2em] mb-2">INTELLIGENCE SEARCH</p>
          <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tight uppercase text-white">
            Advanced Search
          </h1>
        </div>

        {/* Search Bar */}
        <div className="card-tactical p-6 mb-6">
          <div className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); fetchSuggestions(e.target.value); }}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by name, alias, charges, or description..."
              className="w-full input-tactical p-4 pr-12"
              data-testid="search-input"
            />
            <button
              onClick={handleSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-cyan-500 text-black rounded-sm hover:bg-cyan-400"
            >
              <Search className="w-5 h-5" />
            </button>

            {/* Suggestions Dropdown */}
            {suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-slate-900 border border-slate-800 rounded-sm z-10 max-h-64 overflow-y-auto">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      if (s.type === 'fugitive') {
                        navigate(`/fugitive/${s.fugitive_id}`);
                      } else {
                        setFilters(prev => ({ ...prev, location: s.text }));
                        setQuery("");
                        setSuggestions([]);
                      }
                    }}
                    className="w-full text-left p-3 hover:bg-slate-800 flex items-center gap-3"
                  >
                    {s.type === 'fugitive' ? (
                      <User className="w-4 h-4 text-cyan-400" />
                    ) : (
                      <MapPin className="w-4 h-4 text-amber-400" />
                    )}
                    <div>
                      <p className="text-white">{s.text}</p>
                      {s.subtext && <p className="text-cyan-400 text-xs">"{s.subtext}"</p>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="mt-4 flex items-center gap-2 text-slate-400 hover:text-cyan-400 text-sm"
          >
            <SlidersHorizontal className="w-4 h-4" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>

          {/* Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="grid md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-800">
                  <div>
                    <label className="block text-slate-500 text-xs uppercase mb-2">Min Reward</label>
                    <input
                      type="number"
                      value={filters.minReward}
                      onChange={(e) => setFilters(prev => ({ ...prev, minReward: e.target.value }))}
                      placeholder="$0"
                      className="w-full input-tactical p-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-500 text-xs uppercase mb-2">Max Reward</label>
                    <input
                      type="number"
                      value={filters.maxReward}
                      onChange={(e) => setFilters(prev => ({ ...prev, maxReward: e.target.value }))}
                      placeholder="$1,000,000"
                      className="w-full input-tactical p-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-500 text-xs uppercase mb-2">Danger Level</label>
                    <select
                      value={filters.dangerLevel}
                      onChange={(e) => setFilters(prev => ({ ...prev, dangerLevel: e.target.value }))}
                      className="w-full input-tactical p-2 text-sm"
                    >
                      <option value="">Any</option>
                      <option value="EXTREMELY">Extremely Dangerous</option>
                      <option value="ARMED">Armed and Dangerous</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-500 text-xs uppercase mb-2">Status</label>
                    <select
                      value={filters.status}
                      onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full input-tactical p-2 text-sm"
                    >
                      <option value="">Any</option>
                      <option value="WANTED">Wanted</option>
                      <option value="CAPTURED">Captured</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-500 text-xs uppercase mb-2">Location</label>
                    <input
                      type="text"
                      value={filters.location}
                      onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="City, State..."
                      className="w-full input-tactical p-2 text-sm"
                    />
                  </div>
                  <div className="flex items-end">
                    <button onClick={clearFilters} className="btn-secondary w-full text-sm py-2">
                      Clear All
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full" />
          </div>
        ) : searched ? (
          results.length > 0 ? (
            <>
              <p className="text-slate-400 mb-4">{results.length} result{results.length !== 1 ? 's' : ''} found</p>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {results.map((fugitive) => (
                  <FugitiveCard 
                    key={fugitive.fugitive_id}
                    fugitive={fugitive}
                    onClick={() => navigate(`/fugitive/${fugitive.fugitive_id}`)}
                  />
                ))}
              </div>
            </>
          ) : (
            <div className="card-tactical p-12 text-center">
              <Search className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Results Found</h3>
              <p className="text-slate-400">Try adjusting your search terms or filters</p>
            </div>
          )
        ) : (
          <div className="card-tactical p-12 text-center">
            <ScanLine className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Search the Database</h3>
            <p className="text-slate-400">Enter a search term or use filters to find fugitives</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Component extracted to pages/
// Component extracted to pages/
// Component extracted to pages/
// Auth Callback
const AuthCallback = () => {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");
      
      if (!sessionId) {
        navigate("/login");
        return;
      }

      try {
        const response = await api.post("/auth/session", { session_id: sessionId });
        // Store the JWT token for subsequent requests
        if (response.data.token) {
          localStorage.setItem("wysspr_token", response.data.token);
        }
        setUser(response.data);
        toast.success("Logged in successfully!");
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { state: { user: response.data } });
      } catch {
        toast.error("Authentication failed");
        navigate("/login");
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full" />
    </div>
  );
};

// Unified SuperAdmin Dashboard
const SuperAdminDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardData, setDashboardData] = useState(null);
  const [publicUsers, setPublicUsers] = useState([]);
  const [communityPosts, setCommunityPosts] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [agencyUsers, setAgencyUsers] = useState([]);
  const [tips, setTips] = useState([]);
  const [leContacts, setLeContacts] = useState([]);
  const [captures, setCaptures] = useState([]);
  const [tipForwards, setTipForwards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [selectedTipForForward, setSelectedTipForForward] = useState(null);
  const [newContact, setNewContact] = useState({ name: '', agency_name: '', agency_type: 'FBI', email: '', phone: '', jurisdiction: '', notes: '' });
  const [newCapture, setNewCapture] = useState({ fugitive_id: '', capture_date: '', capture_location: '', capture_details: '', news_link: '', contributing_tip_ids: [], arresting_agency: '' });
  const [fugitives, setFugitives] = useState([]);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageTarget, setMessageTarget] = useState(null);
  const [messageForm, setMessageForm] = useState({ subject: '', message: '', sendEmail: true });
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showEditPostModal, setShowEditPostModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editPostForm, setEditPostForm] = useState({ title: '', content: '', category: '', type: '' });
  const [securityData, setSecurityData] = useState(null);
  const [blockedIPs, setBlockedIPs] = useState([]);
  const [threatSummary, setThreatSummary] = useState(null);
  const [botStats, setBotStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [newBlockIP, setNewBlockIP] = useState({ ip: '', reason: '', duration: 24 });
  const [tipAnalytics, setTipAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const navigate = useNavigate();

  const viewUserProfile = async (userId) => {
    setLoadingProfile(true);
    try {
      const res = await api.get(`/profile/${userId}`);
      setSelectedUserProfile(res.data);
    } catch (error) {
      toast.error("Failed to load user profile");
    } finally {
      setLoadingProfile(false);
    }
  };

  const openMessageModal = (user) => {
    setMessageTarget(user);
    setMessageForm({ subject: '', message: '', sendEmail: true });
    setShowMessageModal(true);
  };

  const sendMessageToUser = async () => {
    if (!messageTarget || !messageForm.subject || !messageForm.message) {
      toast.error("Please fill in subject and message");
      return;
    }
    
    setSendingMessage(true);
    try {
      const res = await api.post(`/superadmin/users/${messageTarget.user_id}/message`, {
        subject: messageForm.subject,
        message: messageForm.message,
        send_email: messageForm.sendEmail
      });
      toast.success(`Message sent to ${messageTarget.name || messageTarget.email}!`);
      setShowMessageModal(false);
      setMessageTarget(null);
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchFugitives();
  }, []);

  useEffect(() => {
    if (activeTab === 'users') fetchPublicUsers();
    if (activeTab === 'community') fetchCommunityPosts();
    if (activeTab === 'agencies') fetchAgencies();
    if (activeTab === 'agency-users') fetchAgencyUsers();
    if (activeTab === 'tips') { fetchTips(); fetchLeContacts(); }
    if (activeTab === 'contacts') fetchLeContacts();
    if (activeTab === 'captures') { fetchCaptures(); fetchTipForwards(); }
    if (activeTab === 'security') fetchSecurityData();
    if (activeTab === 'tip-analytics') fetchTipAnalytics();
  }, [activeTab]);

  const fetchFugitives = async () => {
    try {
      const res = await api.get('/fugitives?limit=300');
      setFugitives(res.data || []);
    } catch (error) {
      console.error('Failed to load fugitives');
    }
  };

  const fetchLeContacts = async () => {
    try {
      const res = await api.get('/superadmin/le-contacts');
      setLeContacts(res.data.contacts || []);
    } catch (error) {
      console.error('Failed to load LE contacts');
    }
  };

  const fetchCaptures = async () => {
    try {
      const res = await api.get('/superadmin/captures');
      setCaptures(res.data.captures || []);
    } catch (error) {
      console.error('Failed to load captures');
    }
  };

  const fetchTipForwards = async () => {
    try {
      const res = await api.get('/superadmin/tips/forwards');
      setTipForwards(res.data.forwards || []);
    } catch (error) {
      console.error('Failed to load tip forwards');
    }
  };

  const fetchSecurityData = async () => {
    try {
      const [blockedRes, threatRes, botRes, auditRes, summaryRes] = await Promise.all([
        api.get('/superadmin/security/blocked-ips'),
        api.get('/superadmin/security/threat-summary?hours=24'),
        api.get('/superadmin/security/bot-stats?hours=24'),
        api.get('/superadmin/security/audit-logs?limit=50'),
        api.get('/superadmin/security/security-summary?hours=24')
      ]);
      setBlockedIPs(blockedRes.data || []);
      setThreatSummary(threatRes.data || {});
      setBotStats(botRes.data || {});
      setAuditLogs(auditRes.data || []);
      setSecurityData(summaryRes.data || {});
    } catch (error) {
      console.error('Failed to load security data:', error);
      toast.error('Failed to load security data');
    }
  };

  const fetchTipAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await api.get('/superadmin/tip-analytics');
      setTipAnalytics(res.data);
    } catch (error) {
      toast.error('Failed to load tip analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleBlockIP = async () => {
    if (!newBlockIP.ip || !newBlockIP.reason) {
      toast.error('IP and reason are required');
      return;
    }
    setActionLoading('block-ip');
    try {
      await api.post(`/superadmin/security/block-ip?ip_address=${encodeURIComponent(newBlockIP.ip)}&reason=${encodeURIComponent(newBlockIP.reason)}&duration_hours=${newBlockIP.duration || ''}`);
      toast.success(`IP ${newBlockIP.ip} blocked successfully`);
      setNewBlockIP({ ip: '', reason: '', duration: 24 });
      fetchSecurityData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to block IP');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnblockIP = async (ip) => {
    setActionLoading(ip);
    try {
      await api.post(`/superadmin/security/unblock-ip?ip_address=${encodeURIComponent(ip)}`);
      toast.success(`IP ${ip} unblocked`);
      fetchSecurityData();
    } catch (error) {
      toast.error('Failed to unblock IP');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateContact = async () => {
    try {
      await api.post('/superadmin/le-contacts', newContact);
      toast.success('Contact created');
      setShowContactModal(false);
      setNewContact({ name: '', agency_name: '', agency_type: 'FBI', email: '', phone: '', jurisdiction: '', notes: '' });
      fetchLeContacts();
    } catch (error) {
      toast.error('Failed to create contact');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await api.delete(`/superadmin/le-contacts/${contactId}`);
      toast.success('Contact deleted');
      fetchLeContacts();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const handleForwardTip = async (contactId, additionalNotes) => {
    if (!selectedTipForForward) return;
    setActionLoading(selectedTipForForward.tip_id);
    try {
      const res = await api.post('/superadmin/tips/forward', {
        tip_id: selectedTipForForward.tip_id,
        contact_id: contactId,
        additional_notes: additionalNotes
      });
      toast.success(res.data.message);
      setShowForwardModal(false);
      setSelectedTipForForward(null);
      fetchTips();
      fetchTipForwards();
    } catch (error) {
      toast.error('Failed to forward tip');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReportCapture = async () => {
    if (!newCapture.fugitive_id || !newCapture.capture_date) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      await api.post('/superadmin/captures', newCapture);
      toast.success('Capture reported successfully!');
      setShowCaptureModal(false);
      setNewCapture({ fugitive_id: '', capture_date: '', capture_location: '', capture_details: '', news_link: '', contributing_tip_ids: [], arresting_agency: '' });
      fetchCaptures();
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to report capture');
    }
  };

  const fetchDashboardData = async () => {
    try {
      const res = await api.get('/superadmin/dashboard');
      setDashboardData(res.data);
    } catch (error) {
      toast.error('Failed to load dashboard. Are you a Super Admin?');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchPublicUsers = async () => {
    try {
      const res = await api.get('/superadmin/users');
      setPublicUsers(res.data.users || []);
    } catch (error) {
      toast.error('Failed to load users');
    }
  };

  const fetchCommunityPosts = async () => {
    try {
      const res = await api.get('/superadmin/community/posts');
      setCommunityPosts(res.data.posts || []);
    } catch (error) {
      toast.error('Failed to load posts');
    }
  };

  const fetchAgencies = async () => {
    try {
      const res = await api.get('/superadmin/agencies');
      setAgencies(res.data.agencies || []);
    } catch (error) {
      toast.error('Failed to load agencies');
    }
  };

  const fetchAgencyUsers = async () => {
    try {
      const res = await api.get('/superadmin/agency-users');
      setAgencyUsers(res.data.users || []);
    } catch (error) {
      toast.error('Failed to load agency users');
    }
  };

  const fetchTips = async () => {
    try {
      const res = await api.get('/superadmin/tips');
      setTips(res.data.tips || []);
    } catch (error) {
      toast.error('Failed to load tips');
    }
  };

  const handleDeleteUser = async (userId, email) => {
    if (!window.confirm(`Delete user "${email}" and all their data?`)) return;
    setActionLoading(userId);
    try {
      await api.delete(`/superadmin/users/${userId}`);
      toast.success('User deleted');
      fetchPublicUsers();
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    setActionLoading(postId);
    try {
      await api.delete(`/superadmin/community/posts/${postId}`);
      toast.success('Post deleted');
      fetchCommunityPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteComment = async (postId, commentIdx) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await api.delete(`/superadmin/community/posts/${postId}/comments/${commentIdx}`);
      toast.success('Comment deleted');
      fetchCommunityPosts();
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const openEditPostModal = (post) => {
    setEditingPost(post);
    setEditPostForm({
      title: post.title || '',
      content: post.content || '',
      category: post.category || 'General',
      type: post.type || 'DISCUSSION'
    });
    setShowEditPostModal(true);
  };

  const handleUpdatePost = async () => {
    if (!editingPost) return;
    setActionLoading(editingPost.post_id);
    try {
      await api.put(`/superadmin/community/posts/${editingPost.post_id}`, editPostForm);
      toast.success('Post updated successfully');
      setShowEditPostModal(false);
      setEditingPost(null);
      fetchCommunityPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update post');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteAgency = async (agencyId, agencyName) => {
    if (!window.confirm(`Delete agency "${agencyName}" and ALL its users?`)) return;
    setActionLoading(agencyId);
    try {
      await api.delete(`/superadmin/agencies/${agencyId}`);
      toast.success('Agency deleted');
      fetchAgencies();
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete agency');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteAgencyUser = async (userId, email) => {
    if (!window.confirm(`Delete agency user "${email}"?`)) return;
    setActionLoading(userId);
    try {
      await api.delete(`/superadmin/agency-users/${userId}`);
      toast.success('Agency user deleted');
      fetchAgencyUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteTip = async (tipId) => {
    if (!window.confirm('Delete this tip?')) return;
    setActionLoading(tipId);
    try {
      await api.delete(`/superadmin/tips/${tipId}`);
      toast.success('Tip deleted');
      fetchTips();
    } catch (error) {
      toast.error('Failed to delete tip');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteCapture = async (captureId) => {
    if (!window.confirm('Delete this capture record? This will also revert the fugitive status to WANTED.')) return;
    setActionLoading(captureId);
    try {
      await api.delete(`/superadmin/captures/${captureId}`);
      toast.success('Capture deleted and fugitive status reverted');
      fetchCaptures();
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to delete capture');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'users', label: 'Public Users', icon: Users },
    { id: 'community', label: 'Community Posts', icon: MessageSquare },
    { id: 'tips', label: 'All Tips', icon: Target },
    { id: 'contacts', label: 'LE Contacts', icon: FileText },
    { id: 'captures', label: 'Captures', icon: CheckCircle },
    { id: 'agencies', label: 'Agencies', icon: Building },
    { id: 'agency-users', label: 'Agency Users', icon: Shield },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'tip-analytics', label: 'Tip Analytics', icon: TrendingUp },
  ];

  return (
    <div className="min-h-screen pt-20 px-4 pb-12">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <p className="text-red-400 font-mono text-sm tracking-[0.2em] mb-2">SUPER ADMIN</p>
          <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tight uppercase text-white">
            Unified Control Center
          </h1>
          <p className="text-slate-400 mt-2">Manage both Public and Agency platforms from one dashboard</p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`px-4 py-2 rounded-sm font-semibold text-sm transition-all flex items-center gap-2 ${
                activeTab === id
                  ? 'bg-red-500 text-white'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && dashboardData && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="card-tactical p-4 border-cyan-500/30">
                <p className="text-slate-400 text-xs uppercase">Public Users</p>
                <p className="text-cyan-400 font-bold text-2xl">{dashboardData.public_stats.users}</p>
              </div>
              <div className="card-tactical p-4 border-green-500/30">
                <p className="text-slate-400 text-xs uppercase">Total Tips</p>
                <p className="text-green-400 font-bold text-2xl">{dashboardData.public_stats.tips}</p>
              </div>
              <div className="card-tactical p-4 border-purple-500/30">
                <p className="text-slate-400 text-xs uppercase">Community Posts</p>
                <p className="text-purple-400 font-bold text-2xl">{dashboardData.public_stats.community_posts}</p>
              </div>
              <div className="card-tactical p-4 border-amber-500/30">
                <p className="text-slate-400 text-xs uppercase">Fugitives</p>
                <p className="text-amber-400 font-bold text-2xl">{dashboardData.fugitive_stats.total}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="card-tactical p-4 border-blue-500/30">
                <p className="text-slate-400 text-xs uppercase">Agencies</p>
                <p className="text-blue-400 font-bold text-2xl">{dashboardData.agency_stats.agencies}</p>
              </div>
              <div className="card-tactical p-4 border-pink-500/30">
                <p className="text-slate-400 text-xs uppercase">Agency Users</p>
                <p className="text-pink-400 font-bold text-2xl">{dashboardData.agency_stats.users}</p>
              </div>
              <div className="card-tactical p-4 border-red-500/30">
                <p className="text-slate-400 text-xs uppercase">Captured</p>
                <p className="text-red-400 font-bold text-2xl">{dashboardData.fugitive_stats.captured}</p>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="card-tactical p-4">
                <h3 className="text-white font-semibold mb-3">Recent Tips</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {dashboardData.recent_tips.slice(0, 5).map((tip, idx) => (
                    <div key={idx} className="bg-slate-800/50 p-2 rounded text-sm">
                      <p className="text-slate-300 truncate">{tip.content}</p>
                      <p className="text-slate-500 text-xs">{new Date(tip.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card-tactical p-4">
                <h3 className="text-white font-semibold mb-3">Recent Posts</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {dashboardData.recent_posts.slice(0, 5).map((post, idx) => (
                    <div key={idx} className="bg-slate-800/50 p-2 rounded text-sm">
                      <p className="text-slate-300 truncate">{post.title || post.content}</p>
                      <p className="text-slate-500 text-xs">{post.anonymous_name} • {new Date(post.created_at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Public Users Tab */}
        {activeTab === 'users' && (
          <div className="card-tactical p-4">
            <h3 className="text-white font-semibold mb-4">Public Users ({publicUsers.length})</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700 text-left">
                    <th className="py-2 px-3 text-slate-400 text-sm">Email</th>
                    <th className="py-2 px-3 text-slate-400 text-sm">Name</th>
                    <th className="py-2 px-3 text-slate-400 text-sm">Role</th>
                    <th className="py-2 px-3 text-slate-400 text-sm">Joined</th>
                    <th className="py-2 px-3 text-slate-400 text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {publicUsers.map((u) => (
                    <tr key={u.user_id} className="border-b border-slate-800">
                      <td className="py-2 px-3 text-cyan-400 text-sm">{u.email}</td>
                      <td className="py-2 px-3 text-white text-sm">{u.name}</td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${u.role === 'super_admin' ? 'bg-red-500/20 text-red-400' : 'bg-slate-700 text-slate-300'}`}>
                          {u.role || 'user'}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-slate-500 text-sm">{new Date(u.created_at).toLocaleDateString()}</td>
                      <td className="py-2 px-3 flex gap-2">
                        <button
                          onClick={() => viewUserProfile(u.user_id)}
                          className="p-1 bg-cyan-500/20 text-cyan-400 rounded hover:bg-cyan-500/30 flex items-center gap-1"
                          title="View profile"
                          data-testid={`view-profile-${u.user_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openMessageModal(u)}
                          className="p-1 bg-emerald-500/20 text-emerald-400 rounded hover:bg-emerald-500/30 flex items-center gap-1"
                          title="Send message"
                          data-testid={`message-user-${u.user_id}`}
                        >
                          <Mail className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(u.user_id, u.email)}
                          disabled={actionLoading === u.user_id || u.role === 'super_admin'}
                          className="p-1 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 disabled:opacity-50"
                          title="Delete user"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Community Posts Tab */}
        {activeTab === 'community' && (
          <div className="space-y-4">
            <h3 className="text-white font-semibold">Community Posts ({communityPosts.length}) - FULL ACCESS</h3>
            {communityPosts.map((post) => (
              <div key={post.post_id} className="card-tactical p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-cyan-400 text-sm font-semibold">{post.anonymous_name}</span>
                      <span className="text-slate-500 text-xs">({post.category})</span>
                    </div>
                    {/* Real User Info - GOD MODE */}
                    <p className="text-red-400 text-xs mt-1">
                      <span className="text-slate-500">Real Identity:</span> {post.author_email || 'Anonymous'}
                      {post.author_real_name && post.author_real_name !== 'Anonymous' && (
                        <span className="text-slate-400 ml-1">({post.author_real_name})</span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditPostModal(post)}
                      className="p-1 bg-cyan-500/20 text-cyan-400 rounded hover:bg-cyan-500/30"
                      title="Edit post"
                    >
                      <FileText className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeletePost(post.post_id)}
                      disabled={actionLoading === post.post_id}
                      className="p-1 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30"
                      title="Delete post"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <h4 className="text-white font-semibold">{post.title}</h4>
                <p className="text-slate-400 text-sm mt-1">{post.content}</p>
                <p className="text-slate-600 text-xs mt-2">{new Date(post.created_at).toLocaleString()}</p>
                
                {/* Comments with Real User Info */}
                {post.comments?.length > 0 && (
                  <div className="mt-3 pl-4 border-l border-slate-700 space-y-2">
                    <p className="text-slate-500 text-xs">Comments ({post.comments.length})</p>
                    {post.comments.map((comment, idx) => (
                      <div key={idx} className="flex justify-between items-start bg-slate-800/50 p-2 rounded">
                        <div>
                          <p className="text-slate-300 text-sm">{comment.content}</p>
                          <p className="text-slate-600 text-xs">{comment.anonymous_name}</p>
                          {/* Commenter Real Identity - GOD MODE */}
                          <p className="text-red-400 text-xs">
                            <span className="text-slate-500">Real:</span> {comment.commenter_email || 'Anonymous'}
                            {comment.commenter_real_name && comment.commenter_real_name !== 'Anonymous' && (
                              <span className="text-slate-400 ml-1">({comment.commenter_real_name})</span>
                            )}
                          </p>
                        </div>
                        <button
                          onClick={() => handleDeleteComment(post.post_id, idx)}
                          className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Tips Tab */}
        {activeTab === 'tips' && (
          <div className="card-tactical p-4">
            <h3 className="text-white font-semibold mb-4">All Tips ({tips.length}) - FULL ACCESS</h3>
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {tips.map((tip) => (
                <div key={tip.tip_id} className="bg-slate-800/50 p-4 rounded border border-slate-700">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          tip.status === 'VERIFIED' ? 'bg-green-500/20 text-green-400' :
                          tip.status === 'LED_TO_CAPTURE' ? 'bg-purple-500/20 text-purple-400' :
                          tip.status === 'PENDING' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-slate-700 text-slate-400'
                        }`}>{tip.status}</span>
                        {tip.forwarded && <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">Forwarded</span>}
                        <span className="text-cyan-400 text-sm font-semibold">{tip.fugitive_name}</span>
                        {tip.reward_amount > 0 && <span className="text-amber-400 text-xs">${tip.reward_amount?.toLocaleString()}</span>}
                      </div>
                      
                      {/* FULL TIP CONTENT */}
                      <div className="bg-slate-900/50 p-3 rounded mb-2">
                        <p className="text-white text-sm whitespace-pre-wrap">{tip.full_content || tip.content}</p>
                        {tip.all_details && (
                          <p className="text-slate-400 text-sm mt-2 border-t border-slate-700 pt-2">{tip.all_details}</p>
                        )}
                      </div>
                      
                      {/* Submitter Info - GOD MODE */}
                      <div className="flex items-center gap-4 text-xs mb-2">
                        <span className="text-red-400 font-semibold">
                          <span className="text-slate-500">Submitted by:</span> {tip.submitter_email || 'Anonymous'}
                        </span>
                        {tip.submitter_name && tip.submitter_name !== 'Anonymous' && (
                          <span className="text-slate-400">({tip.submitter_name})</span>
                        )}
                      </div>
                      
                      {/* Location & Time */}
                      <p className="text-slate-600 text-xs">{tip.location} • {new Date(tip.created_at).toLocaleString()}</p>
                      
                      {/* Contact Info if provided */}
                      {tip.contact_info && tip.contact_info !== 'Not provided' && (
                        <p className="text-green-400 text-xs mt-1">Contact: {tip.contact_info}</p>
                      )}
                      
                      {/* Evidence Files */}
                      {tip.evidence_files && tip.evidence_files.length > 0 && (
                        <p className="text-purple-400 text-xs mt-1">Evidence: {tip.evidence_files.length} file(s) attached</p>
                      )}
                      
                      {tip.forwarded_to && <p className="text-blue-400 text-xs mt-1">→ Forwarded to: {tip.forwarded_to}</p>}
                    </div>
                    <div className="flex flex-col gap-2 ml-2">
                      {!tip.forwarded && (
                        <button
                          onClick={() => { setSelectedTipForForward(tip); setShowForwardModal(true); }}
                          className="p-2 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30"
                          title="Forward to Law Enforcement"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteTip(tip.tip_id)}
                        disabled={actionLoading === tip.tip_id}
                        className="p-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30"
                        title="Delete Tip"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Forward Tip Modal */}
        {showForwardModal && selectedTipForForward && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-lg w-full p-6">
              <h3 className="text-white font-bold text-lg mb-4">Forward Tip to Law Enforcement</h3>
              <div className="bg-slate-800/50 p-3 rounded mb-4">
                <p className="text-slate-400 text-xs">Fugitive: <span className="text-cyan-400">{selectedTipForForward.fugitive_name}</span></p>
                <p className="text-slate-300 text-sm mt-1">{selectedTipForForward.content}</p>
              </div>
              <div className="mb-4">
                <label className="block text-slate-400 text-sm mb-2">Select Contact</label>
                <select id="forward-contact" className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white">
                  <option value="">Select a contact...</option>
                  {leContacts.map(c => (
                    <option key={c.contact_id} value={c.contact_id}>{c.name} - {c.agency_name} ({c.email})</option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-slate-400 text-sm mb-2">Additional Notes (optional)</label>
                <textarea id="forward-notes" rows="3" className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm" placeholder="Any additional context..."></textarea>
              </div>
              <div className="flex gap-3">
                <button onClick={() => { setShowForwardModal(false); setSelectedTipForForward(null); }} className="flex-1 px-4 py-2 bg-slate-700 text-white rounded">Cancel</button>
                <button
                  onClick={() => {
                    const contactId = document.getElementById('forward-contact').value;
                    const notes = document.getElementById('forward-notes').value;
                    if (!contactId) { toast.error('Please select a contact'); return; }
                    handleForwardTip(contactId, notes);
                  }}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded font-semibold"
                >
                  Forward Tip
                </button>
              </div>
            </div>
          </div>
        )}

        {/* LE Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-white font-semibold">Law Enforcement Contacts ({leContacts.length})</h3>
              <button onClick={() => setShowContactModal(true)} className="btn-primary px-4 py-2 text-sm flex items-center gap-2">
                <Users className="w-4 h-4" /> Add Contact
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {leContacts.map(contact => (
                <div key={contact.contact_id} className="card-tactical p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="text-white font-semibold">{contact.name}</h4>
                      <p className="text-cyan-400 text-sm">{contact.agency_name} ({contact.agency_type})</p>
                      <p className="text-slate-400 text-sm">{contact.email}</p>
                      {contact.phone && <p className="text-slate-500 text-xs">{contact.phone}</p>}
                      <p className="text-slate-500 text-xs">Jurisdiction: {contact.jurisdiction}</p>
                      <div className="flex gap-4 mt-2 text-xs">
                        <span className="text-blue-400">Tips Forwarded: {contact.tips_forwarded || 0}</span>
                        <span className="text-green-400">Captures: {contact.captures_attributed || 0}</span>
                      </div>
                    </div>
                    <button onClick={() => handleDeleteContact(contact.contact_id)} className="p-1 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add Contact Modal */}
        {showContactModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-lg w-full p-6">
              <h3 className="text-white font-bold text-lg mb-4">Add Law Enforcement Contact</h3>
              <div className="space-y-4">
                <input type="text" placeholder="Contact Name *" value={newContact.name} onChange={e => setNewContact({...newContact, name: e.target.value})} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white" />
                <input type="text" placeholder="Agency Name *" value={newContact.agency_name} onChange={e => setNewContact({...newContact, agency_name: e.target.value})} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white" />
                <select value={newContact.agency_type} onChange={e => setNewContact({...newContact, agency_type: e.target.value})} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white">
                  <option value="FBI">FBI</option>
                  <option value="US_MARSHALS">US Marshals</option>
                  <option value="DEA">DEA</option>
                  <option value="ATF">ATF</option>
                  <option value="ICE">ICE</option>
                  <option value="STATE_POLICE">State Police</option>
                  <option value="LOCAL_POLICE">Local Police</option>
                  <option value="SECRET_SERVICE">Secret Service</option>
                  <option value="OTHER">Other</option>
                </select>
                <input type="email" placeholder="Email *" value={newContact.email} onChange={e => setNewContact({...newContact, email: e.target.value})} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white" />
                <input type="text" placeholder="Phone" value={newContact.phone} onChange={e => setNewContact({...newContact, phone: e.target.value})} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white" />
                <input type="text" placeholder="Jurisdiction (State/Region) *" value={newContact.jurisdiction} onChange={e => setNewContact({...newContact, jurisdiction: e.target.value})} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white" />
                <textarea placeholder="Notes" value={newContact.notes} onChange={e => setNewContact({...newContact, notes: e.target.value})} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white" rows="2"></textarea>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowContactModal(false)} className="flex-1 px-4 py-2 bg-slate-700 text-white rounded">Cancel</button>
                <button onClick={handleCreateContact} className="flex-1 px-4 py-2 bg-cyan-500 text-white rounded font-semibold">Add Contact</button>
              </div>
            </div>
          </div>
        )}

        {/* Captures Tab */}
        {activeTab === 'captures' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-white font-semibold">Capture Records ({captures.length})</h3>
              <button onClick={() => setShowCaptureModal(true)} className="btn-primary px-4 py-2 text-sm flex items-center gap-2">
                <CheckCircle className="w-4 h-4" /> Report Capture
              </button>
            </div>
            
            {/* Recent Tip Forwards */}
            {tipForwards.length > 0 && (
              <div className="card-tactical p-4 border-blue-500/30">
                <h4 className="text-white font-semibold mb-3">Recent Tip Forwards ({tipForwards.length})</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {tipForwards.slice(0, 10).map(fwd => (
                    <div key={fwd.forward_id} className="bg-slate-800/50 p-2 rounded text-sm flex justify-between">
                      <div>
                        <span className="text-cyan-400">{fwd.fugitive_name}</span>
                        <span className="text-slate-500 mx-2">→</span>
                        <span className="text-blue-400">{fwd.contact_agency}</span>
                      </div>
                      <span className="text-slate-500 text-xs">{new Date(fwd.forwarded_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Captures List */}
            <div className="space-y-3">
              {captures.map(cap => (
                <div key={cap.capture_id} className="card-tactical p-4 border-green-500/30">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        <h4 className="text-white font-semibold">{cap.fugitive_name}</h4>
                        <span className={`px-2 py-0.5 rounded text-xs ${cap.status === 'CONFIRMED' ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}`}>{cap.status}</span>
                      </div>
                      <p className="text-slate-400 text-sm mt-1">Captured: {new Date(cap.capture_date).toLocaleDateString()}</p>
                      {cap.capture_location && <p className="text-slate-500 text-xs">Location: {cap.capture_location}</p>}
                      {cap.arresting_agency && <p className="text-blue-400 text-xs">Agency: {cap.arresting_agency}</p>}
                      
                      {/* Reporter Info - GOD MODE */}
                      <div className="mt-2 p-2 bg-slate-900/50 rounded">
                        <p className="text-red-400 text-xs">
                          <span className="text-slate-500">Reported by:</span> {cap.reporter_email || 'System'} 
                          {cap.reporter_name && cap.reporter_name !== 'System' && <span className="text-slate-400 ml-1">({cap.reporter_name})</span>}
                        </p>
                      </div>
                      
                      {/* Contributing Tips with Tipster Info */}
                      {cap.contributing_tips_details && cap.contributing_tips_details.length > 0 && (
                        <div className="mt-2 p-2 bg-purple-900/20 rounded border border-purple-500/20">
                          <p className="text-purple-400 text-xs font-semibold mb-1">Contributing Tips ({cap.contributing_tips_details.length}):</p>
                          {cap.contributing_tips_details.map((tip, idx) => (
                            <div key={idx} className="text-xs mt-1 pl-2 border-l border-purple-500/30">
                              <p className="text-slate-300">{tip.content}</p>
                              <p className="text-red-400">By: {tip.tipster_email} {tip.tipster_name && <span className="text-slate-400">({tip.tipster_name})</span>}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      {cap.contributing_tip_ids?.length > 0 && !cap.contributing_tips_details && (
                        <p className="text-purple-400 text-xs mt-1">Contributing Tips: {cap.contributing_tip_ids.length}</p>
                      )}
                      
                      {cap.news_link && <a href={cap.news_link} target="_blank" rel="noopener noreferrer" className="text-cyan-400 text-xs hover:underline block mt-1">News Link →</a>}
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <p className="text-amber-400 font-bold">${cap.reward_amount?.toLocaleString()}</p>
                      <button
                        onClick={() => handleDeleteCapture(cap.capture_id)}
                        disabled={actionLoading === cap.capture_id}
                        className="p-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 disabled:opacity-50"
                        title="Delete Capture Record"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Report Capture Modal */}
        {showCaptureModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-slate-900 border border-slate-700 rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
              <h3 className="text-white font-bold text-lg mb-4">Report Fugitive Capture</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-slate-400 text-sm mb-1">Fugitive *</label>
                  <select value={newCapture.fugitive_id} onChange={e => setNewCapture({...newCapture, fugitive_id: e.target.value})} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white">
                    <option value="">Select fugitive...</option>
                    {fugitives.filter(f => f.status !== 'captured').map(f => (
                      <option key={f.fugitive_id} value={f.fugitive_id}>{f.name} - ${f.reward_amount?.toLocaleString()}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-slate-400 text-sm mb-1">Capture Date *</label>
                  <input type="date" value={newCapture.capture_date} onChange={e => setNewCapture({...newCapture, capture_date: e.target.value})} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white" />
                </div>
                <input type="text" placeholder="Capture Location" value={newCapture.capture_location} onChange={e => setNewCapture({...newCapture, capture_location: e.target.value})} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white" />
                <input type="text" placeholder="Arresting Agency" value={newCapture.arresting_agency} onChange={e => setNewCapture({...newCapture, arresting_agency: e.target.value})} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white" />
                <textarea placeholder="Capture Details" value={newCapture.capture_details} onChange={e => setNewCapture({...newCapture, capture_details: e.target.value})} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white" rows="2"></textarea>
                <input type="url" placeholder="News Link (URL)" value={newCapture.news_link} onChange={e => setNewCapture({...newCapture, news_link: e.target.value})} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white" />
                <div>
                  <label className="block text-slate-400 text-sm mb-1">Contributing Tip IDs (comma separated)</label>
                  <input type="text" placeholder="tip_xxx, tip_yyy" onChange={e => setNewCapture({...newCapture, contributing_tip_ids: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})} className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white" />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowCaptureModal(false)} className="flex-1 px-4 py-2 bg-slate-700 text-white rounded">Cancel</button>
                <button onClick={handleReportCapture} className="flex-1 px-4 py-2 bg-green-500 text-white rounded font-semibold">Report Capture</button>
              </div>
            </div>
          </div>
        )}

        {/* Agencies Tab */}
        {activeTab === 'agencies' && (
          <div className="card-tactical p-4">
            <h3 className="text-white font-semibold mb-4">All Agencies ({agencies.length}) - FULL REGISTRATION INFO</h3>
            <div className="space-y-4">
              {agencies.map((agency) => (
                <div key={agency.agency_id} className="bg-slate-800/50 p-4 rounded border border-slate-700">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="text-white font-semibold text-lg">{agency.name}</h4>
                        {agency.is_verified ? (
                          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded">Verified</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">Pending</span>
                        )}
                      </div>
                      
                      {/* Basic Info */}
                      <div className="grid md:grid-cols-2 gap-4 mb-3">
                        <div>
                          <p className="text-cyan-400 text-sm">{agency.agency_type} • {agency.jurisdiction}</p>
                          <p className="text-slate-400 text-xs mt-1">Users: {agency.user_count}</p>
                        </div>
                        <div>
                          <p className="text-slate-400 text-xs">Registered: {new Date(agency.registration_date || agency.created_at).toLocaleDateString()}</p>
                          {agency.verification_status === 'Verified' && agency.verification_date && (
                            <p className="text-green-400 text-xs">Verified: {new Date(agency.verification_date).toLocaleDateString()}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Full Contact Info - GOD MODE */}
                      <div className="bg-slate-900/50 p-3 rounded mb-2">
                        <p className="text-red-400 text-xs font-semibold mb-1">Contact Details:</p>
                        <p className="text-slate-300 text-sm">{agency.admin_contact || agency.contact_email}</p>
                        {agency.admin_phone && <p className="text-slate-400 text-xs">{agency.admin_phone}</p>}
                        {agency.full_address && <p className="text-slate-500 text-xs">{agency.full_address}</p>}
                      </div>
                      
                      {/* Admin Users */}
                      {agency.admin_users && agency.admin_users.length > 0 && (
                        <div className="bg-blue-900/20 p-2 rounded border border-blue-500/20">
                          <p className="text-blue-400 text-xs font-semibold mb-1">Admin Users:</p>
                          {agency.admin_users.map((admin, idx) => (
                            <p key={idx} className="text-slate-300 text-xs">
                              {admin.email} ({admin.name || 'No name'}) - <span className="text-blue-400">{admin.role}</span>
                            </p>
                          ))}
                        </div>
                      )}
                      
                      {/* Verification Notes */}
                      {agency.verification_notes && (
                        <p className="text-slate-500 text-xs mt-2 italic">Notes: {agency.verification_notes}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteAgency(agency.agency_id, agency.name)}
                      disabled={actionLoading === agency.agency_id}
                      className="p-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30"
                      title="Delete Agency"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Agency Users Tab */}
        {activeTab === 'agency-users' && (
          <div className="card-tactical p-4">
            <h3 className="text-white font-semibold mb-4">Agency Users ({agencyUsers.length})</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700 text-left">
                    <th className="py-2 px-3 text-slate-400 text-sm">Email</th>
                    <th className="py-2 px-3 text-slate-400 text-sm">Name</th>
                    <th className="py-2 px-3 text-slate-400 text-sm">Agency</th>
                    <th className="py-2 px-3 text-slate-400 text-sm">Role</th>
                    <th className="py-2 px-3 text-slate-400 text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {agencyUsers.map((u) => (
                    <tr key={u.user_id} className="border-b border-slate-800">
                      <td className="py-2 px-3 text-cyan-400 text-sm">{u.email}</td>
                      <td className="py-2 px-3 text-white text-sm">{u.name}</td>
                      <td className="py-2 px-3 text-slate-400 text-sm">{u.agency_name}</td>
                      <td className="py-2 px-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${u.role === 'super_admin' ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'}`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <button
                          onClick={() => handleDeleteAgencyUser(u.user_id, u.email)}
                          disabled={actionLoading === u.user_id || u.role === 'super_admin'}
                          className="p-1 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 disabled:opacity-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            {/* Security Overview Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="card-tactical p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-500/20 rounded">
                    <Shield className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{blockedIPs.length}</p>
                    <p className="text-slate-400 text-sm">Blocked IPs</p>
                  </div>
                </div>
              </div>
              <div className="card-tactical p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-500/20 rounded">
                    <AlertTriangle className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{threatSummary?.threat_types?.length || 0}</p>
                    <p className="text-slate-400 text-sm">Threat Types (24h)</p>
                  </div>
                </div>
              </div>
              <div className="card-tactical p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500/20 rounded">
                    <Eye className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{botStats?.bot_detections || 0}</p>
                    <p className="text-slate-400 text-sm">Bots Detected (24h)</p>
                  </div>
                </div>
              </div>
              <div className="card-tactical p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-cyan-500/20 rounded">
                    <FileText className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{securityData?.total_events || 0}</p>
                    <p className="text-slate-400 text-sm">Audit Events (24h)</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Block IP Form */}
            <div className="card-tactical p-4">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Lock className="w-5 h-5 text-red-400" />
                Block IP Address
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <input
                  type="text"
                  placeholder="IP Address (e.g., 192.168.1.1)"
                  value={newBlockIP.ip}
                  onChange={(e) => setNewBlockIP({ ...newBlockIP, ip: e.target.value })}
                  className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm focus:border-red-500 focus:outline-none"
                />
                <input
                  type="text"
                  placeholder="Reason for blocking"
                  value={newBlockIP.reason}
                  onChange={(e) => setNewBlockIP({ ...newBlockIP, reason: e.target.value })}
                  className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm focus:border-red-500 focus:outline-none"
                />
                <select
                  value={newBlockIP.duration}
                  onChange={(e) => setNewBlockIP({ ...newBlockIP, duration: e.target.value })}
                  className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-sm focus:border-red-500 focus:outline-none"
                >
                  <option value="1">1 hour</option>
                  <option value="24">24 hours</option>
                  <option value="168">1 week</option>
                  <option value="720">30 days</option>
                  <option value="">Permanent</option>
                </select>
                <button
                  onClick={handleBlockIP}
                  disabled={actionLoading === 'block-ip'}
                  className="px-4 py-2 bg-red-500 text-white rounded font-semibold hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {actionLoading === 'block-ip' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                  Block IP
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Blocked IPs List */}
              <div className="card-tactical p-4">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-red-400" />
                  Blocked IPs ({blockedIPs.length})
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {blockedIPs.length === 0 ? (
                    <p className="text-slate-500 text-sm">No IPs currently blocked</p>
                  ) : (
                    blockedIPs.map((ip, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-slate-800/50 p-3 rounded">
                        <div>
                          <p className="text-red-400 font-mono text-sm">{ip.ip_address}</p>
                          <p className="text-slate-500 text-xs">{ip.reason}</p>
                          <p className="text-slate-600 text-xs">
                            {ip.is_permanent ? 'Permanent' : `Expires: ${ip.expires_at}`}
                          </p>
                        </div>
                        <button
                          onClick={() => handleUnblockIP(ip.ip_address)}
                          disabled={actionLoading === ip.ip_address}
                          className="px-3 py-1 bg-green-500/20 text-green-400 rounded text-sm hover:bg-green-500/30"
                        >
                          {actionLoading === ip.ip_address ? 'Unblocking...' : 'Unblock'}
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Top Threat IPs */}
              <div className="card-tactical p-4">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  Top Offending IPs (24h)
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {!threatSummary?.top_offending_ips?.length ? (
                    <p className="text-slate-500 text-sm">No threats detected in the last 24 hours</p>
                  ) : (
                    threatSummary.top_offending_ips.map((ip, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-slate-800/50 p-3 rounded">
                        <div>
                          <p className="text-amber-400 font-mono text-sm">{ip.ip}</p>
                          <p className="text-slate-500 text-xs">{ip.threats} threats: {ip.types?.join(', ')}</p>
                        </div>
                        <button
                          onClick={() => {
                            setNewBlockIP({ ip: ip.ip, reason: 'Repeated attack attempts', duration: 168 });
                          }}
                          className="px-3 py-1 bg-red-500/20 text-red-400 rounded text-sm hover:bg-red-500/30"
                        >
                          Block
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Bot Detection Stats */}
            <div className="card-tactical p-4">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Eye className="w-5 h-5 text-purple-400" />
                Bot Detection Stats (24h)
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-800/50 p-3 rounded text-center">
                  <p className="text-2xl font-bold text-white">{botStats?.total_submissions || 0}</p>
                  <p className="text-slate-400 text-xs">Total Submissions</p>
                </div>
                <div className="bg-slate-800/50 p-3 rounded text-center">
                  <p className="text-2xl font-bold text-purple-400">{botStats?.bot_detections || 0}</p>
                  <p className="text-slate-400 text-xs">Bots Detected</p>
                </div>
                <div className="bg-slate-800/50 p-3 rounded text-center">
                  <p className="text-2xl font-bold text-green-400">{(botStats?.total_submissions || 0) - (botStats?.bot_detections || 0)}</p>
                  <p className="text-slate-400 text-xs">Legitimate</p>
                </div>
                <div className="bg-slate-800/50 p-3 rounded text-center">
                  <p className="text-2xl font-bold text-amber-400">{botStats?.bot_percentage?.toFixed(1) || 0}%</p>
                  <p className="text-slate-400 text-xs">Bot Rate</p>
                </div>
              </div>
            </div>

            {/* Recent Audit Logs */}
            <div className="card-tactical p-4">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-cyan-400" />
                Recent Security Events
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700 text-left">
                      <th className="py-2 px-3 text-slate-400 text-sm">Time</th>
                      <th className="py-2 px-3 text-slate-400 text-sm">Event</th>
                      <th className="py-2 px-3 text-slate-400 text-sm">User</th>
                      <th className="py-2 px-3 text-slate-400 text-sm">IP</th>
                      <th className="py-2 px-3 text-slate-400 text-sm">Severity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.slice(0, 20).map((log, idx) => (
                      <tr key={idx} className="border-b border-slate-800">
                        <td className="py-2 px-3 text-slate-500 text-xs">
                          {new Date(log.timestamp).toLocaleString()}
                        </td>
                        <td className="py-2 px-3 text-white text-sm font-mono">{log.event_type}</td>
                        <td className="py-2 px-3 text-cyan-400 text-sm">{log.email || log.user_id || '-'}</td>
                        <td className="py-2 px-3 text-slate-400 text-sm font-mono">{log.ip_address || '-'}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            log.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                            log.severity === 'warning' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-slate-700 text-slate-400'
                          }`}>
                            {log.severity}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Tip Analytics Tab */}
        {activeTab === 'tip-analytics' && (
          <div className="space-y-6" data-testid="tip-analytics-tab">
            {analyticsLoading ? (
              <div className="text-center py-20">
                <Activity className="w-8 h-8 text-cyan-400 animate-spin mx-auto mb-3" />
                <p className="text-slate-400">Loading analytics...</p>
              </div>
            ) : tipAnalytics ? (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  {[
                    { label: 'Total Tips', value: tipAnalytics.summary.total, color: 'cyan' },
                    { label: 'Last 24h', value: tipAnalytics.summary.last_24h, color: 'green' },
                    { label: 'Last 7d', value: tipAnalytics.summary.last_7d, color: 'blue' },
                    { label: 'Last 30d', value: tipAnalytics.summary.last_30d, color: 'purple' },
                    { label: 'Avg/Day', value: tipAnalytics.summary.avg_per_day, color: 'amber' },
                    { label: 'Anonymous', value: tipAnalytics.summary.anonymous, color: 'slate' },
                    { label: 'Identified', value: tipAnalytics.summary.identified, color: 'emerald' },
                  ].map(({ label, value, color }) => (
                    <div key={label} className={`card-tactical p-3 border-${color}-500/30`} data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                      <p className="text-slate-500 text-xs uppercase tracking-wider">{label}</p>
                      <p className={`text-${color}-400 font-bold text-xl mt-1`}>{value}</p>
                    </div>
                  ))}
                </div>

                {/* Charts Row 1: Volume Trend + Status Distribution */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Daily Volume Trend */}
                  <div className="lg:col-span-2 card-tactical p-5" data-testid="daily-volume-chart">
                    <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Daily Tip Volume (30 Days)</h3>
                    {tipAnalytics.daily_volume.length > 0 ? (
                      <ResponsiveContainer width="100%" height={260}>
                        <LineChart data={tipAnalytics.daily_volume}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                          <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
                          <YAxis tick={{ fill: '#64748b', fontSize: 11 }} allowDecimals={false} />
                          <RechartsTooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0' }} />
                          <Line type="monotone" dataKey="count" stroke="#22d3ee" strokeWidth={2} dot={{ r: 3, fill: '#22d3ee' }} activeDot={{ r: 5 }} name="Tips" />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-slate-500 text-center py-16">No tip data in the last 30 days</p>
                    )}
                  </div>

                  {/* Status Distribution Pie */}
                  <div className="card-tactical p-5" data-testid="status-distribution-chart">
                    <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Status Breakdown</h3>
                    {Object.keys(tipAnalytics.status_distribution).length > 0 ? (
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                          <Pie
                            data={Object.entries(tipAnalytics.status_distribution).map(([name, value]) => ({ name, value }))}
                            cx="50%" cy="50%" innerRadius={50} outerRadius={85}
                            paddingAngle={3} dataKey="value"
                            label={({ name, value }) => `${name}: ${value}`}
                          >
                            {Object.keys(tipAnalytics.status_distribution).map((_, i) => (
                              <Cell key={i} fill={['#22d3ee', '#f59e0b', '#22c55e', '#ef4444', '#8b5cf6', '#6366f1'][i % 6]} />
                            ))}
                          </Pie>
                          <RechartsTooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-slate-500 text-center py-16">No status data available</p>
                    )}
                  </div>
                </div>

                {/* Charts Row 2: Priority + Top Fugitives */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Priority Distribution */}
                  <div className="card-tactical p-5" data-testid="priority-distribution-chart">
                    <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Priority Levels</h3>
                    {Object.keys(tipAnalytics.priority_distribution).length > 0 ? (
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={Object.entries(tipAnalytics.priority_distribution).map(([name, value]) => ({ name, value }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                          <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                          <YAxis tick={{ fill: '#64748b', fontSize: 11 }} allowDecimals={false} />
                          <RechartsTooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0' }} />
                          <Bar dataKey="value" name="Tips" radius={[4, 4, 0, 0]}>
                            {Object.keys(tipAnalytics.priority_distribution).map((key, i) => (
                              <Cell key={i} fill={
                                key === 'CRITICAL' ? '#ef4444' : key === 'HIGH' ? '#f59e0b' : key === 'MEDIUM' ? '#3b82f6' : '#6b7280'
                              } />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-slate-500 text-center py-16">No priority data</p>
                    )}
                  </div>

                  {/* Top Fugitives by Tips */}
                  <div className="card-tactical p-5" data-testid="top-fugitives-chart">
                    <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Most Tipped Fugitives</h3>
                    {tipAnalytics.top_fugitives.length > 0 ? (
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={tipAnalytics.top_fugitives} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                          <XAxis type="number" tick={{ fill: '#64748b', fontSize: 11 }} allowDecimals={false} />
                          <YAxis dataKey="name" type="category" tick={{ fill: '#94a3b8', fontSize: 10 }} width={120} />
                          <RechartsTooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0' }} />
                          <Bar dataKey="tip_count" name="Tips" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-slate-500 text-center py-16">No fugitive tip data</p>
                    )}
                  </div>
                </div>

                {/* Row 3: Geographic + Top Submitters */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Geographic Distribution */}
                  <div className="card-tactical p-5" data-testid="geo-distribution">
                    <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
                      <MapPin className="w-4 h-4 inline mr-2 text-cyan-400" />
                      Tips by Location
                    </h3>
                    {tipAnalytics.geo_distribution.length > 0 ? (
                      <div className="space-y-2 max-h-[260px] overflow-y-auto pr-2">
                        {tipAnalytics.geo_distribution.map((loc, i) => (
                          <div key={i} className="flex items-center justify-between py-2 border-b border-slate-800/50">
                            <span className="text-slate-300 text-sm truncate mr-3">{loc.location}</span>
                            <div className="flex items-center gap-2 shrink-0">
                              <div className="w-24 h-2 bg-slate-800 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-cyan-500 rounded-full"
                                  style={{ width: `${Math.min((loc.count / tipAnalytics.geo_distribution[0].count) * 100, 100)}%` }}
                                />
                              </div>
                              <span className="text-cyan-400 font-mono text-sm w-8 text-right">{loc.count}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-16">No location data available</p>
                    )}
                  </div>

                  {/* Top Submitters */}
                  <div className="card-tactical p-5" data-testid="top-submitters">
                    <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">
                      <Trophy className="w-4 h-4 inline mr-2 text-amber-400" />
                      Top Identified Submitters
                    </h3>
                    {tipAnalytics.top_submitters.length > 0 ? (
                      <div className="space-y-3">
                        {tipAnalytics.top_submitters.map((sub, i) => (
                          <div key={i} className="flex items-center justify-between py-2 border-b border-slate-800/50">
                            <div className="flex items-center gap-3">
                              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                                i === 0 ? 'bg-amber-500/20 text-amber-400' : i === 1 ? 'bg-slate-400/20 text-slate-300' : i === 2 ? 'bg-orange-500/20 text-orange-400' : 'bg-slate-700 text-slate-400'
                              }`}>
                                {i + 1}
                              </span>
                              <div>
                                <p className="text-white text-sm font-medium">{sub.name}</p>
                                <p className="text-slate-500 text-xs">{sub.email}</p>
                              </div>
                            </div>
                            <span className="text-cyan-400 font-mono font-bold">{sub.tip_count}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-center py-16">No identified submitters yet</p>
                    )}
                  </div>
                </div>

                {/* Weekly Volume */}
                <div className="card-tactical p-5" data-testid="weekly-volume-chart">
                  <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Weekly Trend (12 Weeks)</h3>
                  {tipAnalytics.weekly_volume.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={tipAnalytics.weekly_volume}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                        <XAxis dataKey="week" tick={{ fill: '#64748b', fontSize: 10 }} />
                        <YAxis tick={{ fill: '#64748b', fontSize: 11 }} allowDecimals={false} />
                        <RechartsTooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#e2e8f0' }} />
                        <Bar dataKey="count" name="Tips" fill="#22d3ee" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-slate-500 text-center py-12">No weekly data available</p>
                  )}
                </div>
              </>
            ) : (
              <p className="text-slate-500 text-center py-20">No analytics data available</p>
            )}
          </div>
        )}


        {/* User Profile Modal */}
        <AnimatePresence>
          {selectedUserProfile && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedUserProfile(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-slate-900 border border-slate-700 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-slate-800 flex justify-between items-start">
                  <div className="flex items-center gap-4">
                    {selectedUserProfile.picture ? (
                      <img src={selectedUserProfile.picture} alt="" className="w-16 h-16 rounded-full object-cover" />
                    ) : (
                      <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center">
                        <User className="w-8 h-8 text-cyan-400" />
                      </div>
                    )}
                    <div>
                      <h2 className="text-xl font-bold text-white">{selectedUserProfile.name || 'Unknown'}</h2>
                      <p className="text-slate-400">{selectedUserProfile.email}</p>
                      <p className="text-slate-500 text-sm">ID: {selectedUserProfile.user_id}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedUserProfile(null)}
                    className="text-slate-400 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="p-6">
                  {/* User Stats */}
                  <h3 className="text-lg font-semibold text-white mb-4">Activity Stats</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-slate-800/50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-cyan-400">{selectedUserProfile.stats?.tips_submitted || 0}</p>
                      <p className="text-slate-400 text-sm">Tips Submitted</p>
                    </div>
                    <div className="bg-slate-800/50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-400">{selectedUserProfile.stats?.verified_tips || 0}</p>
                      <p className="text-slate-400 text-sm">Verified Tips</p>
                    </div>
                    <div className="bg-slate-800/50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-amber-400">{selectedUserProfile.stats?.tips_led_to_capture || 0}</p>
                      <p className="text-slate-400 text-sm">Led to Capture</p>
                    </div>
                    <div className="bg-slate-800/50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-400">{selectedUserProfile.stats?.case_files_tracking || 0}</p>
                      <p className="text-slate-400 text-sm">Tracking Cases</p>
                    </div>
                    <div className="bg-slate-800/50 p-4 rounded-lg text-center">
                      <p className="text-2xl font-bold text-emerald-400">${(selectedUserProfile.stats?.total_bounty_contributed || 0).toLocaleString()}</p>
                      <p className="text-slate-400 text-sm">Bounty Contributed</p>
                    </div>
                  </div>

                  {/* Account Info */}
                  <h3 className="text-lg font-semibold text-white mb-4">Account Information</h3>
                  <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Joined</span>
                      <span className="text-white">{selectedUserProfile.created_at ? new Date(selectedUserProfile.created_at).toLocaleDateString() : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Region</span>
                      <span className="text-white">{selectedUserProfile.region || 'Not set'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Online Status</span>
                      <span className={selectedUserProfile.is_online ? 'text-green-400' : 'text-slate-500'}>
                        {selectedUserProfile.is_online ? 'Online' : 'Offline'}
                      </span>
                    </div>
                    {selectedUserProfile.last_seen && (
                      <div className="flex justify-between">
                        <span className="text-slate-400">Last Seen</span>
                        <span className="text-white">{new Date(selectedUserProfile.last_seen).toLocaleString()}</span>
                      </div>
                    )}
                    {selectedUserProfile.referral_code && (
                      <div className="flex justify-between">
                        <span className="text-slate-400">Referral Code</span>
                        <span className="text-cyan-400 font-mono">{selectedUserProfile.referral_code}</span>
                      </div>
                    )}
                  </div>

                  {/* Admin Info (if available) */}
                  {selectedUserProfile.admin_info && (
                    <>
                      <h3 className="text-lg font-semibold text-white mb-4 mt-6">Recent Login History</h3>
                      <div className="bg-slate-800/50 rounded-lg p-4">
                        {selectedUserProfile.admin_info.recent_logins?.length > 0 ? (
                          <div className="space-y-2">
                            {selectedUserProfile.admin_info.recent_logins.map((login, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span className="text-slate-400">{new Date(login.timestamp).toLocaleString()}</span>
                                <span className="text-slate-500">{login.ip_address}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-500 text-sm">No recent login history available</p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loading Profile Overlay */}
        {loadingProfile && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
            <div className="bg-slate-800 p-6 rounded-lg flex items-center gap-3">
              <RefreshCw className="w-5 h-5 text-cyan-400 animate-spin" />
              <span className="text-white">Loading profile...</span>
            </div>
          </div>
        )}

        {/* Message User Modal */}
        <AnimatePresence>
          {showMessageModal && messageTarget && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowMessageModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-slate-900 border border-slate-700 rounded-lg max-w-lg w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-slate-800 flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold text-white">Send Message</h2>
                    <p className="text-slate-400 text-sm">To: {messageTarget.name || messageTarget.email}</p>
                  </div>
                  <button
                    onClick={() => setShowMessageModal(false)}
                    className="text-slate-400 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Subject</label>
                    <input
                      type="text"
                      value={messageForm.subject}
                      onChange={(e) => setMessageForm({ ...messageForm, subject: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:border-cyan-500 focus:outline-none"
                      placeholder="Welcome to Wysspr!"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Message</label>
                    <textarea
                      value={messageForm.message}
                      onChange={(e) => setMessageForm({ ...messageForm, message: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:border-cyan-500 focus:outline-none min-h-[150px]"
                      placeholder="Write your message here..."
                    />
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="sendEmail"
                      checked={messageForm.sendEmail}
                      onChange={(e) => setMessageForm({ ...messageForm, sendEmail: e.target.checked })}
                      className="w-4 h-4 text-cyan-500 bg-slate-800 border-slate-700 rounded focus:ring-cyan-500"
                    />
                    <label htmlFor="sendEmail" className="text-slate-400 text-sm">
                      Also send via email
                    </label>
                  </div>
                </div>

                <div className="p-6 border-t border-slate-800 flex justify-end gap-3">
                  <button
                    onClick={() => setShowMessageModal(false)}
                    className="px-4 py-2 text-slate-400 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={sendMessageToUser}
                    disabled={sendingMessage || !messageForm.subject || !messageForm.message}
                    className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-50 flex items-center gap-2"
                  >
                    {sendingMessage ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Message
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Edit Community Post Modal */}
        <AnimatePresence>
          {showEditPostModal && editingPost && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowEditPostModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-slate-900 border border-slate-700 rounded-lg max-w-lg w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6 border-b border-slate-800 flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold text-white">Edit Community Post</h2>
                    <p className="text-slate-400 text-sm">Post ID: {editingPost.post_id}</p>
                  </div>
                  <button
                    onClick={() => setShowEditPostModal(false)}
                    className="text-slate-400 hover:text-white"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Title</label>
                    <input
                      type="text"
                      value={editPostForm.title}
                      onChange={(e) => setEditPostForm({ ...editPostForm, title: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:border-cyan-500 focus:outline-none"
                      placeholder="Post title"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Content</label>
                    <textarea
                      value={editPostForm.content}
                      onChange={(e) => setEditPostForm({ ...editPostForm, content: e.target.value })}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:border-cyan-500 focus:outline-none min-h-[150px]"
                      placeholder="Post content..."
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Category</label>
                      <select
                        value={editPostForm.category}
                        onChange={(e) => setEditPostForm({ ...editPostForm, category: e.target.value })}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:border-cyan-500 focus:outline-none"
                      >
                        <option value="General">General</option>
                        <option value="Sighting">Sighting</option>
                        <option value="Discussion">Discussion</option>
                        <option value="Question">Question</option>
                        <option value="Alert">Alert</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">Type</label>
                      <select
                        value={editPostForm.type}
                        onChange={(e) => setEditPostForm({ ...editPostForm, type: e.target.value })}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:border-cyan-500 focus:outline-none"
                      >
                        <option value="DISCUSSION">Discussion</option>
                        <option value="SIGHTING">Sighting</option>
                        <option value="ALERT">Alert</option>
                        <option value="QUESTION">Question</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="p-6 border-t border-slate-800 flex justify-end gap-3">
                  <button
                    onClick={() => setShowEditPostModal(false)}
                    className="px-4 py-2 text-slate-400 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdatePost}
                    disabled={actionLoading === editingPost.post_id || !editPostForm.title || !editPostForm.content}
                    className="px-6 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 disabled:opacity-50 flex items-center gap-2"
                  >
                    {actionLoading === editingPost.post_id ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// Privacy Policy Page
const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen pt-20 px-4 pb-12" data-testid="privacy-policy-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <p className="text-cyan-400 font-mono text-sm tracking-[0.2em] mb-2">LEGAL</p>
          <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tight uppercase text-white">
            Privacy Policy
          </h1>
          <p className="text-slate-400 mt-2">Last Updated: December 28, 2025</p>
        </div>

        <div className="card-tactical p-8 space-y-8 text-slate-300">
          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">1. Introduction</h2>
            <p className="mb-4">
              Wysspr Intelligence ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you use our fugitive tracking 
              platform and services.
            </p>
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-sm">
              <p className="text-red-400 font-semibold mb-2">Important Notice</p>
              <p className="text-sm">
                Wysspr is an independent platform and is NOT affiliated with, endorsed by, or connected to the 
                Federal Bureau of Investigation (FBI) or any government agency. We utilize publicly available data 
                from the FBI's Most Wanted API for informational purposes only.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">2. Information We Collect</h2>
            
            <h3 className="text-lg font-semibold text-cyan-400 mb-2">2.1 Account Information</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Name and email address when you create an account</li>
              <li>Authentication credentials (securely hashed passwords)</li>
              <li>Profile information from Google if you use social login</li>
            </ul>

            <h3 className="text-lg font-semibold text-cyan-400 mb-2">2.2 Tip Submissions</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Information you provide when submitting tips about fugitives</li>
              <li>Location data if you choose to include it</li>
              <li>Images or evidence you upload</li>
              <li>We offer anonymous submission options - if selected, no identifying information is stored with your tip</li>
            </ul>

            <h3 className="text-lg font-semibold text-cyan-400 mb-2">2.3 Usage Data</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Fugitives you track or add to case files</li>
              <li>Bounty pool contributions</li>
              <li>AI chat interactions</li>
              <li>Device and browser information</li>
              <li>IP addresses and general location data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide and maintain our services</li>
              <li>To process and analyze tip submissions using AI technology</li>
              <li>To send notifications about fugitives you're tracking</li>
              <li>To communicate updates about your submitted tips</li>
              <li>To improve our platform through usage analytics</li>
              <li>To detect and prevent fraudulent or malicious activity</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">4. Information Sharing</h2>
            <p className="mb-4">We may share your information in the following circumstances:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-white">Law Enforcement:</strong> We may share tip information with appropriate law enforcement agencies, including the FBI, when tips are deemed credible and actionable</li>
              <li><strong className="text-white">Legal Requirements:</strong> When required by law, subpoena, or court order</li>
              <li><strong className="text-white">Safety:</strong> When necessary to protect the safety of individuals or the public</li>
              <li><strong className="text-white">Service Providers:</strong> With trusted third-party service providers who assist in operating our platform</li>
            </ul>
            <p className="mt-4 text-amber-400">
              We do NOT sell your personal information to third parties for marketing purposes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">5. Data Security</h2>
            <p className="mb-4">
              We implement industry-standard security measures to protect your information:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Encryption of data in transit (HTTPS/TLS)</li>
              <li>Secure password hashing using bcrypt</li>
              <li>JWT-based authentication with expiring tokens</li>
              <li>Regular security audits and updates</li>
            </ul>
            <p className="mt-4 text-slate-400">
              However, no method of transmission over the Internet is 100% secure. While we strive to protect 
              your information, we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">6. Your Rights</h2>
            <p className="mb-4">You have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your account and associated data</li>
              <li>Opt out of non-essential communications</li>
              <li>Submit tips anonymously without creating an account</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, contact us at{' '}
              <a href="mailto:privacy@wysspr.com" className="text-cyan-400 hover:underline">privacy@wysspr.com</a>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">7. Data Retention</h2>
            <p>
              We retain your personal information for as long as your account is active or as needed to provide 
              services. Tip information may be retained indefinitely for law enforcement purposes. You may request 
              deletion of your account data, though certain information may be retained as required by law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">8. Third-Party Services</h2>
            <p className="mb-4">Our platform may integrate with third-party services:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-white">FBI Most Wanted API:</strong> Public data source for fugitive information</li>
              <li><strong className="text-white">Google Authentication:</strong> For social login functionality</li>
              <li><strong className="text-white">AI Services:</strong> For tip analysis and chat features</li>
            </ul>
            <p className="mt-4">
              These services have their own privacy policies, and we encourage you to review them.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">9. Children's Privacy</h2>
            <p>
              Wysspr is not intended for users under 18 years of age. We do not knowingly collect personal 
              information from children. If you believe a child has provided us with personal information, 
              please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy periodically. We will notify you of significant changes by 
              posting the new policy on this page and updating the "Last Updated" date. Continued use of 
              our services after changes constitutes acceptance of the revised policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">11. Contact Us</h2>
            <p className="mb-4">For questions about this Privacy Policy or our data practices:</p>
            <div className="p-4 bg-slate-800 rounded-sm">
              <p>Wysspr Intelligence</p>
              <p>Email: <a href="mailto:privacy@wysspr.com" className="text-cyan-400 hover:underline">privacy@wysspr.com</a></p>
              <p>General Inquiries: <a href="mailto:support@wysspr.com" className="text-cyan-400 hover:underline">support@wysspr.com</a></p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

// Terms of Service Page
const TermsOfService = () => {
  return (
    <div className="min-h-screen pt-20 px-4 pb-12" data-testid="terms-of-service-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <p className="text-cyan-400 font-mono text-sm tracking-[0.2em] mb-2">LEGAL</p>
          <h1 className="font-heading font-bold text-3xl md:text-4xl tracking-tight uppercase text-white">
            Terms of Service
          </h1>
          <p className="text-slate-400 mt-2">Last Updated: December 28, 2025</p>
        </div>

        <div className="card-tactical p-8 space-y-8 text-slate-300">
          {/* Critical Notice */}
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-sm">
            <p className="text-red-400 font-semibold mb-2">⚠️ Critical Notice</p>
            <p className="text-sm mb-2">
              <strong>Wysspr is NOT affiliated with the FBI or any government agency.</strong> This is an independent 
              platform that uses publicly available data. For urgent sightings or tips about fugitives, always 
              contact the FBI directly:
            </p>
            <ul className="text-sm space-y-1 text-cyan-400">
              <li>📞 1-800-CALL-FBI (1-800-225-5324)</li>
              <li>🌐 tips.fbi.gov</li>
            </ul>
          </div>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">1. Acceptance of Terms</h2>
            <p className="mb-4">
              By accessing or using Wysspr ("the Platform"), you agree to be bound by these Terms of Service 
              ("Terms"). If you do not agree to these Terms, you may not use the Platform.
            </p>
            <p>
              You must be at least 18 years old to use this Platform. By using Wysspr, you represent and warrant 
              that you meet this age requirement.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">2. Description of Service</h2>
            <p className="mb-4">
              Wysspr is a community intelligence platform that:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Displays information about wanted fugitives from publicly available FBI data</li>
              <li>Allows users to submit tips and information about potential fugitive sightings</li>
              <li>Provides AI-powered analysis tools for submitted information</li>
              <li>Enables users to track fugitives and receive notifications</li>
              <li>Facilitates community participation through bounty pool contributions</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">3. User Responsibilities</h2>
            
            <h3 className="text-lg font-semibold text-cyan-400 mb-2">3.1 Accurate Information</h3>
            <p className="mb-4">
              You agree to provide accurate, truthful information when submitting tips. Submitting false, 
              misleading, or fabricated information may result in legal consequences and permanent ban from the Platform.
            </p>

            <h3 className="text-lg font-semibold text-cyan-400 mb-2">3.2 Safety First</h3>
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-sm mb-4">
              <p className="text-amber-400 font-semibold mb-2">⚠️ Important Safety Warning</p>
              <p className="text-sm">
                <strong>NEVER attempt to apprehend, approach, or confront any fugitive.</strong> These individuals 
                are considered dangerous. Your role is strictly to report information. Leave all apprehension to 
                trained law enforcement professionals.
              </p>
            </div>

            <h3 className="text-lg font-semibold text-cyan-400 mb-2">3.3 Prohibited Activities</h3>
            <p className="mb-2">You agree NOT to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Submit false or fraudulent tips</li>
              <li>Attempt to personally locate, track, or confront fugitives</li>
              <li>Harass, threaten, or intimidate other users or individuals</li>
              <li>Use the Platform for vigilante activities</li>
              <li>Impersonate law enforcement or government officials</li>
              <li>Attempt to hack, disrupt, or compromise the Platform</li>
              <li>Use automated systems to scrape or collect data</li>
              <li>Share your account credentials with others</li>
              <li>Use the Platform for any illegal purpose</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">4. Tip Submissions</h2>
            
            <h3 className="text-lg font-semibold text-cyan-400 mb-2">4.1 Processing of Tips</h3>
            <p className="mb-4">
              Tips submitted through Wysspr are processed using AI analysis and may be reviewed by our team. 
              Credible tips may be forwarded to appropriate law enforcement agencies, including the FBI.
            </p>

            <h3 className="text-lg font-semibold text-cyan-400 mb-2">4.2 Anonymous Tips</h3>
            <p className="mb-4">
              You may submit tips anonymously. Anonymous tips will not be linked to your account, and we will 
              not store identifying information with the tip.
            </p>

            <h3 className="text-lg font-semibold text-cyan-400 mb-2">4.3 No Guarantee of Action</h3>
            <p>
              Submission of a tip does not guarantee any action will be taken. We and law enforcement agencies 
              retain full discretion over how tips are processed and investigated.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">5. Rewards and Bounty Pool</h2>
            
            <h3 className="text-lg font-semibold text-cyan-400 mb-2">5.1 Official FBI Rewards</h3>
            <p className="mb-4">
              Reward amounts displayed on the Platform are sourced from the FBI and are subject to FBI terms 
              and conditions. Wysspr does not distribute official FBI rewards. To be eligible for official 
              rewards, tips must be submitted directly to the FBI at 1-800-CALL-FBI or tips.fbi.gov.
            </p>

            <h3 className="text-lg font-semibold text-cyan-400 mb-2">5.2 Community Bounty Pool</h3>
            <p className="mb-4">
              The community bounty pool feature allows users to contribute funds toward rewards. These 
              contributions are separate from official FBI rewards. Distribution of community bounty funds 
              is at the sole discretion of Wysspr and subject to verification of tip credibility and contribution 
              to capture.
            </p>

            <h3 className="text-lg font-semibold text-cyan-400 mb-2">5.3 No Guarantee of Payment</h3>
            <p>
              Wysspr makes no guarantee of reward payment for any tip submitted through our Platform. Reward 
              eligibility is determined by the FBI and relevant law enforcement agencies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">6. Intellectual Property</h2>
            <p className="mb-4">
              The Platform, including its design, features, and content (excluding FBI public data), is owned 
              by Wysspr Intelligence. You may not copy, modify, distribute, or create derivative works without 
              our written permission.
            </p>
            <p>
              FBI Most Wanted data is public information provided by the U.S. government and is not owned by Wysspr.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">7. Disclaimers</h2>
            
            <h3 className="text-lg font-semibold text-cyan-400 mb-2">7.1 No Government Affiliation</h3>
            <p className="mb-4">
              Wysspr is an independent, privately-operated platform. We are NOT affiliated with, endorsed by, 
              or connected to the FBI, Department of Justice, or any government agency.
            </p>

            <h3 className="text-lg font-semibold text-cyan-400 mb-2">7.2 Information Accuracy</h3>
            <p className="mb-4">
              While we strive to display accurate information from the FBI API, we cannot guarantee the accuracy, 
              completeness, or timeliness of all information. Fugitive statuses may change without immediate 
              reflection on our Platform.
            </p>

            <h3 className="text-lg font-semibold text-cyan-400 mb-2">7.3 No Professional Advice</h3>
            <p className="mb-4">
              Information on this Platform is for general informational purposes only and does not constitute 
              legal, law enforcement, or professional advice.
            </p>

            <h3 className="text-lg font-semibold text-cyan-400 mb-2">7.4 AI Limitations</h3>
            <p>
              AI-powered features are provided as tools to assist users and may not always be accurate. AI 
              analysis should not be relied upon as definitive truth.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">8. Limitation of Liability</h2>
            <p className="mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WYSSPR SHALL NOT BE LIABLE FOR:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Any direct, indirect, incidental, or consequential damages arising from your use of the Platform</li>
              <li>Personal injury or property damage resulting from your actions based on Platform information</li>
              <li>Loss of rewards or bounty payments</li>
              <li>Actions taken by law enforcement based on your tips</li>
              <li>Accuracy of fugitive information or status</li>
              <li>Actions of other users or third parties</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">9. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless Wysspr, its officers, directors, employees, and agents 
              from any claims, damages, losses, or expenses arising from your use of the Platform, violation 
              of these Terms, or infringement of any rights of another party.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">10. Account Termination</h2>
            <p className="mb-4">
              We reserve the right to suspend or terminate your account at any time for:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Violation of these Terms</li>
              <li>Submission of false or malicious information</li>
              <li>Conduct that endangers users or the public</li>
              <li>Any reason at our sole discretion</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">11. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the United States. 
              Any disputes arising from these Terms shall be resolved in the appropriate federal or state courts.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">12. Changes to Terms</h2>
            <p>
              We may modify these Terms at any time. Continued use of the Platform after changes constitutes 
              acceptance of the revised Terms. We will notify users of significant changes via email or 
              Platform notification.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-bold text-white mb-4">13. Contact</h2>
            <p className="mb-4">For questions about these Terms:</p>
            <div className="p-4 bg-slate-800 rounded-sm">
              <p>Wysspr Intelligence</p>
              <p>Email: <a href="mailto:legal@wysspr.com" className="text-cyan-400 hover:underline">legal@wysspr.com</a></p>
              <p>General Inquiries: <a href="mailto:support@wysspr.com" className="text-cyan-400 hover:underline">support@wysspr.com</a></p>
            </div>
          </section>

          <section className="pt-4 border-t border-slate-700">
            <p className="text-slate-500 text-sm">
              By using Wysspr, you acknowledge that you have read, understood, and agree to be bound by these 
              Terms of Service and our Privacy Policy.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <>
      <Navbar TutorialModal={TutorialModal} ReferralInviteModal={ReferralInviteModal} />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/search" element={<AdvancedSearchPage />} />
        <Route path="/map" element={<FugitiveMap />} />
        <Route path="/fugitive/:id" element={<FugitiveDetail />} />
        <Route path="/tips" element={<TipSubmission />} />
        <Route path="/bounty" element={<BountyPoolPage />} />
        <Route path="/bounty/success" element={<BountyPoolPage />} />
        <Route path="/rewards" element={<RewardsPage />} />
        <Route path="/chat" element={<AIChat />} />
        <Route path="/community" element={<CommunityPage />} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/case-files" element={<ProtectedRoute><CaseFilesPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        <Route path="/superadmin" element={<ProtectedRoute><SuperAdminDashboard /></ProtectedRoute>} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/google/callback" element={<GoogleCallback />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/state-fugitives" element={<StateFugitivesPage />} />
        <Route path="/missing-persons" element={<MissingPersonsPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        
        {/* Agency Portal Routes */}
        <Route path="/agency/login" element={<AgencyLoginPage />} />
        <Route path="/agency/dashboard" element={<AgencyDashboard />} />
        <Route path="/agency/tips" element={<AgencyTipsPage />} />
        <Route path="/agency/analytics" element={<AgencyAnalyticsPage />} />
        <Route path="/agency/ai" element={<AgencyAICommandCenter />} />
        <Route path="/agency/cases" element={<AgencyDashboard />} />
        <Route path="/agency/heatmap" element={<AgencyAnalyticsPage />} />
        <Route path="/agency/network" element={<AgencyAnalyticsPage />} />
        <Route path="/agency/profile" element={<AgencyProfilePage />} />
        <Route path="/agency/fugitives" element={<AgencyFugitivesPage />} />
        <Route path="/agency" element={<Navigate to="/agency/login" replace />} />
        
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <GoogleReCaptchaProvider
        reCaptchaKey={RECAPTCHA_SITE_KEY}
        scriptProps={{
          async: true,
          defer: true,
          appendTo: "head",
        }}
      >
        <BrowserRouter>
          <AuthContext>
            <AgencyAuthProvider>
              <AppRouter />
            <Toaster 
              position="top-right"
              toastOptions={{
                style: {
                  background: '#0f172a',
                  border: '1px solid #1e293b',
                  color: '#f8fafc',
                },
              }}
            />
            </AgencyAuthProvider>
          </AuthContext>
        </BrowserRouter>
      </GoogleReCaptchaProvider>
    </div>
  );
}

export default App;
